import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation.js';
import { requestJson } from '../utils/authClient.js';
import { AdminDialog, AdminSection, fill, writeAdminRoute } from '../components/AdminNav.jsx';
import { FactGrid, formatBerlin } from './VeridionShared.jsx';

// Data Lake & Training — the autopilot-first operator surface (IDD D-HP-ADMIN-063,
// D-SAAS-102, D-SAAS-105, D-SAAS-106, D-SAAS-107): one unmistakable autopilot toggle
// with its phase and its honest blocked reason, owner-edited thresholds with measured
// progress, the raw-data inbox, the owner-defined folder tree, the knowledge-graph
// summary, and the training/benchmark/HuggingFace release trail. Every number is
// measured or labeled a configured default; engine internals (ring-buffer zones,
// shard tables, transfer ledger) live in the engine documentation, not here. Each
// new endpoint is feature-detected: a 404 renders an honest "not rolled out yet"
// sentence instead of invented data, so the page works against the old backend too.
// The readiness-to-train pipeline (D-HP-ADMIN-067) leads the surface: the engine's
// readiness endpoint answers "how far from the first training run?" in measured,
// plain bilingual sentences, and the training gates render from the same stages.
const API = '/api/admin/datalake';
const CORE_ARCHITECTURE = '/api/admin/veridion-core/quantum-architecture';
// The three graph-stage loops the engine reports (D-SAAS-155 §6), in the order the panel lists them.
const GRAPH_LOOPS = ['grag-embed', 'grag-extract', 'grag-link-stage'];

// The training-strand selector (D-SAAS-125 §2, D-HP-ADMIN-072): which strand the autopilot trains.
// The selection lives in the core (default: the document strand); the unselected strand keeps
// preparing data but never reaches a training start. Feature-detected: an older backend without
// strands yields the honest localized note, never an invented control.
const TrainingStrandSelector = ({ d, isDe }) => {
  const [payload, setPayload] = useState(null);
  const [state, setState] = useState('loading'); // loading | ok | unavailable
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const load = useCallback(async () => {
    try {
      const data = await requestJson(CORE_ARCHITECTURE);
      if (data && data.strands && typeof data.strands === 'object') {
        setPayload(data);
        setState('ok');
      } else {
        setState('unavailable');
      }
    } catch (error) {
      setState('unavailable');
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (state === 'loading') return null;
  if (state === 'unavailable') {
    return (
      <AdminSection id="datalake-training-strand" title={d.strandTitle} subtitle={d.strandSubtitle}>
        <p className="text-sm text-text-secondary">{d.strandUnavailable}</p>
      </AdminSection>
    );
  }
  const strands = payload.strands;
  const selected = payload.training_strand;
  const choose = async (name) => {
    if (name === selected || busy) return;
    setBusy(true);
    setNotice('');
    try {
      await requestJson(`${CORE_ARCHITECTURE}/training-strand`, {
        method: 'POST', body: JSON.stringify({ strand: name }),
      });
      setNotice(d.strandSwitched);
      await load();
    } catch (error) {
      setNotice(d.strandSwitchFailed);
    }
    setBusy(false);
  };
  return (
    <AdminSection id="datalake-training-strand" title={d.strandTitle} subtitle={d.strandSubtitle}
      state={strands[selected]?.current?.codename || selected} tone="ok">
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {Object.entries(strands).map(([name, block]) => (
            <button key={name} type="button" disabled={busy}
              className={name === selected ? 'homepage-button-primary' : 'homepage-button-secondary'}
              onClick={() => choose(name)}>
              {block?.current?.codename || block?.strand_title_en || name}
              {name === selected ? ` — ${d.strandSelectedBadge}` : ''}
            </button>
          ))}
        </div>
        <p className="text-sm text-text-secondary">
          {fill(d.strandExplain, {
            selected: strands[selected]?.current?.codename || selected,
            other: Object.entries(strands).filter(([name]) => name !== selected)
              .map(([, block]) => block?.current?.codename).filter(Boolean).join(', ') || '—',
          })}
        </p>
        {notice ? <p className="text-sm">{notice}</p> : null}
      </div>
    </AdminSection>
  );
};

const SUB_PAGES = ['autopilot', 'storage', 'acquisition', 'data', 'training'];
const VALID_SUB_PAGE_IDS = new Set(SUB_PAGES);
const CAPABILITIES = ['tool_calling', 'deep_reasoning', 'software_engineering', 'office_finance', 'multimodal_any2any'];
// The readiness stages that gate the training start (the ALL-GREEN gates of
// D-SAAS-106): the training section renders exactly these in plain language
// (D-HP-ADMIN-067); the ids survive only in tooltips. The kernel report and the start hold gate the start as well,
// so the list names every point the header counts.
const TRAINING_GATE_STAGE_IDS = new Set(['data_plan', 'tokenizer', 'sealed_tdv', 'compat_all_green', 'trainer_image',
  'vram_budget', 'ring_buffer_work', 'kernel_report', 'start_hold']);
const ACTIVE_RUN_STATES = new Set(['starting', 'running', 'interrupted', 'restoring']);
// Sizes on this page are decimal (D-SAAS-302): 1 GB = 10⁹ bytes, 1 MB = 10⁶, 1 kB = 10³.
const GB = 1e9;
const MAX_UPLOAD_BYTES = 512 * 1024 * 1024;
const EVENTS_PAGE = 25;

// The one measured per-folder count the tree view shows; invisible plumbing on the
// catalog, never a SQL console (D-HP-ADMIN-063). Fails silently: the tree stays
// honest without it.
// Shards per top folder plus the landed raw assets: raw/ holds the downloads, which are files, not shards, so its
// shard count stays empty and its bytes are measured.
const TREE_STATS_SQL = 'SELECT top_folder, sum(shards) AS shards, sum(bytes) AS bytes FROM ('
  + "SELECT split_part(path, '/', 1) AS top_folder, count(*) AS shards, COALESCE(sum(size_bytes), 0) AS bytes "
  + 'FROM shards WHERE path IS NOT NULL GROUP BY 1 UNION ALL '
  + "SELECT split_part(cold_path, '/', 1) AS top_folder, NULL AS shards, COALESCE(sum(size_bytes), 0) AS bytes "
  + 'FROM raw_assets WHERE cold_path IS NOT NULL GROUP BY 1) GROUP BY 1 ORDER BY 1';

const formatInteger = (value) => (value === null || value === undefined || value === '' ? '-' : Number(value).toLocaleString());
// Seconds of a measured round with one decimal; '-' when the engine reports none.
const formatSeconds = (value) => (value === null || value === undefined || value === '' || Number.isNaN(Number(value))
  ? '-' : Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 }));
// Relative time in the administrator's language from the browser's own locale data (never a
// hard-coded word): negative seconds lie in the past. Empty when the browser cannot format it,
// so the caller falls back to the absolute Berlin time.
const formatRelative = (deltaSeconds, lang) => {
  if (deltaSeconds === null || deltaSeconds === undefined || Number.isNaN(Number(deltaSeconds))) return '';
  if (typeof Intl === 'undefined' || typeof Intl.RelativeTimeFormat !== 'function') return '';
  const seconds = Math.round(Number(deltaSeconds));
  const [unit, size] = [['day', 86400], ['hour', 3600], ['minute', 60]].find(([, s]) => Math.abs(seconds) >= s) || ['second', 1];
  try {
    return new Intl.RelativeTimeFormat(lang || 'en', { numeric: 'always', style: 'short' }).format(Math.trunc(seconds / size), unit);
  } catch {
    return '';
  }
};
const formatAgo = (value, lang) => {
  if (value === null || value === undefined || value === '') return '';
  let text = String(value).trim();
  // A naive timestamp from the engine is UTC, as the shared Berlin formatter assumes too.
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(text) && !/(Z|[+-]\d{2}:?\d{2})$/i.test(text)) text = `${text.replace(' ', 'T')}Z`;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? '' : formatRelative((date.getTime() - Date.now()) / 1000, lang);
};
const formatBytes = (bytes) => {
  const value = Number(bytes || 0);
  if (value >= GB) return `${(value / GB).toFixed(2)} GB`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(1)} MB`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(1)} kB`;
  return `${value} B`;
};
const formatLoss = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(4) : '-';
};
// A refusal from the engine carries bilingual sentences next to the code (D-SAAS-100).
const errorText = (error, isDe) => {
  const detail = error?.payload?.detail || {};
  const sentence = isDe ? detail.message : (detail.message_en || detail.message);
  return sentence || error?.code || error?.message || '-';
};
const pick = (payload, field, isDe) => (isDe ? payload?.[field] : (payload?.[`${field}_en`] || payload?.[field]));
// Bilingual {de,en} objects (autopilot blocked_detail).
const pickBilingual = (value, isDe) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return isDe ? (value.de || value.en || '') : (value.en || value.de || '');
};

// --- tiny markdown renderer for the stored benchmark reports --------------------------
// Headings, bullet lists, tables, paragraphs, **bold** and `code`; React elements only,
// no HTML injection.
const inlineMd = (text) => String(text || '').split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, index) => {
  if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={index}>{part.slice(2, -2)}</strong>;
  if (/^`[^`]+`$/.test(part)) return <code key={index} className="font-mono text-xs">{part.slice(1, -1)}</code>;
  return part;
});
const renderMarkdown = (text) => {
  const lines = String(text || '').split(/\r?\n/);
  const blocks = [];
  let paragraph = [];
  let list = null;
  let table = null;
  const flushParagraph = () => {
    if (paragraph.length) blocks.push(<p key={`p${blocks.length}`} className="text-sm leading-relaxed">{inlineMd(paragraph.join(' '))}</p>);
    paragraph = [];
  };
  const flushList = () => {
    if (list && list.length) {
      blocks.push(<ul key={`l${blocks.length}`} className="list-disc text-sm space-y-1" style={{ paddingLeft: '1.25rem' }}>
        {list.map((item, index) => <li key={index}>{inlineMd(item)}</li>)}
      </ul>);
    }
    list = null;
  };
  const flushTable = () => {
    if (table && table.length) {
      const [head, ...body] = table;
      blocks.push(
        <div key={`t${blocks.length}`} className="admin-table-shell"><div className="admin-table-scroll">
          <table className="admin-table">
            <thead><tr>{head.map((cell, index) => <th key={index}>{inlineMd(cell)}</th>)}</tr></thead>
            <tbody>
              {body.map((row, rowIndex) => (
                <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex} className="text-xs">{inlineMd(cell)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div></div>,
      );
    }
    table = null;
  };
  lines.forEach((rawLine) => {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();
    const heading = /^(#{1,4})\s+(.*)$/.exec(trimmed);
    if (heading) {
      flushParagraph(); flushList(); flushTable();
      const level = heading[1].length;
      const className = level <= 2 ? 'font-black text-base' : 'font-bold text-sm';
      blocks.push(<div key={`h${blocks.length}`} className={className}>{inlineMd(heading[2])}</div>);
      return;
    }
    if (/^[-*]\s+/.test(trimmed)) {
      flushParagraph(); flushTable();
      list = list || [];
      list.push(trimmed.replace(/^[-*]\s+/, ''));
      return;
    }
    if (/^\|.*\|$/.test(trimmed)) {
      flushParagraph(); flushList();
      if (/^\|[\s:|-]+\|$/.test(trimmed)) return; // separator row
      table = table || [];
      table.push(trimmed.slice(1, -1).split('|').map((cell) => cell.trim()));
      return;
    }
    if (!trimmed) {
      flushParagraph(); flushList(); flushTable();
      return;
    }
    flushList(); flushTable();
    paragraph.push(trimmed);
  });
  flushParagraph(); flushList(); flushTable();
  return <div className="space-y-2">{blocks}</div>;
};

// State word of an acquisition source (D-SAAS-157): the engine's "done" only says
// the source's listing is exhausted; whether the byte target was reached is its
// boolean `target_reached`. Without that flag the raw state stays, so an older
// engine never shows a wrong word.
const acquisitionStateLabel = (source, d) => {
  if (source.state !== 'done') return source.state;
  if (source.target_reached === true) return d.acqTargetReached || source.state;
  if (source.target_reached === false) return d.acqExhausted || source.state;
  return source.state;
};

// Progress toward an integer target, rem-sized only (D-HP-ADMIN-046). A target of
// 0, null or undefined means "no minimum" (D-SAAS-157): the measured value stands
// alone with the translated suffix, without a bar and without a fill against zero.
const ProgressRow = ({ label, value, target, met, noMinimumLabel }) => {
  if (!(Number(target) > 0)) {
    return (
      <div className="space-y-1 text-xs">
        <div className="flex justify-between gap-2">
          <span>{label}</span>
          <span>
            <span className="font-mono">{formatInteger(value)}</span>
            {noMinimumLabel ? <span className="text-text-secondary"> · {noMinimumLabel}</span> : null}
          </span>
        </div>
      </div>
    );
  }
  const pct = Math.min(100, Math.round((Number(value || 0) / Number(target)) * 100));
  return (
    <div className="space-y-1 text-xs">
      <div className="flex justify-between gap-2">
        <span>{label}</span>
        <span className="font-mono">{formatInteger(value)} / {formatInteger(target)}</span>
      </div>
      <div style={{ height: '0.5rem', background: 'var(--color-border, #e5e5e5)', borderRadius: '0.125rem', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '0.5rem', background: met ? 'var(--success)' : 'var(--text-primary)' }} />
      </div>
    </div>
  );
};

const StatusPill = ({ ok, children }) => (
  <span className={`admin-status-pill ${ok ? 'is-ok' : 'is-missing'}`}>{children}</span>
);
const severityClass = (severity) => {
  const value = String(severity || '').toLowerCase();
  if (['error', 'fatal', 'critical'].includes(value)) return 'is-missing';
  if (['warning', 'warn'].includes(value)) return 'is-warning';
  return 'is-ok';
};
// Readiness stage states (D-HP-ADMIN-067): met green, active blue with a subtle
// pulse, waiting neutral, blocked red — state speaks through color and the localized
// state word, never through size (D-HP-ADMIN-066).
const stageStateClass = (state) => {
  const value = String(state || '').toLowerCase();
  if (value === 'ok') return 'is-ok';
  if (value === 'active') return 'is-active';
  if (value === 'blocked') return 'is-blocked';
  return 'is-waiting';
};

// "Path to the training start" (D-HP-ADMIN-080, D-SAAS-161): one card that answers where the autopilot stands —
// the current step in plain words, every step up to the running training with state, times and progress, and
// the blockers. It reads the autopilot door (cycle actions with times, progress inside long steps), the
// readiness door and the training checklist; an engine without the timing fields still yields the steps from
// the cycle counts, and the card says that timings are not reported yet. Remaining times are computed here from
// measured counts and labelled as estimates.
const PATH_ACTIONS = [
  ['tokenizer_release', 'tpStepTokenizerRelease'],
  ['tokenize_tdv', 'tpStepTokenizeTdv'],
  ['tokenize_corpus', 'tpStepTokenizeCorpus'],
  ['tdv_create', 'tpStepTdvCreate'],
  ['tdv_seal', 'tpStepTdvSeal'],
];
const ACTION_STATE = { done: 'done', running: 'running', pending: 'waiting', failed: 'failed', skipped: 'skipped' };
const parseTime = (value) => {
  const parsed = Date.parse(String(value || ''));
  return Number.isNaN(parsed) ? null : parsed;
};
const formatDuration = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return `${Math.max(1, Math.round(ms / 1000))} s`;
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min`;
};

// The trainer's own dashboard (D-HP-ADMIN-081, D-SAAS-162 §5): the admin replica reads the run directory the
// trainer writes (run.json, progress.jsonl, latest.json) directly, so this card works while the worker restarts.
// Loss chart: three series on one axis (same unit), validated categorical slots 1-3, line-end labels, a legend,
// a hover crosshair with tooltip and a value table (the third slot's surface contrast obliges visible labels).
const RUN_SERIES = [
  { key: 'loss', color: '#2a78d6', labelKey: 'trLossTotal' },
  { key: 'ntp', color: '#eb6834', labelKey: 'trLossNtp' },
  { key: 'mtp', color: '#1baf7a', labelKey: 'trLossMtp' },
];
const formatSpan = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const minutes = Math.round(seconds / 60);
  if (minutes < 1) return `${Math.max(1, Math.round(seconds))} s`;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ${String(minutes % 60).padStart(2, '0')} min`;
  return `${Math.floor(hours / 24)} d ${hours % 24} h`;
};
const formatNumber = (value, isDe, digits = 0) => (Number.isFinite(Number(value))
  ? Number(value).toLocaleString(isDe ? 'de-DE' : 'en-GB', { maximumFractionDigits: digits, minimumFractionDigits: digits })
  : '—');

// D-HP-ADMIN-090 §4: an event's values as a short line — words for keys, numbers formatted, at most six values, no JSON.
const readablePayload = (payload, isDe) => Object.entries(payload || {})
  .filter(([, value]) => value !== null && value !== undefined && value !== '' && typeof value !== 'object')
  .slice(0, 6)
  .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${typeof value === 'number'
    ? new Intl.NumberFormat(isDe ? 'de-DE' : 'en-GB', { maximumFractionDigits: 2 }).format(value) : String(value).slice(0, 80)}`)
  .join(' · ');

const formatCompact = (value, isDe) => (Number.isFinite(Number(value))
  ? new Intl.NumberFormat(isDe ? 'de-DE' : 'en-GB', { notation: 'compact', maximumFractionDigits: 2 }).format(Number(value))
  : '—');

// A compact line chart (D-HP-ADMIN-081): it fills the width of its column (measured with a ResizeObserver) at a fixed
// height, with a legend that names every series and its current value, axis titles, a recessive grid, 2px lines and
// a hover crosshair with a tooltip. One axis, one unit per chart.
const useElementWidth = (fallback = 600) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const measure = () => setWidth(Math.max(280, Math.floor(node.getBoundingClientRect().width)));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

const MiniLineChart = ({ d, series, lines, isDe, digits = 2, title, yTitle }) => {
  const [hover, setHover] = useState(null);
  const [boxRef, width] = useElementWidth();
  const points = (Array.isArray(series) ? series : []).filter((point) => Number.isFinite(Number(point?.step)));
  const values = points.flatMap((point) => lines.map((line) => Number(point[line.key])).filter(Number.isFinite));
  const last = points[points.length - 1] || {};
  const legend = (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
      {lines.map((line) => (
        <span key={line.key} className="inline-flex items-center gap-1">
          <span style={{ display: 'inline-block', width: 14, height: 3, borderRadius: 2, background: line.color }} />
          <span>{line.label}</span>
          {Number.isFinite(Number(last[line.key])) ? (
            <span className="font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(last[line.key], isDe, digits)}</span>
          ) : null}
        </span>
      ))}
    </div>
  );
  if (points.length < 2 || !values.length) {
    return (
      <div className="space-y-1" ref={boxRef}>
        <div className="text-xs font-semibold">{title}</div>
        {legend}
        <p className="text-xs text-text-secondary">{d.trChartEmpty}</p>
      </div>
    );
  }
  const height = 190;
  const pad = { left: 58, right: 16, top: 10, bottom: 34 };
  const steps = points.map((point) => Number(point.step));
  const xMin = Math.min(...steps);
  const xMax = Math.max(...steps);
  const low = Math.min(...values);
  const high = Math.max(...values);
  const spread = Math.max((high - low) * 0.08, Math.abs(high) * 0.01, 1e-6);
  const yMin = Math.max(0, low - spread);
  const yMax = high + spread;
  const x = (step) => pad.left + ((step - xMin) / Math.max(xMax - xMin, 1)) * (width - pad.left - pad.right);
  const y = (value) => pad.top + (1 - (value - yMin) / Math.max(yMax - yMin, 1e-9)) * (height - pad.top - pad.bottom);
  const ticks = [0, 1, 2, 3].map((index) => yMin + ((yMax - yMin) * index) / 3);
  const xTicks = [0, 1, 2, 3, 4].map((index) => Math.round(xMin + ((xMax - xMin) * index) / 4));
  const path = (key) => points.filter((point) => Number.isFinite(Number(point[key])))
    .map((point, index) => `${index ? 'L' : 'M'}${x(Number(point.step)).toFixed(1)},${y(Number(point[key])).toFixed(1)}`).join(' ');
  const onMove = (event) => {
    const box = event.currentTarget.getBoundingClientRect();
    const svgX = ((event.clientX - box.left) / box.width) * width;
    let nearest = 0;
    points.forEach((point, index) => {
      if (Math.abs(x(Number(point.step)) - svgX) < Math.abs(x(Number(points[nearest].step)) - svgX)) nearest = index;
    });
    setHover(nearest);
  };
  const active = hover !== null ? points[hover] : null;
  return (
    <div className="space-y-1" ref={boxRef} style={{ minWidth: 0 }}>
      <div className="text-xs font-semibold">{title}</div>
      {legend}
      <div style={{ position: 'relative' }}>
        <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} role="img" aria-label={title}
          onMouseMove={onMove} onMouseLeave={() => setHover(null)} style={{ display: 'block' }}>
          {ticks.map((tick) => (
            <g key={`y${tick}`}>
              <line x1={pad.left} x2={width - pad.right} y1={y(tick)} y2={y(tick)} stroke="#e2e2e6" strokeWidth="1" />
              <text x={pad.left - 6} y={y(tick) + 3} textAnchor="end" fontSize="10" fill="#6b6b70">{formatNumber(tick, isDe, digits > 0 ? 1 : 0)}</text>
            </g>
          ))}
          {xTicks.map((tick) => (
            <text key={`x${tick}`} x={x(tick)} y={height - 18} textAnchor="middle" fontSize="10" fill="#6b6b70">{formatNumber(tick, isDe)}</text>
          ))}
          <text x={pad.left + (width - pad.left - pad.right) / 2} y={height - 3} textAnchor="middle" fontSize="10" fill="#6b6b70">{d.trAxisStep}</text>
          <text x={12} y={pad.top + (height - pad.top - pad.bottom) / 2} textAnchor="middle" fontSize="10" fill="#6b6b70"
            transform={`rotate(-90 12 ${pad.top + (height - pad.top - pad.bottom) / 2})`}>{yTitle}</text>
          {lines.map((line) => (
            <path key={line.key} d={path(line.key)} fill="none" stroke={line.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          ))}
          {lines.filter((line) => Number.isFinite(Number(last[line.key]))).map((line) => (
            <circle key={`end-${line.key}`} cx={x(Number(last.step))} cy={y(Number(last[line.key]))} r="3.5" fill={line.color} stroke="#ffffff" strokeWidth="2" />
          ))}
          {active ? (
            <g>
              <line x1={x(Number(active.step))} x2={x(Number(active.step))} y1={pad.top} y2={height - pad.bottom} stroke="#6b6b70" strokeWidth="1" strokeDasharray="3 3" />
              {lines.filter((line) => Number.isFinite(Number(active[line.key]))).map((line) => (
                <circle key={`h-${line.key}`} cx={x(Number(active.step))} cy={y(Number(active[line.key]))} r="3.5" fill={line.color} stroke="#ffffff" strokeWidth="2" />
              ))}
            </g>
          ) : null}
        </svg>
        {active ? (
          <div className="homepage-muted-panel text-xs p-2" role="status"
            style={{ position: 'absolute', top: 4, left: Math.min(width - 170, Math.max(0, x(Number(active.step)) + 8)), pointerEvents: 'none', minWidth: 150 }}>
            <div><strong>{d.trStep} {formatNumber(active.step, isDe)}</strong>{active.ts ? ` · ${formatBerlin(active.ts)}` : ''}</div>
            {lines.map((line) => <div key={`t-${line.key}`}>{line.label}: {formatNumber(active[line.key], isDe, digits)}</div>)}
          </div>
        ) : null}
      </div>
    </div>
  );
};

// The curriculum timer, how long the current stage still takes: arithmetic over the measured seconds per step and
// the stage boundaries in steps; it counts down every second.
const formatCountdown = (seconds, d) => {
  const total = Math.max(0, Math.floor(seconds));
  return fill(d.trCountdown, {
    d: Math.floor(total / 86400), h: Math.floor((total % 86400) / 3600),
    m: String(Math.floor((total % 3600) / 60)).padStart(2, '0'), s: String(total % 60).padStart(2, '0'),
  });
};

const CurriculumTimer = ({ d, data, isDe, stageWord }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const sum = data.summary || {};
  const cur = sum.curriculum || {};
  const perStep = Number(data.seconds_per_step);
  const step = Number(data.step) || 0;
  const measuredAt = Date.parse(String(data.measured_at || '')) || now;
  const eta = data.eta || null;
  // A warmup-stable-decay run (D-SAAS-167) walks the bound version once per epoch: the remaining time is stated
  // per epoch, for the decay start and for the whole run, from the measured seconds per step without pauses.
  if (eta && eta.run) {
    const driftNow = (now - measuredAt) / 1000;
    const left = (block) => Math.max(0, Number(block.remaining_seconds) - driftNow);
    const clock = (secondsAhead) => new Date(now + secondsAhead * 1000).toLocaleString(isDe ? 'de-DE' : 'en-GB',
      { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const basis = eta.basis || {};
    const secondsPerStep = Number(basis.seconds_per_step) || perStep;
    return (
      <div className="homepage-muted-panel p-3 space-y-1">
        <div className="text-xs text-text-secondary">{d.trEtaTitle}</div>
        {eta.epoch ? (
          <>
            <div className="text-xs text-text-secondary">{fill(d.trEtaEpochEnds, { epoch: formatNumber(eta.epoch.epoch, isDe), epochs: formatNumber(eta.epoch.epochs, isDe) })}</div>
            <div className="text-2xl font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCountdown(left(eta.epoch), d)}</div>
            <div className="text-xs text-text-secondary">{fill(d.trEtaEpochAt, { time: clock(left(eta.epoch)), step: formatNumber(eta.epoch.ends_at_step, isDe) })}</div>
          </>
        ) : null}
        {(eta.epochs_ahead || []).map((entry) => (
          <div key={entry.epoch} className="text-xs">
            {fill(d.trEtaNextEpoch, { epoch: formatNumber(entry.epoch, isDe), step: formatNumber(entry.starts_at_step, isDe),
              time: clock(Math.max(0, (Number(entry.starts_at_step) - step) * secondsPerStep - driftNow)) })}
          </div>
        ))}
        {eta.decay && !eta.decay.started ? (
          <div className="text-xs">{fill(d.trEtaDecay, { span: formatSpan(left(eta.decay)), time: clock(left(eta.decay)), step: formatNumber(eta.decay.ends_at_step, isDe) })}</div>
        ) : null}
        <div className="text-xs font-semibold">{fill(d.trTimerRunEnds, { span: formatCountdown(left(eta.run), d), time: clock(left(eta.run)) })}</div>
        <div className="text-xs text-text-secondary">
          {fill(d.trEtaBasis, { wall: formatNumber(basis.seconds_per_step, isDe, 1), n: formatNumber(basis.window_steps, isDe),
            pauses: formatNumber(basis.pauses_excluded, isDe), compute: basis.compute_seconds ? formatNumber(basis.compute_seconds, isDe, 1) : '—' })}
        </div>
      </div>
    );
  }
  if (!Number.isFinite(perStep) || perStep <= 0 || !Number.isFinite(Number(cur.capability_starts_at_step))) {
    return <p className="text-xs text-text-secondary">{d.trTimerWaiting}</p>;
  }
  const bounds = [
    ['foundation', 0, Number(cur.capability_starts_at_step)],
    ['capability', Number(cur.capability_starts_at_step), Number(cur.anneal_starts_at_step)],
    ['anneal', Number(cur.anneal_starts_at_step), Number(cur.full_pass_steps)],
  ];
  const drift = (now - measuredAt) / 1000;
  const current = bounds.find(([, , stop]) => step < stop) || bounds[bounds.length - 1];
  const stageLeft = (current[2] - step) * perStep - drift;
  const runLeft = (Number(data.max_steps || cur.full_pass_steps) - step) * perStep - drift;
  const at = (secondsAhead) => new Date(now + secondsAhead * 1000).toLocaleString(isDe ? 'de-DE' : 'en-GB',
    { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const later = bounds.filter(([, start]) => start >= current[2]);
  return (
    <div className="homepage-muted-panel p-3 space-y-1">
      <div className="text-xs text-text-secondary">{fill(d.trTimerStageEnds, { stage: stageWord[current[0]] || current[0] })}</div>
      <div className="text-2xl font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCountdown(stageLeft, d)}</div>
      <div className="text-xs text-text-secondary">{fill(d.trTimerAt, { time: at(stageLeft) })}</div>
      {later.map(([name, start, stop]) => (
        <div key={name} className="text-xs">
          {fill(d.trTimerNext, { stage: stageWord[name] || name, span: formatSpan((stop - start) * perStep), time: at((start - step) * perStep - drift) })}
        </div>
      ))}
      <div className="text-xs font-semibold">{fill(d.trTimerRunEnds, { span: formatCountdown(runLeft, d), time: at(runLeft) })}</div>
    </div>
  );
};

// One-column facts for a half-width column: labels and values wrap instead of being squeezed (D-HP-ADMIN-081).
const FactList = ({ items }) => (
  <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(9rem, 14rem) minmax(0, 1fr)', columnGap: 12, rowGap: 6 }}>
    {items.filter((entry) => Array.isArray(entry) && entry[0]).map(([label, value]) => (
      <React.Fragment key={label}>
        <dt className="text-xs text-text-secondary" style={{ margin: 0 }}>{label}</dt>
        <dd className="text-sm" style={{ margin: 0, minWidth: 0, overflowWrap: 'anywhere' }}>{value === null || value === undefined || value === '' ? '-' : value}</dd>
      </React.Fragment>
    ))}
  </dl>
);

const KpiTile = ({ label, value, sub, sub2 = '', tone = '', bar = null }) => (
  <div className="homepage-muted-panel p-3" style={{ minWidth: 0 }}>
    <div className="text-xs text-text-secondary">{label}</div>
    <div className="text-lg font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {bar !== null ? (
      <div style={{ height: 4, background: '#e2e2e6', borderRadius: 2, margin: '4px 0' }} role="progressbar"
        aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(bar * 1000) / 10}>
        <div style={{ width: `${Math.max(0.5, Math.min(100, bar * 100))}%`, height: 4, background: '#2a78d6', borderRadius: 2 }} />
      </div>
    ) : null}
    {sub ? <div className={`text-xs ${tone === 'warn' ? 'text-warning' : 'text-text-secondary'}`}>{sub}</div> : null}
    {sub2 ? <div className="text-xs text-text-secondary">{sub2}</div> : null}
  </div>
);

// The run's progress in plain words (a bare "72/20000" says too little): the share of the planned
// steps, the steps, the time it has been training and the expected end — one source for every place that shows it.
export const runProgress = (data, isDe) => {
  if (!data || !data.run) return null;
  const step = Number(data.step) || 0;
  const max = Number(data.max_steps) || 0;
  const share = max ? step / max : null;
  const pctText = share === null ? '—' : formatNumber(share * 100, isDe, share < 0.01 ? 2 : 1);
  const startedAge = Date.parse(String(data.run.started_at || ''));
  const elapsed = Number.isNaN(startedAge) ? null : (Date.now() - startedAge) / 1000;
  const end = (data.summary || {}).end_at_estimate || null;
  return { step, max, share, pctText, stepText: formatNumber(step, isDe), maxText: formatNumber(max, isDe),
    elapsedText: elapsed !== null ? formatSpan(elapsed) : '—', endText: end ? formatBerlin(end) : '—' };
};

// The run's typed deviations in plain words, never as bare codes: every
// code the trainer records maps to what it concerns, one sentence of meaning, and whether it is planned or a limitation.
const KERNEL_NAMES = {
  attention_flex_docmask: 'Dokument-Masken-Attention (flex)', delta_scan_compiled: 'DeltaNet-Scan (kompiliert)',
  delta_chunk_compiled: 'DeltaNet-Chunk (kompiliert)', rmsnorm_triton: 'RMSNorm (Triton)',
  fused_linear_cross_entropy: 'Fused Cross-Entropy',
};
const kernelName = (id) => KERNEL_NAMES[id] || id;
export const describeDeviation = (code, d, isDe) => {
  const text = String(code || '');
  let match = text.match(/^sequence:trained_at_(\d+)_of_spec_(\d+)$/);
  if (match) return { what: d.devSeqLen, text: fill(d.devSeqLenText, { trained: formatNumber(match[1], isDe), spec: formatNumber(match[2], isDe) }), kind: 'planned' };
  match = text.match(/^objective:fim_not_applied\(rate_([\d.]+)\)$/);
  if (match) return { what: d.devFim, text: fill(d.devFimText, { rate: formatNumber(Number(match[1]) * 100, isDe) }), kind: 'limit' };
  if (text === 'objective:termination_probe_post_training_mode') return { what: d.devTermination, text: d.devTerminationText, kind: 'planned' };
  if (text === 'objective:decision_scoring_no_decision_marks_served') return { what: d.devDecision, text: d.devDecisionText, kind: 'limit' };
  match = text.match(/^kernels:(.+)_unverified$/);
  if (match) return { what: d.devKernelOff, text: fill(d.devKernelUnverifiedText, { kernel: kernelName(match[1]) }), kind: 'limit' };
  match = text.match(/^kernels:(.+)_unavailable$/);
  if (match) return { what: d.devKernelOff, text: fill(d.devKernelUnavailableText, { kernel: kernelName(match[1]) }), kind: 'limit' };
  match = text.match(/^kernels:(.+?)_fell_back\((.*)\)$/);
  if (match) return { what: d.devKernelOff, text: fill(d.devKernelFellBackText, { kernel: kernelName(match[1]), reason: match[2] }), kind: 'limit' };
  if (text === 'optimizer:adamw_bf16_instead_of_8bit') return { what: d.devOptimizer, text: d.devOptimizerText, kind: 'limit' };
  match = text.match(/^scaled_instantiation:([\d.]+)$/);
  if (match) return { what: d.devScale, text: fill(d.devScaleText, { scale: formatNumber(Number(match[1]) * 100, isDe) }), kind: 'limit' };
  if (text === 'modality_heads_untrained:no_media_tokens_in_tdv') return { what: d.devMediaHeads, text: d.devMediaHeadsText, kind: 'planned' };
  if (text === 'vision:no_image_samples_in_tdv') return { what: d.devVision, text: d.devVisionText, kind: 'planned' };
  return { what: d.devOther, text, kind: 'limit' };
};

const phaseWord = (d, phase) => ({
  warmup: d.trPhaseWarmup, stable: d.trPhaseStable, decay: d.trPhaseDecay, cosine: d.trPhaseCosine,
}[phase] || '—');

export const TrainingRunCard = ({ d, live, isDe }) => {
  const data = live?.data || null;
  if (live?.status === 'error' && !data) {
    return (
      <AdminSection id="datalake-training-run" title={d.trTitle} subtitle={d.trSubtitle} state="—" tone="error">
        <p className="text-sm">{live?.error || d.trNoRun}</p>
      </AdminSection>
    );
  }
  if (!data || !data.run) {
    return (
      <AdminSection id="datalake-training-run" title={d.trTitle} subtitle={d.trSubtitle} state="—" tone="neutral">
        <p className="text-sm">{d.trNoRun}</p>
      </AdminSection>
    );
  }
  const run = data.run || {};
  const last = data.last || {};
  const sum = data.summary || {};
  const curriculum = sum.curriculum || {};
  const stateWord = {
    training: d.trStateTraining, starting: d.trStateStarting, finished: d.trStateFinished,
    silent: fill(d.trStateSilent, { age: formatSpan(Number(data.last_age_seconds)) }),
  }[data.state] || data.state;
  const tone = data.state === 'silent' ? 'error' : (data.state === 'finished' ? 'ok' : 'warning');
  const progress = runProgress(data, isDe);
  const lossNow = Number(sum.loss_now);
  const lossHour = Number(sum.loss_hour_ago);
  const trend = Number.isFinite(lossNow) && Number.isFinite(lossHour) ? lossNow - lossHour : null;
  const stageWord = { foundation: d.trStageFoundation, capability: d.trStageCapability, anneal: d.trStageAnneal, anneal_end: d.trStageAnneal };
  const stageState = { current: d.trStageCurrent, done: d.trStageDone, ahead: d.trStageAhead, unknown: '—' };
  const stagePill = { current: 'is-warning', done: 'is-ok', ahead: 'is-neutral', unknown: 'is-neutral' };
  const stageBegins = { foundation: 0, capability: curriculum.capability_starts_at_step, anneal: curriculum.anneal_starts_at_step };
  const stageEnds = { foundation: curriculum.capability_starts_at_step, capability: curriculum.anneal_starts_at_step, anneal: curriculum.full_pass_steps };
  const stageTokens = curriculum.tokens_by_stage || {};
  const block = data.block_storage || {};
  const copies = Array.isArray(block.copies) ? block.copies : [];
  const newestCopy = copies[copies.length - 1];
  const checkpoint = data.checkpoint || null;
  const activeKernels = Object.entries((last.kernels_active || run.kernels?.active || {})).filter(([, on]) => on).map(([name]) => name);
  const deviations = Array.isArray(run.deviations) ? run.deviations : [];
  const reads = last.shard_reads || {};
  const planShort = curriculum.planned_end_reaches && curriculum.planned_end_reaches !== 'anneal_end';
  const series = Array.isArray(data.series) ? data.series : [];
  const plan = sum.plan || null;
  return (
    <AdminSection id="datalake-training-run" title={d.trTitle} subtitle={d.trSubtitle} state={stateWord} tone={tone}>
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <KpiTile label={d.trKpiProgress} value={`${progress.pctText} %`} bar={progress.share}
          sub={fill(d.trProgressSub, { step: progress.stepText, max: progress.maxText, elapsed: progress.elapsedText })}
          sub2={sum.end_at_estimate ? fill(d.trEndAt, { time: progress.endText }) : ''} />
        <KpiTile label={d.trKpiTokens} value={formatCompact(sum.tokens_seen, isDe)}
          bar={Number(sum.tokens_planned) ? Number(sum.tokens_seen) / Number(sum.tokens_planned) : null}
          sub={fill(d.trTokensSub, { planned: formatCompact(sum.tokens_planned, isDe) })} />
        <KpiTile label={d.trKpiThroughput} value={`${formatNumber(last.tokens_per_s, isDe)} ${d.trTokensPerSecond}`}
          sub={data.step_timing?.wall_clock_seconds
            ? fill(d.trStepTimeSub, { wall: formatNumber(data.step_timing.wall_clock_seconds, isDe, 1),
              compute: data.step_timing.compute_seconds ? formatNumber(data.step_timing.compute_seconds, isDe, 1) : '—' })
            : (data.seconds_per_step ? `${formatNumber(data.seconds_per_step, isDe, 1)} ${d.trSecondsPerStep}` : '')} />
        <KpiTile label={d.trKpiLoss} value={formatNumber(sum.loss_now, isDe, 2)}
          sub={[sum.loss_best !== undefined && sum.loss_best !== null ? fill(d.trBest, { value: formatNumber(sum.loss_best, isDe, 2) }) : '',
            trend !== null ? fill(d.trHourAgo, { value: `${trend <= 0 ? '−' : '+'}${formatNumber(Math.abs(trend), isDe, 2)}` }) : '',
            sum.perplexity_ntp ? fill(d.trPerplexity, { value: formatCompact(sum.perplexity_ntp, isDe) }) : ''].filter(Boolean).join(' · ')} />
        <KpiTile label={d.trKpiLr} value={Number.isFinite(Number(sum.lr)) ? Number(sum.lr).toExponential(1) : '—'}
          sub={phaseWord(d, sum.lr_phase)} />
        {plan ? (
          <KpiTile label={d.trKpiEpoch}
            value={plan.epoch ? fill(d.trEpochValue, { epoch: formatNumber(plan.epoch, isDe), epochs: formatNumber(plan.epochs, isDe) }) : '—'}
            sub={phaseWord(d, plan.phase)} />
        ) : null}
        <KpiTile label={d.trKpiGpu} value={`${formatNumber(sum.vram_used_gb, isDe, 1)} / ${formatNumber(sum.vram_cap_gb, isDe, 0)} GB`}
          sub={last.ts ? `${d.trLastHeartbeat}: ${fill(d.trAgo, { age: formatSpan(Number(data.last_age_seconds)) })}` : ''}
          tone={data.state === 'silent' ? 'warn' : ''} />
      </div>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <MiniLineChart d={d} isDe={isDe} series={series} title={d.trLossTitle} digits={2} yTitle={d.trAxisLoss}
          lines={RUN_SERIES.map((entry) => ({ key: entry.key, color: entry.color, label: d[entry.labelKey] }))} />
        <MiniLineChart d={d} isDe={isDe} series={series} title={d.trThroughputTitle} digits={0} yTitle={d.trTokensPerSecond}
          lines={[{ key: 'tokens_per_s', color: '#2a78d6', label: d.trTokensPerSecond }]} />
      </div>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))' }}>
        {plan ? (
          <div className="space-y-2" style={{ minWidth: 0 }}>
            <h4 className="text-sm font-semibold">{d.trPlanTitle}</h4>
            <p className="text-xs text-text-secondary">{d.trPlanExplain}</p>
            <FactList items={[
              [d.trPlanPhase, phaseWord(d, plan.phase)],
              [d.trKpiEpoch, plan.epoch ? fill(d.trEpochValue, { epoch: formatNumber(plan.epoch, isDe), epochs: formatNumber(plan.epochs, isDe) }) : null],
              [d.trPlanStableSteps, plan.stable_steps_planned ? formatNumber(plan.stable_steps_planned, isDe) : null],
              [d.trPlanDecay, plan.decay_started
                ? fill(d.trPlanDecayRunning, { step: formatNumber(plan.decay_starts_at_step, isDe), steps: formatNumber(plan.decay_steps, isDe) })
                : (plan.decay_starts_at_step
                  ? (plan.decay_starts_in_seconds
                    ? fill(d.trPlanDecayAhead, { step: formatNumber(plan.decay_starts_at_step, isDe), time: formatSpan(Number(plan.decay_starts_in_seconds)) })
                    : fill(d.trPlanDecayAheadNoTime, { step: formatNumber(plan.decay_starts_at_step, isDe) }))
                  : null)],
              [d.trPlanTokensPerStep, plan.tokens_per_step
                ? fill(d.trPlanTokensPerStepValue, { tokens: formatNumber(plan.tokens_per_step, isDe), accum: formatNumber(plan.accum, isDe) })
                : null],
              [d.trPlanRecompute, plan.activation_recomputation === null || plan.activation_recomputation === undefined
                ? null : (plan.activation_recomputation ? d.trPlanRecomputeOn : d.trPlanRecomputeOff)],
              [d.trPlanFim, plan.fim_documents !== null && plan.fim_documents !== undefined ? formatNumber(plan.fim_documents, isDe) : null],
            ]} />
          </div>
        ) : null}
        <div className="space-y-1" style={{ minWidth: 0, display: plan ? 'none' : undefined }}>
          <h4 className="text-sm font-semibold">{d.trCurriculumTitle}</h4>
          <CurriculumTimer d={d} data={data} isDe={isDe} stageWord={stageWord} />
          <ul className="space-y-1" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {(Array.isArray(data.stages) ? data.stages : []).map((stage) => (
              <li key={stage.stage} className="flex flex-wrap items-center gap-2 text-xs" style={{ padding: '4px 0', borderBottom: '1px solid #e2e2e6' }}>
                <span className="text-sm" style={{ minWidth: '11rem' }}>{stageWord[stage.stage] || stage.stage}</span>
                <span className={`admin-status-pill ${stagePill[stage.state] || 'is-neutral'}`}>{stageState[stage.state] || stage.state}</span>
                <span className="text-text-secondary" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                  {stageTokens[stage.stage] ? fill(d.trStageTokens, { tokens: formatCompact(stageTokens[stage.stage], isDe) }) : `${formatNumber(stage.rows, isDe)} ${d.trRows}`}
                  {Number.isFinite(Number(stageBegins[stage.stage])) && stage.stage !== 'foundation' ? ` · ${fill(d.trStageBegins, { step: formatNumber(stageBegins[stage.stage], isDe) })}` : ''}
                  {stage.state === 'current' && Number.isFinite(Number(stageBegins[stage.stage])) && Number.isFinite(Number(stageEnds[stage.stage]))
                    ? ` · ${fill(d.trStageShare, { pct: formatNumber(Math.max(0, Math.min(100, ((Number(data.step) - Number(stageBegins[stage.stage])) / Math.max(1, Number(stageEnds[stage.stage]) - Number(stageBegins[stage.stage]))) * 100)), isDe, 2) })}`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
          {curriculum.planned_end_reaches ? (
            <p className={`text-xs ${planShort ? '' : 'text-text-secondary'}`}>
              {planShort ? <span className="admin-status-pill is-missing">{d.trPlanWarningLabel}</span> : null}{' '}
              {planShort
                ? fill(d.trPlanWarning, {
                  max: formatNumber(data.max_steps, isDe), stage: stageWord[curriculum.planned_end_reaches] || curriculum.planned_end_reaches,
                  cap: formatNumber(curriculum.capability_starts_at_step, isDe), steps: formatNumber(curriculum.full_pass_steps, isDe),
                  days: formatNumber(Number(curriculum.full_pass_seconds_estimate) / 86400, isDe, 0),
                })
                : fill(d.trPlanOk, { stage: stageWord[curriculum.planned_end_reaches] || curriculum.planned_end_reaches })}
            </p>
          ) : null}
        </div>
        <div className="space-y-1" style={{ minWidth: 0 }}>
          <h4 className="text-sm font-semibold">{d.trCheckpointsTitle}</h4>
          <FactList items={[
            [d.trCheckpoint, checkpoint ? `${d.trStep} ${formatNumber(checkpoint.step, isDe)} · ${fill(d.trAgo, { age: formatSpan(Number(data.checkpoint_age_seconds)) })}` : d.trCheckpointNone],
            [d.trNextCheckpoint, Number.isFinite(Number(sum.next_checkpoint_in_seconds)) ? fill(d.trInSpan, { span: formatSpan(Number(sum.next_checkpoint_in_seconds)) }) : '—'],
            [d.trBlockStorage, [newestCopy ? `${d.trStep} ${formatNumber(newestCopy.step, isDe)} · ${fill(d.trAgo, { age: formatSpan(Number(newestCopy.age_seconds)) })} · ${formatNumber(Number(newestCopy.bytes) / 1e9, isDe, 1)} GB` : d.trBlockStorageNone,
              Array.isArray(block.uploading_steps) && block.uploading_steps.length ? fill(d.trUploading, { step: formatNumber(block.uploading_steps[block.uploading_steps.length - 1], isDe) }) : ''].filter(Boolean).join(' · ')],
            [d.trCheckpoints, `${(Array.isArray(data.checkpoints) ? data.checkpoints : []).filter((entry) => entry.complete).length} · ${fill(d.trCheckpointEvery, { minutes: formatNumber(run.checkpoint_every_minutes, isDe) })}`],
            [d.trResumes, run.resumed_at_step ? fill(d.trResumed, { step: formatNumber(run.resumed_at_step, isDe) }) : d.trNoResume],
          ]} />
        </div>
      </div>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))' }}>
        <div className="space-y-1" style={{ minWidth: 0 }}>
          <h4 className="text-sm font-semibold">{d.trConfig}</h4>
          <FactList items={[
            [d.trRun, run.run_id],
            [d.trVersion, run.tdv_id],
            [d.trModel, [run.codename, run.parameters ? `${formatNumber(Number(run.parameters) / 1e9, isDe, 2)} B` : null, run.preset].filter(Boolean).join(' · ')],
            [d.trMicroBatch, `${formatNumber(run.micro_batch, isDe)} / ${formatNumber(run.tokens_per_step, isDe)}`],
            [d.trSeqLen, formatNumber(run.seq_len, isDe)],
            [d.trLrPlan, fill(d.trLrPlanValue, { lr: Number.isFinite(Number(run.learning_rate)) ? Number(run.learning_rate).toExponential(1) : '—', warmup: formatNumber(run.warmup_steps, isDe), max: formatNumber(run.max_steps, isDe) })],
          ]} />
        </div>
        <div className="space-y-1" style={{ minWidth: 0 }}>
          <h4 className="text-sm font-semibold">{d.trHealth}</h4>
          <FactList items={[
            [d.trSpikes, formatNumber(sum.spikes_last_hour, isDe)],
            [d.trShardReads, `${formatNumber(reads.local, isDe)} / ${formatNumber(reads.worker, isDe)}`],
            [d.trKernels, activeKernels.length ? activeKernels.map(kernelName).join(', ') : '—'],
          ]} />
          <h4 className="text-sm font-semibold">{d.devTitle}</h4>
          {deviations.length ? (
            <ul className="space-y-2" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {deviations.map((code) => {
                const item = describeDeviation(code, d, isDe);
                return (
                  <li key={code} className="homepage-muted-panel p-2" title={code}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold">{item.what}</span>
                      <span className={`admin-status-pill ${item.kind === 'planned' ? 'is-neutral' : 'is-warning'}`}>{item.kind === 'planned' ? d.devPlanned : d.devLimit}</span>
                    </div>
                    <p className="text-xs" style={{ margin: '4px 0 0', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{item.text}</p>
                  </li>
                );
              })}
            </ul>
          ) : <p className="text-xs text-text-secondary">{d.devNone}</p>}
        </div>
      </div>
    </AdminSection>
  );
};

// The data readiness of every strand (D-SAAS-164 §4): while one strand trains, the others are prepared — this
// card says, per strand, how far its data stands and what it still lacks before its own version is sealed.
export const TextbookProgramCard = ({ d, autopilot, isDe }) => {
  const program = autopilot?.textbook;
  if (!program) return null;
  const tokens = Number(program.tokens) || 0;
  const target = Number(program.target_tokens) || 0;
  const share = target ? Math.min(1, tokens / target) : null;
  return (
    <AdminSection id="datalake-textbook-program" title={d.tbTitle} subtitle={d.tbSubtitle}
      state={share !== null ? `${formatNumber(share * 100, isDe, 2)} %` : '—'} tone={share !== null && share >= 1 ? 'ok' : 'neutral'}>
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        <KpiTile label={d.tbTokens} value={formatCompact(tokens, isDe)} bar={share}
          sub={target ? fill(d.tbTokensSub, { pct: formatNumber((share || 0) * 100, isDe, 2), target: formatCompact(target, isDe) }) : ''} />
        <KpiTile label={d.tbSamples} value={formatNumber(program.samples, isDe)} />
        <KpiTile label={d.tbJobs} value={formatNumber(program.jobs, isDe)} />
        <KpiTile label={d.tbTeacher} value={program.teacher_tokens_per_textbook_token ? formatNumber(program.teacher_tokens_per_textbook_token, isDe, 2) : '—'} />
        <KpiTile label={d.tbCost}
          value={program.cost_usd_per_million_tokens !== null && program.cost_usd_per_million_tokens !== undefined
            ? `${formatNumber(program.cost_usd_per_million_tokens, isDe, 2)} USD` : '—'}
          sub={program.cost_usd_per_million_tokens === null || program.cost_usd_per_million_tokens === undefined ? d.tbCostNone : ''} />
      </div>
      <p className="text-xs text-text-secondary">{d.tbExplain}</p>
    </AdminSection>
  );
};

export const StrandReadinessCard = ({ d, autopilot, isDe }) => {
  const strands = Array.isArray(autopilot?.strands) ? autopilot.strands : [];
  if (!strands.length) return null;
  // "Training now" only while a run trains; the selected strand without a run is named as selected.
  const training = autopilot?.phase === 'training';
  const capabilityWord = (id) => d[`cap_${id}`] || id;
  const lackWord = { thresholds: d.srLackThresholds, data_plan: d.srLackDataPlan, codec: d.srLackCodec, tokenizer: d.srLackTokenizer,
    sealed_version: d.srLackVersion };
  const modalityWord = { text: d.modText, image: d.modImage, audio: d.modAudio, video: d.modVideo };
  const mods = (list) => (Array.isArray(list) ? list : []).map((m) => modalityWord[m] || m).join(', ');
  return (
    <AdminSection id="datalake-strand-readiness" title={d.srTitle} subtitle={d.srSubtitle} count={strands.length}>
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        {strands.map((entry) => {
          const met = Number(entry.thresholds_met || 0);
          const total = Number(entry.thresholds_total || 0);
          const ready = !(entry.lacking || []).length;
          return (
            <div key={entry.strand} className="homepage-muted-panel p-3 space-y-2" style={{ minWidth: 0 }}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">{entry.codename || entry.strand}</span>
                {entry.selected ? <span className={`admin-status-pill ${training ? 'is-warning' : 'is-neutral'}`}>{training ? d.srTrainingNow : d.srSelected}</span>
                  : <span className={`admin-status-pill ${ready ? 'is-ok' : 'is-neutral'}`}>{ready ? d.srDataReady : d.srPreparing}</span>}
              </div>
              <div className="text-xs text-text-secondary">
                {fill(d.srModalities, { in: mods(entry.modalities_in), out: mods(entry.modalities_out) })}
              </div>
              {entry.data_plan ? (
                <div className="text-xs">
                  <strong>{fill(d.srDataPlan, { met: formatNumber(entry.data_plan.reached, isDe), total: formatNumber(entry.data_plan.total, isDe) })}</strong>
                  {(entry.data_plan.open || []).length ? (
                    <span className="text-text-secondary">{' · '}{fill(d.srPlanOpen, { areas: entry.data_plan.open.map((area) => d[`tpPlanCat_${area}`] || area).join(', ') })}</span>
                  ) : null}
                </div>
              ) : (
              <div className="text-xs">
                <strong>{fill(d.srThresholds, { met: formatNumber(met, isDe), total: formatNumber(total, isDe) })}</strong>
                <ul style={{ listStyle: 'none', margin: '2px 0 0', padding: 0 }}>
                  {(entry.thresholds || []).map((row) => (
                    <li key={`${row.capability}/${row.mod_sig}`} className="flex flex-wrap items-center gap-2" style={{ padding: '1px 0' }}>
                      <span className={`admin-status-pill ${row.met ? 'is-ok' : 'is-warning'}`}>{row.met ? d.tpDone : d.tpRunning}</span>
                      <span>{capabilityWord(row.capability)}</span>
                      <span className="text-text-secondary">{fill(d.tpThresholdRow, { measured: formatNumber(row.measured_samples, isDe), target: formatNumber(row.min_samples, isDe) })}</span>
                    </li>
                  ))}
                </ul>
              </div>
              )}
              <div className="text-xs">
                <strong>{d.srCorpus}:</strong> {entry.corpus_tokens ? fill(d.srCorpusValue, { tokens: formatCompact(entry.corpus_tokens, isDe), shards: formatNumber(entry.corpus_chunk_shards_tokenized, isDe) }) : '—'}
              </div>
              <div className="text-xs">
                <strong>{d.srVersion}:</strong>{' '}
                {entry.newest_version ? `${entry.newest_version.tdv_id} · ${entry.newest_version.status === 'sealed' ? d.tdvStatusSealed : d.tdvStatusDraft}${entry.newest_version.sealed_at ? ` · ${formatBerlin(entry.newest_version.sealed_at)}` : ''}` : d.srNoVersion}
              </div>
              <div className="text-xs">
                <strong>{d.srLacking}:</strong> {(entry.lacking || []).length ? entry.lacking.map((item) => lackWord[item] || item).join(' · ') : d.srLacksNothing}
              </div>
              <p className="text-xs text-text-secondary" style={{ margin: 0 }}>{entry.selected ? (training ? d.srSelectedNote : d.srSelectedIdleNote) : d.srUnselectedNote}</p>
            </div>
          );
        })}
      </div>
    </AdminSection>
  );
};

// D-HP-ADMIN-090: the training page opens with the situation in plain words — when the training can start at the
// earliest and what binds it, one row per area of the paper with German and English as bars, what runs now and what
// comes after the data. Every figure is the engine's measurement (the data plan of the readiness door).
const SituationBar = ({ value, target, isDe }) => {
  const have = Number(value || 0);
  const want = Number(target || 0);
  const share = want > 0 ? Math.min(1, have / want) : 0;
  const done = want > 0 && have >= want;
  return (
    <div style={{ minWidth: '8rem' }}>
      <div style={{ height: 6, background: '#e2e2e6', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${Math.round(share * 100)}%`, height: '100%', background: done ? '#2e7d32' : '#2a78d6' }} />
      </div>
      <div className="text-xs" style={{ marginTop: 2 }}>
        {formatCompact(have * 1e9, isDe)} / {formatCompact(want * 1e9, isDe)}
      </div>
    </div>
  );
};

export const SituationCard = ({ d, plan, isDe, held }) => {
  const rows = Array.isArray(plan?.categories) ? plan.categories : [];
  if (!rows.length) return null;
  const word = (row) => d[`tpPlanCat_${row.category}`] || (isDe ? row.label_de : row.label_en);
  const languageWord = (code) => (code === 'de' ? d.tpLangDe : d.tpLangEn);
  const open = rows.filter((row) => row.met !== true);
  const noSource = open.filter((row) => row.binding === 'source');
  // D-SAAS-252: the blocks the run needs besides the data; a block that does not exist yet holds the start
  const blocks = Array.isArray(plan?.blocks) ? plan.blocks : [];
  const missing = blocks.filter((block) => block.state === 'not_built');
  const blockWord = (block) => d[`sitBlock_${block.block}`] || (isDe ? block.label_de : block.label_en);
  const stateWord = { not_built: d.sitStateNotBuilt, waiting: d.sitStateWaiting, ready: d.sitStateReady };
  const statePill = { not_built: 'is-missing', waiting: 'is-warning', ready: 'is-ok' };
  const duration = plan?.duration || null;
  const timed = open.filter((row) => row.binding !== 'source' && Number.isFinite(Number(row.eta_hours)) && Number(row.eta_hours) > 0);
  const slowest = timed.reduce((best, row) => (!best || Number(row.eta_hours) > Number(best.eta_hours) ? row : best), null);
  const timeText = (hours) => (hours >= 48 ? fill(d.sitDays, { n: formatNumber(Math.round(hours / 24), isDe) })
    : fill(d.sitHours, { n: formatNumber(Math.max(1, Math.round(hours)), isDe) }));
  // the languages of an area that have no source of their own; the other language's inflow never fills them
  const sourceless = (row) => row.languages_without_source || row.open_languages || [];
  // D-SAAS-263: the binding stage names the language half it belongs to — a waiting half before it, the half that
  // sets the time after it
  const bindWord = (row) => {
    if (row.binding === 'source') return `${sourceless(row).map(languageWord).join(', ')}: ${d.sitNoSource}`;
    const word = (row.binding && d[`tpBind_${row.binding}`]) || '—';
    if (Array.isArray(row.languages_waiting) && row.languages_waiting.length) {
      return `${row.languages_waiting.map(languageWord).join(', ')}: ${word}`;
    }
    return row.binding_language ? `${word} (${languageWord(row.binding_language)})` : word;
  };
  let headline;
  if (missing.length) headline = fill(d.sitHeadlineBlocks, { items: missing.map(blockWord).join(', ') });
  else if (!open.length) headline = d.sitHeadlineReached;
  else if (noSource.length) {
    headline = fill(d.sitHeadlineBlocked, { items: noSource.map((row) => `${word(row)} (${sourceless(row).map(languageWord).join(', ')})`).join(', ') });
  } else if (slowest) {
    headline = fill(d.sitHeadlineDays, { time: timeText(Number(slowest.eta_hours)), area: word(slowest), bottleneck: bindWord(slowest) });
  } else headline = d.sitHeadlineUnknown;
  const sum = (key) => rows.reduce((total, row) => total + Number(row.inflow?.[key] || 0), 0);
  const running = [
    [d.sitDownloads, sum('download_bytes_1h') > 0 ? fill(d.sitPerHour, { value: `${formatNumber(sum('download_bytes_1h') / 1e9, isDe, 1)} GB` }) : d.sitIdle],
    [d.sitIngest, sum('ingest_tokens_1h') > 0 ? fill(d.sitPerHourTokens, { value: formatCompact(sum('ingest_tokens_1h'), isDe) }) : d.sitIdle],
    [d.sitTokenize, sum('tokenize_tokens_1h') > 0 ? fill(d.sitPerHourTokens, { value: formatCompact(sum('tokenize_tokens_1h'), isDe) }) : d.sitIdle],
    [d.sitGenerate, sum('accepted_tokens_1h') > 0 ? fill(d.sitPerHourTokens, { value: formatCompact(sum('accepted_tokens_1h'), isDe) }) : d.sitIdle],
  ];
  return (
    <AdminSection id="datalake-situation" title={d.sitTitle} subtitle={d.sitSubtitle}
      tone={open.length ? (noSource.length ? 'error' : 'warning') : 'ok'}>
      <p className="text-base font-semibold" style={{ margin: 0 }}>{headline}</p>
      {missing.length && (noSource.length || slowest) ? (
        <p className="text-sm" style={{ margin: 0 }}>
          {noSource.length
            ? fill(d.sitHeadlineBlocked, { items: noSource.map((row) => `${word(row)} (${sourceless(row).map(languageWord).join(', ')})`).join(', ') })
            : fill(d.sitHeadlineDays, { time: timeText(Number(slowest.eta_hours)), area: word(slowest), bottleneck: bindWord(slowest) })}
        </p>
      ) : null}
      {duration ? (
        <p className="text-sm text-text-secondary" style={{ margin: 0 }}>
          {fill(d.sitDuration, { days: formatNumber(Math.round(duration.days), isDe), budget: formatCompact(duration.budget_tokens_b * 1e9, isDe),
            rate: formatNumber(duration.tokens_per_s, isDe) })}
        </p>
      ) : null}
      {held ? <p className="text-sm text-text-secondary" style={{ margin: 0 }}>{d.sitHold}</p> : null}
      <div className="admin-table-shell"><div className="admin-table-scroll">
        <table className="admin-table">
          <thead>
            <tr><th>{d.sitArea}</th><th>{d.tpLangDe}</th><th>{d.tpLangEn}</th><th>{d.sitTotal}</th>
              <th>{d.sitFlow}</th><th>{d.sitLeft}</th><th>{d.sitBottleneck}</th></tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const languages = row.by_language_b && row.language_target_b ? row.by_language_b : null;
              const hours = Number(row.eta_hours);
              return (
                <tr key={row.category}>
                  <td className="text-sm">{word(row)}</td>
                  <td>{languages ? <SituationBar value={languages.de} target={row.language_targets_b?.de ?? row.language_target_b} isDe={isDe} /> : <span className="text-xs text-text-secondary">{d.sitNoLanguage}</span>}</td>
                  <td>{languages ? <SituationBar value={languages.en} target={row.language_targets_b?.en ?? row.language_target_b} isDe={isDe} /> : <span className="text-xs text-text-secondary">{d.sitNoLanguage}</span>}</td>
                  <td><SituationBar value={row.measured_b} target={row.target_b} isDe={isDe} /></td>
                  <td className="text-xs">{Number(row.rate_tokens_per_hour) > 0 ? fill(d.sitPerHourTokens, { value: formatCompact(row.rate_tokens_per_hour, isDe) }) : '—'}</td>
                  <td className="text-xs">{row.met === true ? d.sitReached : (row.binding === 'source' ? d.sitNoSource
                    : (row.binding === 'paused' ? d.tpBind_paused : (Number.isFinite(hours) && hours > 0 ? timeText(hours) : '—')))}</td>
                  <td className="text-xs">{row.met === true ? '—' : bindWord(row)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div></div>
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        <div className="homepage-muted-panel p-3 space-y-1 text-sm">
          <div className="font-semibold">{d.sitRunningTitle}</div>
          {running.map(([label, value]) => (<div key={label}>{label}: <span className="text-text-secondary">{value}</span></div>))}
        </div>
        <div className="homepage-muted-panel p-3 space-y-1 text-sm">
          <div className="font-semibold">{d.sitBlocksTitle}</div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {blocks.map((block) => (
              <li key={block.block} className="flex flex-wrap items-center gap-2" style={{ padding: '2px 0' }}>
                <span className={`admin-status-pill ${statePill[block.state] || 'is-neutral'}`}>{stateWord[block.state] || block.state}</span>
                <span>{blockWord(block)}</span>
                {block.block === 'data' && block.detail
                  ? <span className="text-text-secondary text-xs">{fill(d.tpPlanDetail, { met: block.detail.reached, total: block.detail.areas })}</span> : null}
                {/* D-SAAS-256 §2: what the tokenizer still waits for, area and language */}
                {block.block === 'tokenizer' && Array.isArray(block.detail?.missing) && block.detail.missing.length
                  ? <span className="text-text-secondary text-xs">{fill(d.sitBlockWaitsFor, { items: block.detail.missing.map((cell) => `${d[`tpPlanCat_${cell.area}`] || cell.area}${cell.language === 'code' ? '' : ` (${languageWord(cell.language)})`}`).join(', ') })}</span> : null}
                {/* D-SAAS-260, D-SAAS-265: the parts of a block that exist beside the parts still open */}
                {Array.isArray(block.detail?.built) && Array.isArray(block.detail?.open) && block.detail.open.length
                  ? <span className="text-text-secondary text-xs">{fill(d.sitBlockParts, { built: block.detail.built.map((part) => d[`part_${part}`] || part).join(', '), open: block.detail.open.map((part) => d[`part_${part}`] || part).join(', ') })}</span> : null}
                {/* D-SAAS-259: how far the benchmark suite has grown */}
                {block.block === 'bench' && block.detail && Number.isFinite(Number(block.detail.of))
                  ? <span className="text-text-secondary text-xs">{fill(d.sitBenchProgress, { made: formatNumber(block.detail.items, isDe), total: formatNumber(block.detail.of, isDe) })}</span> : null}
              </li>
            ))}
          </ul>
        </div>
        <div className="homepage-muted-panel p-3 space-y-1 text-sm">
          <div className="font-semibold">{d.sitNextTitle}</div>
          <div className="text-text-secondary">{d.sitNextSteps}</div>
          {plan?.provisional ? <div className="text-xs text-text-secondary">{d.sitProvisional}</div> : null}
        </div>
      </div>
    </AdminSection>
  );
};

export const TrainingPathCard = ({ d, autopilot, checklist, runs, isDe, live = null, plan = null }) => {
  const now = Date.now();
  const actions = Array.isArray(autopilot?.cycle_actions) ? autopilot.cycle_actions : null;
  const counts = autopilot?.cycle_progress || {};
  const thresholds = (Array.isArray(autopilot?.thresholds) ? autopilot.thresholds : [])
    .filter((row) => row && row.enabled !== false && row.applies_to_strand !== false);
  const metCount = thresholds.filter((row) => row.met === true).length;
  const items = Object.fromEntries((Array.isArray(checklist?.items) ? checklist.items : []).map((item) => [item.id, item]));
  // A display that does not explain itself is worthless: every step says what it means,
  // and the thresholds step lists every capability with its measured count against its target.
  const capabilityWord = (id) => d[`cap_${id}`] || id;
  // D-HP-ADMIN-087 (D-SAAS-244): a strand with a data plan starts on its paper's plan — every area against its amount,
  // German and English each against half of it, code without a language — not on the capability thresholds.
  const planRows = Array.isArray(plan?.categories) ? plan.categories : [];
  const steps = planRows.length ? [{
    key: 'data_plan', label: d.tpStepPlan, explain: d.tpExplainPlan,
    state: planRows.every((row) => row.met === true) ? 'done' : 'running',
    detail: [fill(d.tpPlanDetail, { met: planRows.filter((row) => row.met === true).length, total: planRows.length }),
      d.tpPlanSeeSituation].filter(Boolean).join(' · '),
    rows: [],
  }, {
    key: 'own_tokenizer', label: d.tpStepOwnTokenizer, explain: d.tpStepOwnTokenizerExplain,
    state: plan.tokenizer_released ? 'done' : 'waiting',
  }] : [{
    key: 'thresholds', label: d.tpStepThresholds, explain: d.tpExplainThresholds,
    state: thresholds.length && metCount === thresholds.length ? 'done' : 'running',
    detail: thresholds.length ? fill(d.tpThresholdsDetail, { met: metCount, total: thresholds.length }) : '—',
    rows: thresholds.map((row) => ({
      key: `${row.capability}/${row.mod_sig}`, label: capabilityWord(row.capability), met: row.met === true,
      text: fill(d.tpThresholdRow, { measured: formatNumber(row.measured_samples, isDe), target: formatNumber(row.min_samples, isDe) }),
    })),
  }];

  PATH_ACTIONS.forEach(([kind, labelKey]) => {
    const action = actions ? actions.find((row) => row.kind === kind) : null;
    const counted = counts[kind] || null;
    if (!action && !counted) return;
    const rawState = action ? action.state : Object.keys(counted).find((state) => counted[state] > 0);
    const created = parseTime(action?.created_at);
    const finished = parseTime(action?.finished_at);
    steps.push({
      key: kind, label: d[labelKey] || kind, explain: d[`${labelKey}Explain`] || '', state: ACTION_STATE[rawState] || 'waiting',
      since: action?.created_at || null, heartbeat: action?.heartbeat_at || null,
      duration: created ? (finished || now) - created : null,
      error: action?.error || '', attempts: action?.attempts,
    });
  });
  if (!steps.some((step) => step.key === 'tdv_seal')) {
    steps.push({ key: 'tdv_seal', label: d.tpStepTdvSeal, explain: d.tpStepTdvSealExplain, state: items.sealed_tdv?.satisfied ? 'done' : 'waiting' });
  }
  if (autopilot?.blocked_code === 'PAUSED_AFTER_SEAL') {
    // D-SAAS-307: the sealed version waits for the review before the path goes on
    steps.push({ key: 'review', label: d.apAfterSealStep, explain: d.apAfterSealHint, state: 'waiting' });
  }
  steps.push({ key: 'compat', label: d.tpStepCompat, explain: d.tpStepCompatExplain, state: items.compat_all_green?.satisfied ? 'done' : 'waiting' });
  const vramDetail = items.vram_budget?.detail || {};
  steps.push({ key: 'vram', label: d.tpStepVram, explain: d.tpStepVramExplain, state: items.vram_budget?.satisfied ? 'done' : 'waiting',
    // while a run trains the card is the run's own (D-SAAS-162 §8): say so instead of "0 GB free"; without a run the
    // engine's own note says why nothing was measured
    detail: vramDetail.note && autopilot?.phase === 'training'
      ? fill(d.tpVramRunning, { need: formatNumber(vramDetail.declared_gb, isDe, 0) })
      : vramDetail.note ? pick(vramDetail, 'note', isDe)
      : (vramDetail.live_free_gb !== undefined && vramDetail.live_free_gb !== null && vramDetail.declared_gb
        ? fill(d.tpVramDetail, { free: formatNumber(vramDetail.live_free_gb, isDe, 1), need: formatNumber(vramDetail.declared_gb, isDe, 0) }) : '') });
  // Only an active run stands on this path; an aborted, failed or finished run is history (the runs section), and
  // the path then leads to the next run.
  const run = (Array.isArray(runs) ? runs : [])
    .find((row) => ACTIVE_RUN_STATES.has(String(row?.state || row?.status || ''))) || null;
  const runState = String(run?.state || run?.status || '');
  steps.push({ key: 'start', label: d.tpStepTrainStart, explain: d.tpStepTrainStartExplain, state: run ? 'done' : 'waiting', since: run?.started_at || null });
  const liveProgress = runProgress(live, isDe);
  steps.push({
    key: 'training', label: d.tpStepTraining, explain: d.tpStepTrainingExplain,
    state: run ? 'running' : 'waiting',
    detail: liveProgress && /run/i.test(runState)
      ? (liveProgress.endText !== '—'
        ? fill(d.trPathTrainingDetail, { pct: liveProgress.pctText, step: liveProgress.stepText, max: liveProgress.maxText, end: liveProgress.endText })
        : fill(d.trPathTrainingDetailNoEnd, { pct: liveProgress.pctText, step: liveProgress.stepText, max: liveProgress.maxText }))
      : (runState || ''),
  });
  const currentIndex = steps.findIndex((step) => step.state !== 'done' && step.state !== 'skipped');
  const current = currentIndex >= 0 ? steps[currentIndex] : null;
  const progress = Array.isArray(autopilot?.work_progress?.corpus) ? autopilot.work_progress.corpus : [];
  const failed = steps.filter((step) => step.state === 'failed');
  const pill = { done: 'is-ok', running: 'is-warning', waiting: 'is-neutral', failed: 'is-missing', skipped: 'is-neutral' };
  const word = { done: d.tpDone, running: d.tpRunning, waiting: d.tpWaiting, failed: d.tpFailed, skipped: d.tpSkipped };
  const phaseWord = { reading: d.tpPhaseReading, admission: d.tpPhaseAdmission, encoding: d.tpPhaseEncoding };
  return (
    <AdminSection id="datalake-training-path" title={d.tpTitle} subtitle={d.tpSubtitle}
      state={current ? `${d.tpStep} ${currentIndex + 1} ${d.tpOf} ${steps.length}` : d.tpDone}
      tone={failed.length || autopilot?.blocked_code ? 'error' : (current ? 'warning' : 'ok')}>
      <p className="text-sm">
        <strong>{d.tpNow}:</strong>{' '}
        {current ? `${d.tpStep} ${currentIndex + 1} ${d.tpOf} ${steps.length} — ${current.label}` : d.tpStepTraining}
        {current?.duration ? ` · ${d.tpFor} ${formatDuration(current.duration)}` : ''}
      </p>
      {progress.map((entry) => {
        const done = Number(entry.rows_done || 0);
        const total = Number(entry.rows_total || 0);
        const started = parseTime(entry.phase_started_at || entry.started_at);
        const updated = parseTime(entry.updated_at) || now;
        const seconds = started ? (updated - started) / 1000 : 0;
        const rate = seconds > 0 ? done / seconds : 0;
        const remaining = rate > 0 && total > done ? ((total - done) / rate) * 1000 : null;
        return (
          <div key={entry.chunk_shard_id} className="homepage-muted-panel p-3 space-y-1">
            <ProgressRow label={`${d.tpProgress} · ${phaseWord[entry.phase] || entry.phase || ''}`} value={done} target={total || null} met={total > 0 && done >= total} />
            <div className="text-xs text-text-secondary">
              {done.toLocaleString(isDe ? 'de-DE' : 'en-GB')} / {total.toLocaleString(isDe ? 'de-DE' : 'en-GB')} {d.tpRows}
              {rate > 0 ? ` · ${Math.round(rate).toLocaleString(isDe ? 'de-DE' : 'en-GB')} ${d.tpRows} ${d.tpRate}` : ''}
              {remaining ? ` · ${d.tpRemaining}: ${formatDuration(remaining)} (${d.tpEstimateNote})` : ''}
            </div>
          </div>
        );
      })}
      <table className="admin-table">
        <tbody>
          {steps.map((step, index) => (
            <tr key={step.key}>
              <td className="text-xs">{index + 1}</td>
              <td className="text-sm" style={{ whiteSpace: 'normal', minWidth: '14rem' }}>
                <div>{step.label}</div>
                {step.explain ? <div className="text-xs text-text-secondary" style={{ whiteSpace: 'normal' }}>{step.explain}</div> : null}
              </td>
              <td><span className={`admin-status-pill ${pill[step.state] || 'is-neutral'}`}>{word[step.state] || step.state}</span></td>
              <td className="text-xs text-text-secondary" style={{ whiteSpace: 'normal' }}>
                {step.since ? `${d.tpSince} ${formatBerlin(step.since)}` : ''}
                {step.duration ? ` · ${formatDuration(step.duration)}` : ''}
                {step.heartbeat && step.state === 'running' ? ` · ${d.tpHeartbeat} ${formatBerlin(step.heartbeat)}` : ''}
                {step.detail ? ` ${step.detail}` : ''}
                {step.error ? ` · ${step.error}` : ''}
                {Array.isArray(step.rows) && step.rows.length ? (
                  <ul style={{ listStyle: 'none', margin: '4px 0 0', padding: 0 }}>
                    {step.rows.map((row) => (
                      <li key={row.key} className="flex flex-wrap items-center gap-2" style={{ padding: '2px 0' }}>
                        <span className={`admin-status-pill ${row.met ? 'is-ok' : 'is-warning'}`}>{row.met ? d.tpDone : d.tpRunning}</span>
                        <span className="text-text-primary">{row.label}</span>
                        <span>{row.text}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!actions ? <p className="text-xs text-text-secondary">{d.tpNoTiming}</p> : null}
      <div className="text-xs">
        <strong>{d.tpBlockers}:</strong>{' '}
        {failed.length || autopilot?.blocked_code
          ? [autopilot?.blocked_code ? (pickBilingual(autopilot.blocked_detail, isDe) || `${autopilot.blocked_code}`) : '', ...failed.map((step) => `${step.label}: ${step.error || d.tpFailed}`)]
            .filter(Boolean).join(' · ')
          : d.tpNone}
      </div>
    </AdminSection>
  );
};

// --- block storage: space, branch sizes and the full download (D-SAAS-302, D-SAAS-303, D-HP-ADMIN-093) ---------------

// The honest sentence of a feature-detected endpoint: not rolled out (404), its error, or loading.
const FeatureStateNote = ({ state, d }) => {
  if (state?.status === 'missing') return <p className="text-sm text-text-secondary">{d.notRolled}</p>;
  if (state?.status === 'error') return <p className="text-sm text-error">{state.error}</p>;
  return <p className="text-sm text-text-secondary">{d.loading}</p>;
};

const formatShare = (part, whole, lang) => {
  const a = Number(part);
  const b = Number(whole);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= 0) return '-';
  try {
    return new Intl.NumberFormat(lang || 'en', { style: 'percent', maximumFractionDigits: 1 }).format(a / b);
  } catch {
    return `${((a / b) * 100).toFixed(1)} %`;
  }
};
const hasValue = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));

// Used, limit and free space of the block storage account and the lake's share of it (D-SAAS-302 §1). An account
// without a fixed limit says so and shows no free space.
const BlockStorageUsage = ({ d, usage, lang }) => (
  <AdminSection id="datalake-blockstorage-usage" title={d.bsUsageTitle} subtitle={d.bsUsageSubtitle}>
    {usage.status !== 'ok' || !usage.data ? <FeatureStateNote state={usage} d={d} /> : (() => {
      const data = usage.data;
      const measured = data.measured_at
        ? `${formatBerlin(data.measured_at)}${formatAgo(data.measured_at, lang) ? ` (${formatAgo(data.measured_at, lang)})` : ''}` : '-';
      if (data.connected === false) {
        return (
          <div className="space-y-2">
            <p className="text-sm text-error">{d.bsNotConnected}</p>
            <p className="text-xs text-text-secondary">{d.bsMeasuredAt}: {measured}</p>
          </div>
        );
      }
      const used = hasValue(data.account_used_bytes) ? Number(data.account_used_bytes) : null;
      const limit = hasValue(data.account_limit_bytes) && Number(data.account_limit_bytes) > 0 ? Number(data.account_limit_bytes) : null;
      const lake = hasValue(data.lake_bytes) ? Number(data.lake_bytes) : null;
      const free = limit !== null && used !== null ? Math.max(0, limit - used) : null;
      const base = limit !== null ? limit : used;
      const pct = (value) => `${Math.max(0, Math.min(100, (Number(value || 0) / base) * 100))}%`;
      const lakeInBar = lake !== null && used !== null ? Math.min(lake, used) : 0;
      return (
        <div className="space-y-3">
          <FactGrid items={[
            [d.bsUsed, used !== null ? formatBytes(used) : '-'],
            [d.bsLimit, limit !== null ? formatBytes(limit) : d.bsNoLimit],
            ...(limit !== null ? [[d.bsFree, free !== null ? formatBytes(free) : '-']] : []),
            [d.bsTrash, hasValue(data.trash_bytes) ? formatBytes(data.trash_bytes) : '-'],
            [d.bsLake, lake !== null ? fill(d.bsLakeLine, {
              size: formatBytes(lake), files: formatInteger(data.lake_files), share: used !== null ? formatShare(lake, used, lang) : '-',
            }) : '-'],
            [d.bsMeasuredAt, measured],
          ]} />
          {base > 0 ? (
            <div className="space-y-1">
              <div style={{ display: 'flex', height: '0.75rem', background: 'var(--color-border, #e5e5e5)', borderRadius: '0.125rem', overflow: 'hidden' }}>
                <div title={d.bsBarLake} style={{ width: pct(lakeInBar), background: 'var(--success)' }} />
                <div title={d.bsBarOther} style={{ width: pct((used || 0) - lakeInBar), background: 'var(--text-primary)' }} />
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-text-secondary">
                {[[d.bsBarLake, 'var(--success)'], [d.bsBarOther, 'var(--text-primary)'],
                  ...(limit !== null ? [[d.bsFree, 'var(--color-border, #e5e5e5)']] : [])].map(([label, color]) => (
                  <span key={label} className="flex items-center gap-1">
                    <span style={{ display: 'inline-block', width: '0.75rem', height: '0.75rem', background: color, borderRadius: '0.125rem' }} />
                    {label}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      );
    })()}
  </AdminSection>
);

// The tree of the block storage: the owner's folders (always shown, in their order), the measured branches below
// them (expandable, at every depth) and the training runs' checkpoints. Size and file count come from the measured
// branch sizes (D-SAAS-302 §2); without them the top folders carry the catalog's shard count and bytes.
const TREE_CHILD_PAGE = 100;
const normalizeTreePath = (value) => String(value ?? '').split('/').filter((part) => part && part !== '.').join('/');
const treeParent = (path) => (path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '');
const treeName = (path) => path.slice(path.lastIndexOf('/') + 1);

const buildStorageTree = (folders, sizeNodes, checkpointRuns, d) => {
  const nodes = new Map();
  // A node and every ancestor exist; a pinned node pins its ancestors, so it is never hidden in a closed branch.
  const ensure = (path, pinned) => {
    const parts = path.split('/');
    let node = null;
    for (let index = 1; index <= parts.length; index += 1) {
      const key = parts.slice(0, index).join('/');
      node = nodes.get(key);
      if (!node) {
        node = { path: key, pinned: false, extras: [] };
        nodes.set(key, node);
      }
      if (pinned) node.pinned = true;
    }
    return node;
  };
  (folders || []).forEach((folder, index) => {
    const path = normalizeTreePath(folder?.path);
    if (!path) return;
    const node = ensure(path, true);
    node.spec = folder;
    node.order = index;
  });
  (sizeNodes || []).forEach((entry) => {
    const path = normalizeTreePath(entry?.path);
    if (path) ensure(path, false).size = entry;
  });
  // D-SAAS-162 §6: the runs, their block-storage copies and the checkpoints on the server's disk.
  (checkpointRuns || []).forEach((runEntry) => {
    if (!runEntry?.run_id) return;
    const runPath = `checkpoints/run=${runEntry.run_id}`;
    const block = runEntry.block_storage || {};
    const runNode = ensure(runPath, true);
    runNode.note = { purpose: `${d.treeCkRun}${runEntry.tdv_id ? ` · ${runEntry.tdv_id}` : ''}`, createdBy: 'trainer' };
    (block.copies || []).forEach((copy) => {
      const path = normalizeTreePath(copy.remote_dir) || `${runPath}/step-${copy.step}`;
      ensure(path, true).note = {
        purpose: fill(d.treeCkCopy, { time: formatBerlin(copy.copied_at) }), createdBy: 'engine', files: 5, bytes: copy.bytes,
      };
    });
    (block.uploading_steps || []).forEach((step) => {
      const node = ensure(`${runPath}/step-${step}`, true);
      if (!node.note) node.note = { purpose: d.treeCkUploading, createdBy: 'engine' };
    });
    runNode.extras = (runEntry.local || []).filter((entry) => entry.complete).map((entry) => ({
      key: `local-${entry.step ?? entry.name}`, name: entry.name, files: entry.final ? 1 : 2, bytes: entry.size_bytes,
    }));
  });
  const children = new Map();
  nodes.forEach((node) => {
    const parent = treeParent(node.path);
    if (!children.has(parent)) children.set(parent, []);
    children.get(parent).push(node);
  });
  const order = (a, b) => {
    const aSpec = a.order !== undefined;
    const bSpec = b.order !== undefined;
    if (aSpec && bSpec) return a.order - b.order;
    if (aSpec !== bSpec) return aSpec ? -1 : 1;
    return a.path.localeCompare(b.path, undefined, { numeric: true });
  };
  children.forEach((list) => list.sort(order));
  return children;
};

const StorageTree = ({ d, folders, sizes, treeStatsByFolder, checkpointRuns, busy, onRetire, lang }) => {
  const [expanded, setExpanded] = useState(() => new Set());
  const [limits, setLimits] = useState({});
  const sizeNodes = sizes && Array.isArray(sizes.nodes) ? sizes.nodes : null;
  const measured = Boolean(sizeNodes);
  const children = useMemo(() => buildStorageTree(folders, sizeNodes || [], checkpointRuns, d),
    [folders, sizeNodes, checkpointRuns, d]);
  const toggle = (path) => setExpanded((previous) => {
    const next = new Set(previous);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    return next;
  });
  const sourceLabel = (source) => ({ catalog: d.treeSourceCatalog, walk: d.treeSourceWalk }[source] || source || undefined);
  const rows = [];
  const visit = (node, depth) => {
    const kids = children.get(node.path) || [];
    const hidden = kids.filter((kid) => !kid.pinned);
    const open = expanded.has(node.path);
    const stats = !measured && depth === 0 ? treeStatsByFolder[node.path] : null;
    const count = measured
      ? (node.size ? formatInteger(node.size.files) : (node.note?.files ?? '-'))
      : (stats ? formatInteger(stats.shards) : (node.note?.files ?? '-'));
    const bytes = measured && node.size ? node.size.bytes : (stats ? stats.bytes : node.note?.bytes);
    rows.push(
      <tr key={node.path}>
        <td className="font-mono text-xs" title={`${node.path}/`}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', paddingLeft: `${depth * 1.25}rem` }}>
            {hidden.length ? (
              <button type="button" aria-expanded={open} onClick={() => toggle(node.path)}
                title={open ? d.treeCollapse : fill(d.treeExpand, { count: formatInteger(hidden.length) })}
                aria-label={open ? d.treeCollapse : fill(d.treeExpand, { count: formatInteger(hidden.length) })}
                style={{ width: '1rem', padding: 0, border: 0, background: 'none', color: 'inherit', cursor: 'pointer' }}>
                {open ? '▾' : '▸'}
              </button>
            ) : <span aria-hidden="true" style={{ display: 'inline-block', width: '1rem' }} />}
            <span>{depth ? treeName(node.path) : node.path}/</span>
          </span>
        </td>
        <td className="text-xs">{node.spec?.purpose || node.note?.purpose || ''}</td>
        <td className="text-xs">{node.spec?.created_by || node.note?.createdBy || ''}</td>
        <td>{count}</td>
        <td title={measured && node.size ? sourceLabel(node.size.source) : undefined}>
          {bytes === null || bytes === undefined || bytes === '' ? '-' : formatBytes(bytes)}
        </td>
        <td>
          {node.spec ? (
            <button type="button" className="homepage-button-secondary" disabled={busy} onClick={() => onRetire(node.spec.path)}>
              {d.treeRetire}
            </button>
          ) : null}
        </td>
      </tr>,
    );
    const limit = limits[node.path] || TREE_CHILD_PAGE;
    let shownHidden = 0;
    kids.forEach((kid) => {
      if (!kid.pinned) {
        if (!open || shownHidden >= limit) return;
        shownHidden += 1;
      }
      visit(kid, depth + 1);
    });
    if (open && hidden.length > limit) {
      rows.push(
        <tr key={`${node.path}/:more`}>
          <td colSpan="6">
            <span style={{ paddingLeft: `${(depth + 1) * 1.25 + 1.25}rem` }}>
              <button type="button" className="homepage-button-secondary"
                onClick={() => setLimits((previous) => ({ ...previous, [node.path]: limit + TREE_CHILD_PAGE }))}>
                {fill(d.treeShowMore, { count: formatInteger(hidden.length - limit) })}
              </button>
            </span>
          </td>
        </tr>,
      );
    }
    node.extras.forEach((extra) => {
      rows.push(
        <tr key={`${node.path}/:${extra.key}`}>
          <td className="font-mono text-xs text-text-secondary">
            <span style={{ display: 'inline-block', paddingLeft: `${(depth + 1) * 1.25 + 1.25}rem` }}>{extra.name}</span>
          </td>
          <td className="text-xs text-text-secondary">{d.treeCkLocal}</td>
          <td className="text-xs">trainer</td><td>{extra.files}</td><td>{formatBytes(extra.bytes)}</td><td />
        </tr>,
      );
    });
  };
  (children.get('') || []).forEach((node) => visit(node, 0));
  const measuredAt = measured
    ? `${formatBerlin(sizes.measured_at)}${formatAgo(sizes.measured_at, lang) ? ` (${formatAgo(sizes.measured_at, lang)})` : ''}` : '';
  return (
    <>
      {measured ? (
        <p className="text-xs text-text-secondary">
          {sizes.walked_at
            ? fill(d.treeSizesMeasured, { measured: measuredAt, walked: formatBerlin(sizes.walked_at) })
            : fill(d.treeSizesNoWalk, { measured: measuredAt })}
        </p>
      ) : <p className="text-xs text-text-secondary">{d.treeShardsHint}</p>}
      <div className="admin-table-shell"><div className="admin-table-scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{d.treePath}</th><th>{d.treePurpose}</th><th>{d.treeCreatedBy}</th>
              <th>{measured ? d.treeFiles : d.treeShards}</th><th>{d.treeBytes}</th><th>{d.treeActions}</th>
            </tr>
          </thead>
          <tbody>
            {rows}
            {!rows.length ? <tr><td colSpan="6" className="text-text-secondary">{d.treeEmpty}</td></tr> : null}
          </tbody>
        </table>
      </div></div>
    </>
  );
};

// The full download of the block storage as its folder tree (D-SAAS-303). A Chromium-family browser writes the
// files straight into a folder the owner picks once; every other browser receives the download script. Nothing
// starts without the owner's click.
const EXPORT_API = `${API}/blockstorage/export`;
const EXPORT_PARALLEL = 2;
const EXPORT_HEARTBEAT_MS = 60000;
const EXPORT_BUILD_POLL_MS = 5000;
// One hour of waiting for the manifest; afterwards the owner continues the wait by hand.
const EXPORT_BUILD_POLL_MAX = 720;
const EXPORT_MANIFEST_PAGE = 1000;
const EXPORT_FILE_ATTEMPTS = 3;
// The download script per system: a batch file for Windows (curl.exe), a POSIX shell script for the others.
const EXPORT_SCRIPT_FILES = { windows: 'block-storage-download.cmd', posix: 'block-storage-download.sh' };
const EXPORT_TICK_MS = 1000;
const EXPORT_RATE_WINDOW_MS = 15000;

const canWriteFolders = () => typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
const detectWindows = () => {
  if (typeof navigator === 'undefined') return false;
  return /win/i.test(navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '');
};
// A backslash is a separator to the browser's file system access, and Windows forbids <>:"|?* and control characters
// in a name as well as a trailing dot or space: such a character is written as %XX (`sig=t->t` becomes `sig=t-%3Et`).
const hexEscape = (character) => `%${character.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`;
const exportSegments = (path, windows) => {
  const parts = String(path || '').split('/').filter((part) => part && part !== '.');
  if (!parts.length || parts.includes('..')) {
    const error = new Error('BAD_PATH');
    error.code = 'BAD_PATH';
    error.permanent = true;
    throw error;
  }
  return parts.map((part) => {
    const escaped = part.replace(windows ? /[\\<>:"|?*\u0000-\u001f]/g : /\\/g, hexEscape);
    return windows ? escaped.replace(/[. ]$/, hexEscape) : escaped;
  });
};
const exportFileError = (code, permanent = false) => {
  const error = new Error(code);
  error.code = code;
  error.permanent = permanent;
  return error;
};
const waitFor = (ms, signal) => new Promise((resolve) => {
  const timer = setTimeout(resolve, ms);
  signal?.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
});
const newExportSession = (exportId, dir) => ({
  exportId, dir, folderName: dir?.name || '', dirs: new Map(), manifest: null, manifestLoaded: 0, manifestTotal: 0,
  done: new Set(), counted: new Map(), bytesDone: 0, bytesTotal: 0, transferred: 0, samples: [], current: new Map(),
  failed: new Map(), skipped: 0, controller: new AbortController(), cancelled: false, queue: null,
});
// Bytes of a file present in the target folder; the total follows every change, a restarted file included.
const countBytes = (session, path, value) => {
  session.bytesDone += value - (session.counted.get(path) || 0);
  session.counted.set(path, value);
};

const BlockStorageDownload = ({ d, isDe, lang }) => {
  const folderMode = canWriteFolders();
  const windows = detectWindows();
  const [availability, setAvailability] = useState({ status: 'pending', error: '' });
  const [server, setServer] = useState(null);
  // idle | building | running | paused | incomplete | script | finished | stopped | ended
  const [phase, setPhase] = useState('idle');
  const [view, setView] = useState(null);
  const [script, setScript] = useState(null);
  const [message, setMessage] = useState({ text: '', error: false });
  const [busy, setBusy] = useState(false);
  // While true this page tells the server every minute that the export's client is alive.
  const [alive, setAlive] = useState(false);
  const run = useRef(null);

  const readServer = useCallback(async () => {
    const value = await requestJson(EXPORT_API);
    setServer(value);
    return value;
  }, []);
  const post = (body) => requestJson(EXPORT_API, { method: 'POST', body: JSON.stringify(body) });

  useEffect(() => {
    readServer()
      .then(() => setAvailability({ status: 'ok', error: '' }))
      .catch((error) => setAvailability(error?.status === 404
        ? { status: 'missing', error: '' } : { status: 'error', error: errorText(error, isDe) }));
  }, [readServer, isDe]);

  const snapshot = useCallback(() => {
    const session = run.current;
    if (!session) {
      setView(null);
      return;
    }
    const now = Date.now();
    session.samples.push([now, session.transferred]);
    while (session.samples.length > 2 && now - session.samples[0][0] > EXPORT_RATE_WINDOW_MS) session.samples.shift();
    const [since, bytesThen] = session.samples[0];
    setView({
      folder: session.folderName,
      filesDone: session.done.size,
      filesTotal: session.manifest ? session.manifest.length : 0,
      bytesDone: session.bytesDone,
      bytesTotal: session.bytesTotal,
      rate: now > since ? ((session.transferred - bytesThen) * 1000) / (now - since) : 0,
      current: [...session.current.entries()].map(([path, item]) => ({ path, ...item })),
      skipped: session.skipped,
      failed: session.failed.size,
      manifestLoaded: session.manifestLoaded,
      manifestTotal: session.manifestTotal,
      hasManifest: Boolean(session.manifest),
    });
  }, []);

  const endedOnServer = useCallback((session) => {
    if (session) {
      session.cancelled = true;
      session.controller.abort();
    }
    setAlive(false);
    setPhase('ended');
    setMessage({ text: d.bsDlEnded, error: true });
  }, [d.bsDlEnded]);

  // Heartbeat: one call a minute, then the export's state (protection mode, deferred work) is read again.
  useEffect(() => {
    if (!alive) return undefined;
    const timer = setInterval(async () => {
      const session = run.current;
      if (!session?.exportId) return;
      try {
        await post({ action: 'heartbeat', export_id: session.exportId });
        const current = await readServer();
        if (current && (current.export_id !== session.exportId || current.state === 'idle')) endedOnServer(session);
      } catch (error) {
        if ([404, 409, 410].includes(error?.status)) endedOnServer(session);
        else setMessage({ text: errorText(error, isDe), error: true });
      }
    }, EXPORT_HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [alive, readServer, endedOnServer, isDe]);

  useEffect(() => {
    if (phase !== 'running') return undefined;
    const timer = setInterval(snapshot, EXPORT_TICK_MS);
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => { clearInterval(timer); window.removeEventListener('beforeunload', warn); };
  }, [phase, snapshot]);

  // Leaving the page ends this page's download; the partial files stay and the next run continues them.
  useEffect(() => () => {
    const session = run.current;
    if (session) {
      session.cancelled = true;
      session.controller.abort();
    }
  }, []);

  const ensureDir = async (session, segments) => {
    let handle = session.dir;
    let key = '';
    for (const segment of segments) {
      key = key ? `${key}/${segment}` : segment;
      let pending = session.dirs.get(key);
      if (!pending) {
        pending = handle.getDirectoryHandle(segment, { create: true });
        session.dirs.set(key, pending);
      }
      try {
        handle = await pending;
      } catch (error) {
        session.dirs.delete(key);
        throw error;
      }
    }
    return handle;
  };

  // One file: skipped when it is already there with the manifest size, continued with a byte range when it is
  // shorter, written anew otherwise; its size is verified at the end.
  const downloadFile = async (session, entry, signal) => {
    const segments = exportSegments(entry.path, windows);
    const name = segments.pop();
    const folder = await ensureDir(session, segments);
    const expected = Number(entry.size || 0);
    let handle = null;
    let existing = 0;
    try {
      handle = await folder.getFileHandle(name);
      existing = (await handle.getFile()).size;
    } catch (error) {
      if (error?.name !== 'NotFoundError') throw error;
    }
    if (handle && existing === expected) {
      countBytes(session, entry.path, expected);
      return 'skipped';
    }
    if (!handle) handle = await folder.getFileHandle(name, { create: true });
    if (existing > expected) existing = 0;
    const url = `${EXPORT_API}/file?export_id=${encodeURIComponent(session.exportId)}&path=${encodeURIComponent(entry.path)}`;
    const open = (from) => fetch(url, {
      credentials: 'include', redirect: 'manual', signal, headers: from > 0 ? { Range: `bytes=${from}-` } : {},
    });
    const writable = await handle.createWritable({ keepExistingData: existing > 0 });
    let closed = false;
    try {
      let position = existing;
      countBytes(session, entry.path, position);
      if (position > 0) await writable.seek(position);
      if (expected > position) {
        let response = await open(position);
        if (position > 0 && response.status !== 206) {
          // The range was not served: the file starts over, from this answer when it carries the whole file.
          position = 0;
          await writable.truncate(0);
          countBytes(session, entry.path, 0);
          if (response.status !== 200) {
            try { await response.body?.cancel(); } catch { /* nothing to release */ }
            response = await open(0);
          }
        } else if (position > 0) {
          const start = /bytes\s+(\d+)-/i.exec(response.headers.get('Content-Range') || '');
          if (start && Number(start[1]) !== position) {
            try { await response.body?.cancel(); } catch { /* nothing to release */ }
            position = 0;
            await writable.truncate(0);
            countBytes(session, entry.path, 0);
            response = await open(0);
          }
        }
        if (response.type === 'opaqueredirect' || response.status === 401 || response.status === 403) {
          throw exportFileError('SESSION_LOST', true);
        }
        if (!response.ok || !response.body) {
          throw exportFileError(`HTTP_${response.status}`, [400, 404, 409, 410].includes(response.status));
        }
        const reader = response.body.getReader();
        session.current.set(entry.path, { bytes: position, size: expected });
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          await writable.write(value);
          position += value.byteLength;
          session.transferred += value.byteLength;
          countBytes(session, entry.path, position);
          session.current.set(entry.path, { bytes: position, size: expected });
        }
      }
      await writable.close();
      closed = true;
    } finally {
      // Closing keeps what arrived, so the next attempt continues the file with a byte range.
      if (!closed) {
        try { await writable.close(); } catch { /* the browser dropped the partial write */ }
      }
    }
    const size = (await handle.getFile()).size;
    countBytes(session, entry.path, Math.min(size, expected));
    if (size !== expected) throw exportFileError('SIZE_MISMATCH');
    return 'loaded';
  };

  const runQueue = async (session) => {
    const controller = new AbortController();
    session.controller = controller;
    session.samples = [];
    session.failed = new Map();
    setPhase('running');
    const pending = session.manifest.filter((entry) => !session.done.has(entry.path));
    let next = 0;
    let fatal = null;
    const worker = async () => {
      while (!controller.signal.aborted) {
        const entry = pending[next];
        next += 1;
        if (!entry) return;
        for (let attempt = 1; !controller.signal.aborted; attempt += 1) {
          try {
            const outcome = await downloadFile(session, entry, controller.signal);
            session.done.add(entry.path);
            if (outcome === 'skipped') session.skipped += 1;
            break;
          } catch (error) {
            if (controller.signal.aborted || error?.name === 'AbortError') break;
            if (error?.code === 'SESSION_LOST' || error?.name === 'QuotaExceededError') {
              fatal = error;
              controller.abort();
              break;
            }
            if (error?.permanent || attempt >= EXPORT_FILE_ATTEMPTS) {
              session.failed.set(entry.path, error?.code || error?.name || 'ERROR');
              break;
            }
            await waitFor(2000 * attempt, controller.signal);
          }
        }
        session.current.delete(entry.path);
      }
    };
    session.queue = Promise.all(Array.from({ length: EXPORT_PARALLEL }, worker));
    await session.queue;
    session.queue = null;
    session.current.clear();
    snapshot();
    if (fatal) {
      setPhase('paused');
      setMessage({ text: fatal.code === 'SESSION_LOST' ? d.bsDlSession : d.bsDlDiskFull, error: true });
      return;
    }
    if (controller.signal.aborted || session.cancelled) return;
    if (session.failed.size) {
      setPhase('incomplete');
      return;
    }
    try {
      await post({ action: 'finish', export_id: session.exportId });
      setAlive(false);
      setPhase('finished');
      setMessage({ text: fill(d.bsDlFinished, { files: formatInteger(session.done.size), size: formatBytes(session.bytesTotal) }), error: false });
      await readServer();
    } catch (error) {
      setPhase('incomplete');
      setMessage({ text: errorText(error, isDe), error: true });
    }
  };

  // Waits while the server walks the block storage for the manifest; true once the export is active.
  const waitActive = async (session) => {
    for (let round = 0; round < EXPORT_BUILD_POLL_MAX; round += 1) {
      if (session.cancelled) return false;
      try {
        const current = await readServer();
        if (current?.export_id !== session.exportId || current?.state === 'idle') {
          endedOnServer(session);
          return false;
        }
        if (current.state === 'active') return true;
      } catch (error) {
        if (error?.status === 404) {
          endedOnServer(session);
          return false;
        }
      }
      await waitFor(EXPORT_BUILD_POLL_MS, session.controller.signal);
    }
    setPhase('paused');
    return false;
  };

  const loadManifest = async (session) => {
    const files = [];
    let total = null;
    while (total === null || files.length < total) {
      if (session.cancelled) return false;
      const page = await requestJson(`${EXPORT_API}/manifest?export_id=${encodeURIComponent(session.exportId)}`
        + `&offset=${files.length}&limit=${EXPORT_MANIFEST_PAGE}`);
      const rows = Array.isArray(page?.files) ? page.files : [];
      files.push(...rows);
      total = hasValue(page?.total) ? Number(page.total) : files.length;
      session.manifestLoaded = files.length;
      session.manifestTotal = total;
      snapshot();
      if (!rows.length) break;
    }
    session.manifest = files;
    session.bytesTotal = files.reduce((sum, entry) => sum + Number(entry.size || 0), 0);
    return true;
  };

  // From any point of a session to its next step: manifest, files, or the script.
  const proceed = async (session) => {
    // The workers of a paused round release their files first.
    if (session.queue) await session.queue;
    session.controller = new AbortController();
    setMessage({ text: '', error: false });
    try {
      if (!session.manifest) {
        setPhase('building');
        if (!(await waitActive(session))) return;
      }
      if (session.dir) {
        if (!session.manifest && !(await loadManifest(session))) return;
        await runQueue(session);
      } else {
        const result = await post({ action: 'script_key', export_id: session.exportId });
        setScript({ exportId: session.exportId, expiresAt: result?.expires_at || null });
        setPhase('script');
      }
    } catch (error) {
      if (session.cancelled) return;
      setPhase('paused');
      setMessage({ text: errorText(error, isDe), error: true });
    }
  };

  const begin = async () => {
    setMessage({ text: '', error: false });
    let dir = null;
    if (folderMode) {
      try {
        dir = await window.showDirectoryPicker({ id: 'block-storage-download', mode: 'readwrite' });
      } catch (error) {
        if (error?.name !== 'AbortError') setMessage({ text: d.bsDlPickFailed, error: true });
        return;
      }
    }
    setBusy(true);
    let session = null;
    try {
      let current = await readServer();
      if (!current?.export_id || !['active', 'building'].includes(current.state)) {
        const started = await post({ action: 'start' });
        current = started?.export_id ? started : await readServer();
      }
      if (!current?.export_id) throw exportFileError('EXPORT_NOT_STARTED');
      session = newExportSession(current.export_id, dir);
      run.current = session;
      setScript(null);
      setAlive(true);
      snapshot();
    } catch (error) {
      setMessage({ text: errorText(error, isDe), error: true });
      return;
    } finally {
      setBusy(false);
    }
    await proceed(session);
  };

  const pause = () => {
    const session = run.current;
    if (!session) return;
    session.controller.abort();
    session.samples = [];
    setPhase('paused');
    snapshot();
  };

  const end = async (action) => {
    const session = run.current;
    const exportId = session?.exportId || server?.export_id;
    if (!exportId) return;
    if (session) {
      session.cancelled = true;
      session.controller.abort();
    }
    setBusy(true);
    try {
      await post({ action, export_id: exportId });
      setAlive(false);
      setPhase(action === 'finish' ? 'finished' : 'stopped');
      setMessage({ text: action === 'finish' ? d.bsDlFinishedNote : d.bsDlStopped, error: false });
      await readServer();
    } catch (error) {
      setMessage({ text: errorText(error, isDe), error: true });
    } finally {
      setBusy(false);
    }
  };

  const proceedAgain = () => {
    const session = run.current;
    if (!session) return;
    session.cancelled = false;
    proceed(session);
  };

  if (availability.status !== 'ok') {
    return (
      <AdminSection id="datalake-blockstorage-download" title={d.bsDlTitle} subtitle={d.bsDlSubtitle}>
        <FeatureStateNote state={availability} d={d} />
      </AdminSection>
    );
  }

  const session = run.current;
  const serverOpen = Boolean(server?.export_id && ['active', 'building'].includes(server?.state));
  const sessionOpen = ['building', 'running', 'paused', 'incomplete', 'script'].includes(phase);
  const phaseLabels = {
    building: d.bsDlStateBuilding, running: d.bsDlStateRunning, paused: d.bsDlStatePaused, incomplete: d.bsDlStateIncomplete,
    script: d.bsDlStateScript, finished: d.bsDlStateFinished, stopped: d.bsDlStateStopped, ended: d.bsDlStateStopped,
  };
  const stateLabel = phaseLabels[phase] || (serverOpen ? (server.state === 'building' ? d.bsDlStateBuilding : d.bsDlStateActive) : '');
  const tone = { running: 'ok', finished: 'ok', script: 'ok', paused: 'warning', building: 'warning', incomplete: 'error', ended: 'error' }[phase]
    || (serverOpen ? 'warning' : '');
  const remaining = view ? Math.max(0, view.bytesTotal - view.bytesDone) : 0;
  const eta = view && phase === 'running' && view.rate > 0 ? formatSpan(remaining / view.rate) : d.bsDlEtaUnknown;
  const scriptUrl = (os) => `${EXPORT_API}/script?export_id=${encodeURIComponent(script?.exportId || '')}&os=${os}`;
  const scriptOrder = windows ? ['windows', 'posix'] : ['posix', 'windows'];
  const lastActivity = server?.last_activity_at
    ? `${formatBerlin(server.last_activity_at)}${formatAgo(server.last_activity_at, lang) ? ` (${formatAgo(server.last_activity_at, lang)})` : ''}` : '-';

  return (
    <AdminSection id="datalake-blockstorage-download" title={d.bsDlTitle} subtitle={d.bsDlSubtitle} state={stateLabel} tone={tone}
      actions={(
        <button type="button" className="homepage-button-primary" disabled={busy || sessionOpen} onClick={begin}>{d.bsDlButton}</button>
      )}>
      <div className="space-y-3 text-sm">
        <p className="text-xs text-text-secondary">{folderMode ? d.bsDlFolderHint : d.bsDlScriptHint}</p>
        {folderMode && windows ? <p className="text-xs text-text-secondary">{d.bsDlWindowsNames}</p> : null}
        {serverOpen && !sessionOpen ? (
          <div className="homepage-muted-panel p-3 space-y-2">
            <p>{fill(d.bsDlActiveNote, { time: formatBerlin(server.started_at) })}</p>
            <div className="flex flex-wrap gap-2">
              <button type="button" className="homepage-button-secondary" disabled={busy} onClick={() => end('finish')}>{d.bsDlFinish}</button>
              <button type="button" className="homepage-button-secondary" disabled={busy} onClick={() => end('stop')}>{d.bsDlStop}</button>
            </div>
          </div>
        ) : null}
        {message.text ? <p className={message.error ? 'text-error' : ''}>{message.text}</p> : null}

        {sessionOpen && session ? (
          <div className="homepage-muted-panel p-3 space-y-3">
            {phase === 'building' ? (
              <p>
                {view?.manifestTotal
                  ? fill(d.bsDlManifest, { done: formatInteger(view.manifestLoaded), total: formatInteger(view.manifestTotal) })
                  : fill(d.bsDlBuilding, { files: formatInteger(server?.files), size: formatBytes(server?.bytes) })}
              </p>
            ) : null}

            {session.dir && view?.hasManifest ? (
              <>
                <FactGrid items={[
                  [d.bsDlFolder, view.folder || '-'],
                  [d.bsDlFiles, fill(d.bsDlOf, { done: formatInteger(view.filesDone), total: formatInteger(view.filesTotal) })],
                  [d.bsDlData, fill(d.bsDlOf, { done: formatBytes(view.bytesDone), total: formatBytes(view.bytesTotal) })],
                  [d.bsDlRate, phase === 'running' && view.rate > 0 ? `${formatBytes(view.rate)}/s` : '-'],
                  [d.bsDlEta, eta],
                ]} />
                <div style={{ height: '0.5rem', background: 'var(--color-border, #e5e5e5)', borderRadius: '0.125rem', overflow: 'hidden' }}>
                  <div style={{
                    width: `${view.bytesTotal > 0 ? Math.min(100, (view.bytesDone / view.bytesTotal) * 100) : (view.filesTotal ? (view.filesDone / view.filesTotal) * 100 : 0)}%`,
                    height: '0.5rem', background: 'var(--success)',
                  }} />
                </div>
                {view.current.length ? (
                  <div className="space-y-1 text-xs">
                    <div className="font-bold">{d.bsDlCurrent}</div>
                    {view.current.map((item) => (
                      <div key={item.path} className="flex flex-wrap justify-between gap-2">
                        <span className="font-mono" style={{ overflowWrap: 'anywhere' }}>{item.path}</span>
                        <span className="font-mono text-text-secondary">{fill(d.bsDlOf, { done: formatBytes(item.bytes), total: formatBytes(item.size) })}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-3 text-xs text-text-secondary">
                  {view.skipped ? <span>{fill(d.bsDlSkipped, { count: formatInteger(view.skipped) })}</span> : null}
                  {view.failed ? <span className="text-error">{fill(d.bsDlFailed, { count: formatInteger(view.failed) })}</span> : null}
                </div>
                {phase === 'paused' ? <p className="text-xs text-text-secondary">{d.bsDlPausedNote}</p> : null}
              </>
            ) : null}

            {phase === 'script' && script ? (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {scriptOrder.map((os, index) => (
                    <a key={os} className={index === 0 ? 'homepage-button-primary' : 'homepage-button-secondary'} href={scriptUrl(os)} download={EXPORT_SCRIPT_FILES[os]}>
                      {os === 'windows' ? d.bsDlScriptWindows : d.bsDlScriptPosix}
                    </a>
                  ))}
                </div>
                <p className="text-xs">{d.bsDlScriptRun}</p>
                {script.expiresAt ? <p className="text-xs text-text-secondary">{fill(d.bsDlScriptKeyValid, { time: formatBerlin(script.expiresAt) })}</p> : null}
                <p className="text-xs text-text-secondary">{d.bsDlScriptHeartbeat}</p>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2">
              {phase === 'running' ? <button type="button" className="homepage-button-secondary" onClick={pause}>{d.bsDlPause}</button> : null}
              {phase === 'paused' ? <button type="button" className="homepage-button-primary" disabled={busy} onClick={proceedAgain}>{d.bsDlContinue}</button> : null}
              {phase === 'incomplete' ? <button type="button" className="homepage-button-primary" disabled={busy} onClick={proceedAgain}>{d.bsDlRetryFailed}</button> : null}
              {phase === 'incomplete' || phase === 'script'
                ? <button type="button" className="homepage-button-secondary" disabled={busy} onClick={() => end('finish')}>{d.bsDlFinish}</button> : null}
              <button type="button" className="homepage-button-secondary" disabled={busy} onClick={() => end('stop')}>{d.bsDlStop}</button>
            </div>
          </div>
        ) : null}

        {serverOpen || sessionOpen ? (
          <div className="space-y-1">
            <div className="font-bold text-sm">{d.bsDlProtection}</div>
            <FactGrid items={[
              [d.bsDlProtectionState, server?.state === 'active'
                ? fill(d.bsDlProtectionOn, { time: formatBerlin(server.started_at) })
                : (server?.state === 'building' ? d.bsDlStateBuilding : d.bsDlProtectionOff)],
              [d.bsDlScope, fill(d.bsDlScopeValue, { files: formatInteger(server?.files), size: formatBytes(server?.bytes) })],
              [d.bsDlDeferredDeletes, formatInteger(server?.deferred?.deletes)],
              [d.bsDlDeferredOverwrites, formatInteger(server?.deferred?.overwrites)],
              [d.bsDlAddedSince, formatInteger(server?.added_since_start)],
              [d.bsDlLastActivity, lastActivity],
            ]} />
          </div>
        ) : null}
      </div>
    </AdminSection>
  );
};

const TrainingDataFactoryPage = ({ section = 'autopilot' }) => {
  const { t, currentLang } = useTranslation();
  const isDe = currentLang === 'de';
  const d = t.admin?.datalake || {};
  const navLabels = t.admin?.nav?.sections?.training || {};

  const normalizedPropSection = VALID_SUB_PAGE_IDS.has(section) ? section : 'autopilot';
  const [activeSubPage, setActiveSubPage] = useState(normalizedPropSection);
  useEffect(() => {
    if (VALID_SUB_PAGE_IDS.has(section)) setActiveSubPage(section);
    else setActiveSubPage('autopilot');
  }, [section]);
  const navigateSubPage = useCallback((subId) => {
    const target = VALID_SUB_PAGE_IDS.has(subId) ? subId : 'autopilot';
    setActiveSubPage(target);
    writeAdminRoute('training', target);
  }, []);

  // Core (existing endpoints, always present).
  const [data, setData] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState({ message: '', error: false });

  // New endpoints, feature-detected: {status: 'ok'|'missing'|'error', data, error}.
  const [ext, setExt] = useState({});
  const setFeature = useCallback((key, patch) => {
    setExt((previous) => ({ ...previous, [key]: { ...(previous[key] || {}), ...patch } }));
  }, []);
  const loadFeature = useCallback(async (key, path, options) => {
    try {
      const value = await requestJson(path, options);
      setFeature(key, { status: 'ok', data: value, error: '' });
      return value;
    } catch (error) {
      if (error?.status === 404) setFeature(key, { status: 'missing', error: '' });
      else setFeature(key, { status: 'error', error: errorText(error, isDe) });
      return null;
    }
  }, [isDe, setFeature]);
  const feature = (key) => ext[key] || { status: 'pending' };
  // The honest per-section sentence when the backend does not carry a route yet,
  // or answered with an error (never invented data).
  const FeatureNote = ({ featureKey }) => {
    const state = feature(featureKey);
    if (state.status === 'missing') return <p className="text-sm text-text-secondary">{d.notRolled}</p>;
    if (state.status === 'error') return <p className="text-sm text-error">{state.error}</p>;
    return <p className="text-sm text-text-secondary">{d.loading}</p>;
  };

  // Event feed with bounded manual pagination.
  const [eventRows, setEventRows] = useState([]);
  const [eventsHaveMore, setEventsHaveMore] = useState(false);

  // Threshold editor rows, seeded once from the measured payload (D-SAAS-106 #2).
  const [thresholdRows, setThresholdRows] = useState(null);

  // Device flow (Google OAuth): user code + verification URL + a bounded poll at
  // Google's own interval; the dialog never hammers the endpoint.
  const [connectOpen, setConnectOpen] = useState(false);
  const [flow, setFlow] = useState(null);
  const [flowStatus, setFlowStatus] = useState('idle'); // idle | webwait | manual | waiting | authorized | failed | expired
  const [flowError, setFlowError] = useState('');
  const [codeValue, setCodeValue] = useState('');
  const pollRef = useRef(null);

  // Bootstrap report of the last run in this session.
  const [bootstrapReport, setBootstrapReport] = useState(null);

  // Tree editor form.
  const [treeForm, setTreeForm] = useState({ path: '', purpose: '' });
  // Per-source byte-target edits (GB in the input, bytes on the wire).
  const [acqTargets, setAcqTargets] = useState({});
  // Per-folder measured counts (invisible catalog plumbing, silent on failure).
  const [treeStats, setTreeStats] = useState(null);

  // Mixing-target editor rows (targets carry set_by/set_at in the engine).
  const [targetRows, setTargetRows] = useState([
    { capability: 'tool_calling', mod_sig: 't->t', tier_level: 'unmeasured', target_count: 0, weight: 1.0 },
  ]);
  const [targetsSeeded, setTargetsSeeded] = useState(false);

  // Compatibility report of the selected TDV (stored by the engine, never computed here).
  const [selectedTdv, setSelectedTdv] = useState('');
  const [tdvDetail, setTdvDetail] = useState(null);

  // Graph neighbors inspector.
  const [neighborNodeId, setNeighborNodeId] = useState('');
  const [neighborResult, setNeighborResult] = useState(null);

  // Benchmark report detail dialog.
  const [benchDetail, setBenchDetail] = useState(null);
  const [benchOpenId, setBenchOpenId] = useState('');

  const loadCore = useCallback(async () => {
    try {
      const overview = await requestJson(`${API}?lang=${isDe ? 'de' : 'en'}`);
      setData(overview);
    } catch (error) {
      setNotice({ message: `${d.loadFailed || '-'}: ${errorText(error, isDe)}`, error: true });
    } finally {
      setLoading(false);
    }
  }, [isDe, d.loadFailed]);

  const loadEvents = useCallback(async (before) => {
    const suffix = before ? `&before=${encodeURIComponent(before)}` : '';
    const value = await loadFeature('events', `${API}/autopilot/events?limit=${EVENTS_PAGE}${suffix}`);
    if (!value) return;
    const page = value.events || [];
    setEventRows((previous) => (before ? [...previous, ...page] : page));
    setEventsHaveMore(page.length === EVENTS_PAGE);
  }, [loadFeature]);

  const loadTreeStats = useCallback(async () => {
    try {
      const result = await requestJson(`${API}/query`, { method: 'POST', body: JSON.stringify({ sql: TREE_STATS_SQL }) });
      if (result?.status === 'ok') setTreeStats(result);
    } catch {
      setTreeStats(null); // measured counts stay absent, never invented
    }
  }, []);

  const loadChecklist = useCallback(async () => {
    try {
      const result = await requestJson(`${API}/training`);
      setChecklist(result.checklist || null);
    } catch {
      setChecklist(null);
    }
  }, []);

  const loadCompatReports = useCallback(async () => {
    try {
      const result = await requestJson(`${API}/compat/reports`);
      setReports(result.reports || []);
    } catch {
      setReports([]);
    }
  }, []);

  const loadSection = useCallback(async (subId) => {
    if (subId === 'autopilot') {
      await Promise.all([
        loadFeature('autopilot', `${API}/autopilot`),
        loadFeature('trainingLive', `${API}/training/live`),
        loadChecklist(),
        loadFeature('readiness', `${API}/readiness`),
        loadFeature('index', `${API}/index`),
        loadFeature('dedup', `${API}/dedup`),
        loadFeature('graphStages', `${API}/graph/stages`),
        loadEvents(),
      ]);
    } else if (subId === 'storage') {
      // The measured branch sizes (D-SAAS-302 §2) carry every depth; without them the catalog's per-folder count
      // stands in for the top folders.
      const loadSizes = async () => {
        if (!(await loadFeature('bsSizes', `${API}/blockstorage/sizes`))) await loadTreeStats();
      };
      await Promise.all([loadFeature('tree', `${API}/tree`), loadSizes(),
        loadFeature('bsUsage', `${API}/blockstorage/usage`),
        loadFeature('checkpoints', `${API}/checkpoints`)]);
    } else if (subId === 'acquisition') {
      await Promise.all([
        loadFeature('acquisition', `${API}/acquisition`),
        loadFeature('preprocessing', `${API}/preprocessing`),
      ]);
    } else if (subId === 'data') {
      await Promise.all([
        loadFeature('inbox', `${API}/inbox`),
        loadFeature('graph', `${API}/graph/summary`),
        loadCompatReports(),
        // D-HP-ADMIN-089: whether the selected strand's paper carries a data plan, and its mix
        loadFeature('readiness', `${API}/readiness`),
      ]);
    } else {
      await Promise.all([
        loadFeature('trainingLive', `${API}/training/live`),
        loadChecklist(),
        loadFeature('readiness', `${API}/readiness`),
        loadFeature('index', `${API}/index`),
        loadFeature('dedup', `${API}/dedup`),
        loadFeature('bench', `${API}/bench/reports`),
        loadFeature('hf', `${API}/hf/uploads`),
      ]);
    }
  }, [loadFeature, loadEvents, loadTreeStats, loadChecklist, loadCompatReports]);

  const refreshAll = useCallback(async () => {
    setThresholdRows(null); // re-seed from the fresh measured payload
    await Promise.all([loadCore(), loadSection(activeSubPage)]);
  }, [loadCore, loadSection, activeSubPage]);

  useEffect(() => { loadCore(); }, [loadCore]);
  // D-HP-ADMIN-081: the live training card refreshes itself while the training sub-page is open (every 30 s,
  // one cheap file read on the admin replica — never a tight loop).
  useEffect(() => {
    if (activeSubPage !== 'training' && activeSubPage !== 'autopilot') return undefined;
    const timer = setInterval(() => { loadFeature('trainingLive', `${API}/training/live`); }, 30000);
    return () => clearInterval(timer);
  }, [activeSubPage, loadFeature]);
  const loadedSectionsRef = useRef({});
  useEffect(() => {
    if (!loadedSectionsRef.current[activeSubPage]) {
      loadedSectionsRef.current[activeSubPage] = true;
      loadSection(activeSubPage);
    }
  }, [activeSubPage, loadSection]);
  // The full download stays mounted from the first visit of the storage section on, so a running download goes on
  // while another section of this page is open.
  const [storageVisited, setStorageVisited] = useState(section === 'storage');
  useEffect(() => {
    if (activeSubPage === 'storage') setStorageVisited(true);
  }, [activeSubPage]);

  // Seed editors once from measured state.
  const autopilot = feature('autopilot').data || null;
  useEffect(() => {
    if (thresholdRows === null && autopilot?.thresholds) {
      setThresholdRows(autopilot.thresholds.map((row) => ({
        capability: row.capability || '', mod_sig: row.mod_sig || 't->t',
        min_samples: Number(row.min_samples || 0), min_token_est: Number(row.min_token_est || 0),
        enabled: row.enabled !== false,
      })));
    }
  }, [autopilot, thresholdRows]);
  useEffect(() => {
    if (targetsSeeded) return;
    const rows = data?.gap_matrix?.rows || [];
    if (rows.length) {
      setTargetRows(rows.map((row) => ({
        capability: row.capability, mod_sig: row.mod_sig, tier_level: row.tier_level,
        target_count: Number(row.target_count || 0), weight: Number(row.weight || 1),
      })));
      setTargetsSeeded(true);
    }
  }, [data, targetsSeeded]);

  const runAction = useCallback(async (action, okText, reload) => {
    setBusy(true);
    setNotice({ message: '', error: false });
    try {
      const result = await action();
      if (okText) setNotice({ message: okText, error: false });
      await (reload ? reload() : refreshAll());
      return result;
    } catch (error) {
      setNotice({ message: errorText(error, isDe), error: true });
      return null;
    } finally {
      setBusy(false);
    }
  }, [isDe, refreshAll]);

  // --- connect flows (web window, manual code, device code) -------------------------
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);
  useEffect(() => () => stopPolling(), [stopPolling]);

  // The connect button opens a Google sign-in
  // window. With the platform's own web client the callback lands in the dashboard
  // domain and the dialog only waits for the measured connection; with the public
  // desktop identity Google redirects to an unreachable localhost page and the person
  // pastes the code from the address bar.
  // The sign-in window opens within the click itself and receives its address once the lake answers: a browser
  // lets a page open a window only right after the person's click, and the address can take seconds to arrive.
  const openSignInWindow = () => window.open('', 'gdriveauth', 'width=540,height=680');
  const showSignIn = (signInWindow, url) => {
    if (signInWindow && !signInWindow.closed) signInWindow.location.href = url;
    else window.open(url, 'gdriveauth', 'width=540,height=680');
  };

  const startWebFlow = async () => {
    setFlowError('');
    // One sign-in per click: every start replaces the previous one's state, so the button leaves at once.
    setFlowStatus('starting');
    const signInWindow = openSignInWindow();
    try {
      const started = await requestJson(`${API}/oauth/web/start`, { method: 'POST', body: '{}' });
      showSignIn(signInWindow, started.auth_url);
      setFlow({ started_at: Date.now() });
      setFlowStatus('webwait');
    } catch (error) {
      signInWindow?.close();
      setFlowStatus('failed');
      setFlowError(errorText(error, isDe));
    }
  };

  const startManualFlow = async () => {
    setFlowError('');
    const signInWindow = openSignInWindow();
    try {
      const started = await requestJson(`${API}/oauth/manual/start`, { method: 'POST', body: '{}' });
      showSignIn(signInWindow, started.auth_url);
      setFlowStatus('manual');
    } catch (error) {
      signInWindow?.close();
      setFlowStatus('failed');
      setFlowError(errorText(error, isDe));
    }
  };

  const submitManualCode = async () => {
    setFlowError('');
    try {
      const raw = codeValue.trim();
      // The person may paste the whole localhost URL — the code parameter is enough.
      const fromUrl = raw.match(/[?&]code=([^&\s]+)/);
      const code = decodeURIComponent(fromUrl ? fromUrl[1] : raw);
      const result = await requestJson(`${API}/oauth/code`, {
        method: 'POST', body: JSON.stringify({ code }),
      });
      if (result.status === 'authorized') {
        setFlowStatus('authorized');
        setCodeValue('');
        loadCore();
      }
    } catch (error) {
      setFlowError(errorText(error, isDe));
    }
  };

  useEffect(() => {
    if (flowStatus !== 'webwait') return undefined;
    const startedAt = flow?.started_at || Date.now();
    pollRef.current = setInterval(async () => {
      if (Date.now() - startedAt > 180000) { setFlowStatus('expired'); stopPolling(); return; }
      try {
        const current = await requestJson(`${API}/overview`);
        if (current?.connection?.connected) {
          setFlowStatus('authorized');
          stopPolling();
          loadCore();
        }
      } catch (error) { /* keep waiting inside the bounded window */ }
    }, 4000);
    return () => stopPolling();
  }, [flowStatus, flow, loadCore, stopPolling]);

  const startDeviceFlow = async () => {
    setFlowError('');
    setFlowStatus('idle');
    setFlow(null);
    try {
      const started = await requestJson(`${API}/oauth/device/start`, { method: 'POST', body: '{}' });
      setFlow({ ...started, started_at: Date.now() });
      setFlowStatus('waiting');
    } catch (error) {
      setFlowStatus('failed');
      setFlowError(errorText(error, isDe));
    }
  };

  useEffect(() => {
    if (flowStatus !== 'waiting' || !flow?.device_code) { stopPolling(); return undefined; }
    const intervalMs = Math.max(5, Number(flow.interval || 5)) * 1000;
    const expiresMs = Math.max(60, Number(flow.expires_in || 1800)) * 1000;
    pollRef.current = setInterval(async () => {
      if (Date.now() - flow.started_at > expiresMs) {
        setFlowStatus('expired');
        stopPolling();
        return;
      }
      try {
        const poll = await requestJson(`${API}/oauth/device/poll`, {
          method: 'POST', body: JSON.stringify({ device_code: flow.device_code }),
        });
        if (poll.status === 'authorized') {
          setFlowStatus('authorized');
          stopPolling();
          loadCore();
        }
      } catch (error) {
        setFlowStatus('failed');
        setFlowError(errorText(error, isDe));
        stopPolling();
      }
    }, intervalMs);
    return () => stopPolling();
  }, [flowStatus, flow, isDe, loadCore, stopPolling]);

  const closeConnect = () => {
    stopPolling(); setConnectOpen(false); setFlow(null); setFlowStatus('idle');
    setFlowError(''); setCodeValue('');
  };

  // --- inbox upload ----------------------------------------------------------------
  const uploadAsset = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setNotice({ message: d.uploadTooLarge, error: true });
      return;
    }
    // fetch headers must stay ISO-8859-1: a non-ASCII filename travels URL-encoded.
    const headerName = /^[ -~]+$/.test(file.name) ? file.name : encodeURIComponent(file.name);
    await runAction(() => requestJson(`${API}/inbox/upload`, {
      method: 'POST',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-Filename': headerName },
    }), d.uploadDone, () => loadFeature('inbox', `${API}/inbox`));
  };

  // --- derived, all backend-measured -----------------------------------------------
  const connection = data?.connection || {};
  const gapMatrix = data?.gap_matrix || {};
  const tdvs = data?.tdvs || [];
  const runs = data?.runs || [];
  const exportsList = data?.exports || [];
  const treeSpec = data?.tree_spec || {};
  const phase = data?.training_phase || 'A';
  const honestyNote = pick(data, 'honesty_note', isDe);
  const inbox = feature('inbox').data || null;
  const graph = feature('graph').data || null;
  const acquisition = feature('acquisition').data || null;
  const preprocessing = feature('preprocessing').data || null;
  const tree = feature('tree').data || null;
  const benchData = feature('bench').data;
  const benchList = Array.isArray(benchData) ? benchData : (benchData?.reports || []);
  const hfData = feature('hf').data;
  const hfUploads = Array.isArray(hfData) ? hfData : (hfData?.uploads || []);
  const treeStatsByFolder = (() => {
    const map = {};
    if (treeStats?.columns && treeStats?.rows) {
      const topIndex = treeStats.columns.indexOf('top_folder');
      const shardsIndex = treeStats.columns.indexOf('shards');
      const bytesIndex = treeStats.columns.indexOf('bytes');
      treeStats.rows.forEach((row) => { map[row[topIndex]] = { shards: row[shardsIndex], bytes: row[bytesIndex] }; });
    }
    return map;
  })();
  const checkpointsData = feature('checkpoints').data;
  const checkpointRuns = useMemo(() => (checkpointsData?.runs || []), [checkpointsData]);
  const treeFolders = tree?.active?.folders;
  const storageFolders = useMemo(() => treeFolders || [], [treeFolders]);

  const loadTdvDetail = async (tdvId) => {
    setSelectedTdv(tdvId);
    setTdvDetail(null);
    try {
      setTdvDetail(await requestJson(`${API}/tdv/${tdvId}`));
    } catch (error) {
      setTdvDetail({ error: errorText(error, isDe) });
    }
  };

  const openBenchReport = async (runId) => {
    setBenchOpenId(runId);
    setBenchDetail(null);
    try {
      setBenchDetail(await requestJson(`${API}/bench/reports/${encodeURIComponent(runId)}`));
    } catch (error) {
      setBenchDetail({ error: errorText(error, isDe) });
    }
  };

  // Every listed state and file explains itself to a lay reader (D-HP-ADMIN-074): composite
  // states (raw_stored:awaiting_stt, unsupported:…) resolve head and detail against the label
  // map, and each inbox row says in words what the file is and where it came from — the raw
  // technical name stays as secondary detail.
  const assetStateLabel = (state) => {
    const known = d.assetStates?.[state];
    if (known) return known;
    const [head, detail] = String(state || '').split(':');
    const headLabel = d.assetStates?.[head] || head;
    if (!detail) return headLabel;
    const detailLabel = d.assetStates?.[detail] || detail.replaceAll('_', ' ');
    return `${headLabel} (${detailLabel})`;
  };
  const acqNameById = Object.fromEntries(((acquisition?.sources) || [])
    .map((source) => [source.source_id, source.name || source.source_id]));
  const mediaWord = (mediaType) => {
    const type = String(mediaType || '');
    if (type.includes('parquet')) return d.mediaParquet;
    const head = type.split('/')[0];
    return d.mediaWords?.[head] || type || '—';
  };
  const describeAsset = (asset) => {
    const source = String(asset?.source || '');
    const origin = source.startsWith('acquisition:')
      ? fill(d.assetFromSource, { source: acqNameById[source.slice('acquisition:'.length)] || source.slice('acquisition:'.length) })
      : d.assetFromUpload;
    return `${mediaWord(asset?.media_type)} — ${origin}`;
  };
  const compatReport = tdvDetail?.compat_report || null;
  const unmetItems = (checklist?.items || []).filter((item) => !item.satisfied);

  // Readiness to train (D-HP-ADMIN-067): everything below is the engine's measured
  // answer — summary, ordered stages, green/total — never computed here.
  const readinessFeature = feature('readiness');
  const readiness = readinessFeature.data || null;
  const readinessStages = Array.isArray(readiness?.stages) ? readiness.stages : [];
  const readinessGateStages = readinessStages.filter((stage) => TRAINING_GATE_STAGE_IDS.has(stage.id));
  const readinessOpenStages = readinessStages.filter((stage) => String(stage.state || '').toLowerCase() !== 'ok');
  const readinessBlocked = readinessStages.some((stage) => String(stage.state || '').toLowerCase() === 'blocked');
  const readinessCounterText = fill(d.readinessCounter, { green: formatInteger(readiness?.green), total: formatInteger(readiness?.total) });
  const stageStateLabel = (state) => d.stageStates?.[String(state || '').toLowerCase()] || String(state || '');
  // One readiness stage in plain language: the localized state word, the engine's
  // bilingual label and measured sentence, the unblock sentence highlighted as the
  // next action. The stage id survives only in the row's tooltip.
  const renderReadinessStage = (stage) => (
    <div key={stage.id} className="homepage-muted-panel p-3 text-sm space-y-1" title={stage.id}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`admin-readiness-stage ${stageStateClass(stage.state)}`}>{stageStateLabel(stage.state)}</span>
        <span className="font-bold">{pickBilingual(stage.label, isDe)}</span>
      </div>
      {pickBilingual(stage.measured, isDe) ? <p className="text-text-secondary">{pickBilingual(stage.measured, isDe)}</p> : null}
      {stage.unblock ? (
        <p className="admin-readiness-unblock"><strong>{d.unblockLabel}:</strong> {pickBilingual(stage.unblock, isDe)}</p>
      ) : null}
    </div>
  );
  const apEnabled = Boolean(autopilot?.enabled);
  const apPaused = Boolean(autopilot?.paused);
  const apState = feature('autopilot');
  const cycleProgress = autopilot?.cycle_progress || {};
  const cyclePhases = Object.keys(cycleProgress);
  const measuredThresholds = autopilot?.thresholds || [];
  // D-HP-ADMIN-088: where the selected strand's paper carries a data plan, the plan sets the targets — the threshold box
  // then only lists what each of the strand's capabilities holds, without targets and without an editor.
  const planGoverns = Boolean(readinessStages.find((stage) => stage.id === 'data_plan')?.plan);
  const planMix = readinessStages.find((stage) => stage.id === 'data_plan')?.plan?.mix || {};
  const planCapabilities = measuredThresholds.filter((row) => row.applies_to_strand !== false && row.enabled !== false);
  const budgets = autopilot?.budgets || [];
  const unprocessedCount = Number(inbox?.unprocessed_count || 0);

  const autopilotToggle = () => runAction(() => requestJson(`${API}/autopilot`, {
    method: 'POST', body: JSON.stringify({ action: apEnabled ? 'disable' : 'enable' }),
  }), apEnabled ? d.apDisabled : d.apEnabled, () => loadFeature('autopilot', `${API}/autopilot`));
  // D-HP-ADMIN-094 / D-SAAS-307: the switch that lets the path go on after the seal (off by default)
  const afterSealOn = Boolean(autopilot?.continue_after_seal);
  // the review pause after the seal is the owner's own stop (D-SAAS-307), never shown as a fault
  const apBlocked = Boolean(autopilot?.blocked_code) && autopilot.blocked_code !== 'PAUSED_AFTER_SEAL';
  const afterSealToggle = () => runAction(() => requestJson(`${API}/autopilot`, {
    method: 'POST', body: JSON.stringify({ action: afterSealOn ? 'continue_after_seal_off' : 'continue_after_seal_on' }),
  }), afterSealOn ? d.apAfterSealTurnedOff : d.apAfterSealTurnedOn, () => loadFeature('autopilot', `${API}/autopilot`));
  const autopilotPauseResume = () => runAction(() => requestJson(`${API}/autopilot`, {
    method: 'POST', body: JSON.stringify({ action: apPaused ? 'resume' : 'pause' }),
  }), apPaused ? d.apResumed : d.apPausedNote, () => loadFeature('autopilot', `${API}/autopilot`));

  if (loading && !data) return <div className="site-card admin-panel p-6">{d.loading}</div>;

  return (
    <div className="space-y-6">
      {/* Control strip: sub-page navigation, refresh, the engine's own honesty note.
          The page title is AdminPageHead's alone (one header, D-HP-ADMIN-063). */}
      <div className="site-card admin-panel p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <nav className="flex flex-wrap gap-2" aria-label={t.admin?.nav?.pages?.training}>
            {SUB_PAGES.map((subId) => (
              <button
                key={subId}
                type="button"
                className={activeSubPage === subId ? 'homepage-button-primary text-xs' : 'homepage-button-secondary text-xs'}
                aria-current={activeSubPage === subId ? 'page' : undefined}
                onClick={() => navigateSubPage(subId)}
              >
                {navLabels[subId] || subId}
              </button>
            ))}
          </nav>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-text-secondary">{d.generatedAt}: {formatBerlin(data?.generated_at)}</span>
            <button type="button" className="homepage-button-secondary text-xs" disabled={busy} onClick={refreshAll}>{d.refresh}</button>
          </div>
        </div>
        {honestyNote ? <p className="text-xs text-text-secondary">{honestyNote}</p> : null}
        {notice.message ? (
          <div className={`homepage-muted-panel p-3 text-sm ${notice.error ? 'text-error' : 'text-text-secondary'}`}>{notice.message}</div>
        ) : null}
      </div>

      {/* 01 — autopilot: readiness hero, the toggle, thresholds, budgets, events. */}
      {activeSubPage === 'autopilot' ? (
        <>
          {apState.status === 'ok' ? (
            <>
              <SituationCard d={d} isDe={isDe} plan={readinessStages.find((stage) => stage.id === 'data_plan')?.plan || null}
                held={Boolean((checklist?.items || []).find((item) => item.id === 'start_hold' && !item.satisfied))} />
              <TrainingPathCard d={d} autopilot={autopilot} checklist={checklist} runs={runs} isDe={isDe} live={feature('trainingLive').data}
                plan={readinessStages.find((stage) => stage.id === 'data_plan')?.plan || null} />
              <StrandReadinessCard d={d} autopilot={autopilot} isDe={isDe} />
              <TextbookProgramCard d={d} autopilot={autopilot} isDe={isDe} />
            </>
          ) : null}
          {/* Readiness hero (D-HP-ADMIN-067): the one plain answer to "how far from the
              first training run?" — the engine's summary sentence, the stage pipeline
              colored by state, the green/total counter, and every unmet stage with its
              measured sentence and highlighted next step. Feature-detected: a backend
              without the endpoint yields the honest localized note. */}
          <AdminSection
            id="datalake-readiness"
            title={d.readinessTitle}
            subtitle={d.readinessSubtitle}
            state={readinessFeature.status === 'ok' ? (readiness?.training_ready ? d.readinessReady : readinessCounterText) : ''}
            tone={readinessFeature.status === 'ok' ? (readiness?.training_ready ? 'ok' : (readinessBlocked ? 'error' : 'warning')) : ''}
          >
            {readinessFeature.status !== 'ok' ? (
              readinessFeature.status === 'missing'
                ? <p className="text-sm text-text-secondary">{d.readinessUnavailable}</p>
                : <FeatureNote featureKey="readiness" />
            ) : (
              <>
                <p className="admin-readiness-summary">{(() => {
                  const liveProgress = readiness?.training_running ? runProgress(feature('trainingLive').data, isDe) : null;
                  const loss = Number(((feature('trainingLive').data || {}).summary || {}).loss_now);
                  return liveProgress
                    ? fill(d.trReadinessRunning, { pct: liveProgress.pctText, step: liveProgress.stepText, max: liveProgress.maxText,
                      loss: Number.isFinite(loss) ? formatNumber(loss, isDe, 2) : '—', end: liveProgress.endText })
                    : pickBilingual(readiness?.summary, isDe);
                })()}</p>
                <div className="admin-readiness-pipeline">
                  {readinessStages.map((stage, index) => (
                    <React.Fragment key={stage.id}>
                      {index > 0 ? <span className="admin-readiness-joint" aria-hidden="true">→</span> : null}
                      <span
                        className={`admin-readiness-stage ${stageStateClass(stage.state)}`}
                        title={`${stage.id} · ${stageStateLabel(stage.state)}${pickBilingual(stage.measured, isDe) ? ` — ${pickBilingual(stage.measured, isDe)}` : ''}`}
                      >
                        {pickBilingual(stage.label, isDe)}
                      </span>
                    </React.Fragment>
                  ))}
                  <span className="admin-readiness-counter">{readinessCounterText}</span>
                </div>
                {readinessOpenStages.length ? (
                  <div className="space-y-2">
                    {readinessOpenStages.map(renderReadinessStage)}
                  </div>
                ) : null}
              </>
            )}
          </AdminSection>

          {/* Executive summary (D-HP-ADMIN-060): the two hypotheses this surface serves. */}
          <div className="homepage-muted-panel p-4 text-sm space-y-2">
            <div className="font-black">{d.summaryTitle}</div>
            <p className="text-text-secondary">{d.summaryBody}</p>
            <button type="button" className="homepage-button-secondary text-xs" onClick={() => writeAdminRoute('llm', '')}>
              {d.summaryArchButton}
            </button>
          </div>

          <AdminSection
            id="datalake-autopilot"
            title={d.apTitle}
            subtitle={d.apSubtitle}
            state={apState.status === 'ok' ? (apBlocked ? d.apBlockedLabel : (apEnabled ? (apPaused ? d.apPausedPill : d.apOn) : d.apOff)) : ''}
            tone={apState.status === 'ok' ? (apBlocked ? 'error' : (apEnabled && !apPaused ? 'ok' : 'warning')) : ''}
          >
            {apState.status !== 'ok' ? <FeatureNote featureKey="autopilot" /> : (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    className={`${apEnabled ? 'homepage-button-primary' : 'homepage-button-secondary'} admin-datalake-toggle ${apEnabled ? 'is-on' : ''}`}
                    disabled={busy}
                    aria-pressed={apEnabled}
                    title={apEnabled ? d.apTurnOff : d.apTurnOn}
                    onClick={() => { if (window.confirm(apEnabled ? d.apConfirmOff : d.apConfirmOn)) autopilotToggle(); }}
                  >
                    {apEnabled ? d.apOn : d.apOff}
                  </button>
                  {apEnabled ? (
                    <button type="button" className="homepage-button-secondary" disabled={busy} onClick={autopilotPauseResume}>
                      {apPaused ? d.apResume : d.apPause}
                    </button>
                  ) : null}
                  {apPaused ? <span className="admin-status-pill is-warning">{d.apPausedPill}</span> : null}
                  <button
                    type="button"
                    className={`${afterSealOn ? 'homepage-button-primary' : 'homepage-button-secondary'} admin-datalake-toggle ${afterSealOn ? 'is-on' : ''}`}
                    disabled={busy}
                    aria-pressed={afterSealOn}
                    title={d.apAfterSealHint}
                    onClick={() => { if (window.confirm(afterSealOn ? d.apAfterSealConfirmOff : d.apAfterSealConfirmOn)) afterSealToggle(); }}
                  >
                    {d.apAfterSealLabel}: {afterSealOn ? d.apAfterSealOn : d.apAfterSealOff}
                  </button>
                </div>
                {autopilot?.blocked_code === 'PAUSED_AFTER_SEAL' ? (
                  <div className="homepage-muted-panel p-3 text-sm">
                    <span className="admin-status-pill is-warning">{d.apAfterSealWaiting}</span>
                    {autopilot?.paused_after_seal?.tdv_id ? <span className="font-mono text-xs"> {autopilot.paused_after_seal.tdv_id}</span> : null}
                    <p className="text-xs text-text-secondary mt-1">{d.apAfterSealHint}</p>
                  </div>
                ) : null}
                <div className="text-sm">
                  {d.apPhaseLabel}: <strong className="font-mono">{autopilot?.phase || '-'}</strong>
                  {autopilot?.phase_since ? <span className="text-text-secondary"> · {fill(d.apPhaseSince, { time: formatBerlin(autopilot.phase_since) })}</span> : null}
                  {autopilot?.cycle_id ? <span className="text-text-secondary"> · {d.apCycleId}: <span className="font-mono">{String(autopilot.cycle_id).slice(0, 13)}</span></span> : null}
                </div>
                {apBlocked ? (
                  <div className="homepage-muted-panel p-3 space-y-1 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="admin-status-pill is-missing">{d.apBlockedLabel}</span>
                      <code className="font-mono text-xs">{autopilot.blocked_code}</code>
                    </div>
                    <p>{pickBilingual(autopilot.blocked_detail, isDe)}</p>
                    {autopilot.blocked_action === 'connect_storage' ? (
                      <button type="button" className="homepage-button-primary" onClick={() => setConnectOpen(true)}>{d.connect}</button>
                    ) : null}
                  </div>
                ) : null}
                {cyclePhases.length ? (
                  <div className="space-y-2">
                    <div className="font-bold text-sm">{d.apCycleTitle}</div>
                    <table className="admin-table">
                      <thead>
                        <tr><th>{d.colPhase}</th><th>{d.colPending}</th><th>{d.colRunning}</th><th>{d.colDone}</th><th>{d.colFailed}</th></tr>
                      </thead>
                      <tbody>
                        {cyclePhases.map((phaseName) => {
                          const counts = cycleProgress[phaseName] || {};
                          return (
                            <tr key={phaseName}>
                              <td className="font-mono text-xs">{phaseName}</td>
                              <td>{formatInteger(counts.pending)}</td>
                              <td>{formatInteger(counts.running)}</td>
                              <td>{formatInteger(counts.done)}</td>
                              <td className={Number(counts.failed) > 0 ? 'text-error' : ''}>{formatInteger(counts.failed)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : <p className="text-sm text-text-secondary">{d.apCycleEmpty}</p>}
              </>
            )}
          </AdminSection>

          {apState.status === 'ok' ? (
            <>
              {(() => {
                const stale = Object.values(ext).map((f) => f?.data?.stale_since).filter(Boolean).sort().slice(-1)[0];
                return stale ? <div className="admin-status-pill is-warning" style={{ display: 'block', marginBottom: '0.75rem' }}>
                  {d.staleSince} {formatBerlin(stale)}. {d.staleHint}</div> : null;
              })()}
              <AdminSection id="datalake-index-dedup" title={d.idxTitle} subtitle={d.idxSubtitle}>
                {feature('index').status === 'ok' || feature('dedup').status === 'ok' ? (
                  <FactGrid items={[
                    [d.idxConfigured, feature('index').data?.configured ? d.yes : d.no],
                    [d.idxReachable, feature('index').data?.reachable ? d.yes : d.no],
                    [d.idxPoints, feature('index').data?.points_count ?? '—'],
                    [d.idxBackfill, feature('index').data?.backfill_done ? d.idxBackfillDone : d.idxBackfillOpen],
                    [d.ddLastPass, feature('dedup').data?.last_pass?.at ? formatBerlin(feature('dedup').data.last_pass.at) : d.ddNone],
                    [d.ddChecked, feature('dedup').data?.last_pass?.assets_checked ?? '—'],
                    [d.ddDropped, feature('dedup').data?.last_pass?.duplicates_dropped ?? '—'],
                    [d.ddRows, feature('dedup').data?.last_pass?.rows_dropped ?? '—'],
                    [d.ddRemaining, feature('dedup').data?.last_pass?.remaining_candidates ?? '—'],
                  ]} />
                ) : <FeatureNote featureKey="index" />}
              </AdminSection>
              {/* Graph stages (D-SAAS-155): the rank plan of the teacher stages, the last round of
                  every loop, the dangling-node and stuck-asset facts, and the bounded orphan cleanup.
                  The door is feature-detected like its siblings; a stale answer (`stale_since`) shows
                  in the banner above, which reads every loaded door. */}
              <AdminSection id="datalake-graph-stages" title={d.gsTitle} subtitle={d.gsSubtitle}>
                {feature('graphStages').status === 'ok' ? (() => {
                  const gs = feature('graphStages').data || {};
                  const ranks = Array.isArray(gs.ranks) ? gs.ranks : [];
                  const loops = gs.loops && typeof gs.loops === 'object' ? gs.loops : {};
                  const loopNames = [...GRAPH_LOOPS, ...Object.keys(loops).filter((name) => !GRAPH_LOOPS.includes(name))];
                  const rankLabels = { owner: d.gsRankOwner, expert: d.gsRankExpert, general: d.gsRankGeneral, code: d.gsRankCode };
                  const loopLabels = { 'grag-embed': d.gsLoopEmbed, 'grag-extract': d.gsLoopExtract, 'grag-link-stage': d.gsLoopLink };
                  const measuredAgo = gs.age_seconds !== undefined && gs.age_seconds !== null
                    ? formatRelative(-Number(gs.age_seconds), currentLang) : formatAgo(gs.measured_at, currentLang);
                  const pass = gs.last_orphans_pass && typeof gs.last_orphans_pass === 'object' ? gs.last_orphans_pass : null;
                  return (
                    <div className="space-y-4 text-sm">
                      <p className="text-xs text-text-secondary">{d.gsMeasuredAt}: {formatBerlin(gs.measured_at)}{measuredAgo ? ` (${measuredAgo})` : ''}</p>
                      <div className="space-y-2">
                        <div className="font-bold text-sm">{d.gsRanksTitle}</div>
                        {ranks.length ? (
                          <div className="admin-table-shell"><div className="admin-table-scroll">
                            <table className="admin-table">
                              <thead>
                                <tr><th>{d.gsColRank}</th><th>{d.gsColLabel}</th><th>{d.gsColSources}</th><th>{d.gsColAssets}</th><th>{d.gsColPendingExtract}</th><th>{d.gsColPendingEmbed}</th></tr>
                              </thead>
                              <tbody>
                                {ranks.map((row, index) => {
                                  const sources = Array.isArray(row?.sources) ? row.sources : [];
                                  return (
                                    <tr key={row?.rank ?? index}>
                                      <td className="font-mono text-xs">{row?.rank ?? '-'}</td>
                                      <td>{rankLabels[row?.label_key] || row?.label_key || '-'}</td>
                                      <td title={sources.length ? sources.join(', ') : undefined}>{formatInteger(sources.length)}</td>
                                      <td>{formatInteger(row?.assets)}</td>
                                      <td>{formatInteger(row?.pending_extract)}</td>
                                      <td>{formatInteger(row?.pending_embed)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div></div>
                        ) : <p className="text-text-secondary">{d.gsRanksEmpty}</p>}
                      </div>
                      <div className="space-y-2">
                        <div className="font-bold text-sm">{d.gsLoopsTitle}</div>
                        <div className="admin-table-shell"><div className="admin-table-scroll">
                          <table className="admin-table">
                            <thead>
                              <tr><th>{d.gsColLoop}</th><th>{d.gsColSource}</th><th>{d.gsColChunks}</th><th>{d.gsColClaims}</th><th>{d.gsColEdges}</th><th>{d.gsColSeconds}</th><th>{d.gsColLast}</th></tr>
                            </thead>
                            <tbody>
                              {loopNames.map((name) => {
                                const round = loops[name] && typeof loops[name] === 'object' ? loops[name] : null;
                                const ago = round ? formatAgo(round.at, currentLang) : '';
                                const detail = round ? [
                                  round.rank !== undefined && round.rank !== null ? fill(d.gsRankShort, { n: round.rank }) : '',
                                  round.asset_id ? String(round.asset_id).slice(0, 13) : '',
                                ].filter(Boolean).join(' · ') : '';
                                return (
                                  <tr key={name}>
                                    <td>{loopLabels[name] || name}<div className="font-mono text-xs text-text-secondary">{name}</div></td>
                                    {round ? (
                                      <>
                                        <td>{round.source || '-'}{detail ? <div className="font-mono text-xs text-text-secondary">{detail}</div> : null}</td>
                                        <td>{formatInteger(round.did)}</td>
                                        <td>
                                          {formatInteger(round.claims)}
                                          {round.ungrounded !== undefined && round.ungrounded !== null
                                            ? <span className="text-text-secondary"> · {fill(d.gsUngrounded, { n: formatInteger(round.ungrounded) })}</span> : null}
                                        </td>
                                        <td>{formatInteger(round.edges)}</td>
                                        <td>{formatSeconds(round.seconds)}</td>
                                        <td>{ago || formatBerlin(round.at)}{ago ? <div className="font-mono text-xs text-text-secondary">{formatBerlin(round.at)}</div> : null}</td>
                                      </>
                                    ) : <td colSpan={6} className="text-text-secondary">{d.gsNoRound}</td>}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div></div>
                      </div>
                      <FactGrid items={[
                        [d.gsClaims, formatInteger(gs.claims)],
                        [d.gsAssetsWithClaims, formatInteger(gs.assets_with_claims)],
                        [d.gsDangling, formatInteger(gs.dangling_chunk_nodes)],
                        [d.gsStuck, formatInteger(gs.stuck_assets)],
                        [d.gsConcurrency, formatInteger(gs.extract_concurrency)],
                      ]} />
                      <div className="homepage-muted-panel p-3 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-bold text-sm">{d.gsOrphansLast}</span>
                          {pass ? (
                            <>
                              <span className={`admin-status-pill ${pass.status === 'cleaned' ? 'is-ok' : 'is-neutral'}`}>
                                {pass.status === 'cleaned' ? d.gsOrphansStatusCleaned : (pass.status || '-')}
                              </span>
                              <span className="text-xs text-text-secondary">
                                {d.gsOrphansDropped}: {formatInteger(pass.nodes_dropped)} · {d.gsOrphansRemaining}: {formatInteger(pass.remaining)} · {formatBerlin(pass.checked_at)}
                              </span>
                            </>
                          ) : <span className="text-xs text-text-secondary">{d.gsOrphansNone}</span>}
                        </div>
                        <p className="text-xs text-text-secondary">{d.gsOrphansHint}</p>
                      </div>
                    </div>
                  );
                })() : <FeatureNote featureKey="graphStages" />}
              </AdminSection>
              {planGoverns ? (
              <AdminSection id="datalake-thresholds" title={d.thrPlanTitle} subtitle={d.thrPlanNote} count={planCapabilities.length}>
                <ul className="space-y-1" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {planCapabilities.map((row) => (
                    <li key={`${row.capability}/${row.mod_sig}`} className="homepage-muted-panel p-3 flex flex-wrap items-center gap-2 text-xs">
                      <span className="text-sm text-text-primary" style={{ minWidth: '12rem' }}>{d[`cap_${row.capability}`] || row.capability}</span>
                      <span className="text-text-secondary">{fill(d.thrPlanRow, { samples: formatNumber(row.measured_samples, isDe),
                        tokens: formatCompact(row.measured_tokens, isDe) })}</span>
                    </li>
                  ))}
                </ul>
              </AdminSection>
              ) : (
              <AdminSection id="datalake-thresholds" title={d.thrTitle} subtitle={d.thrSubtitle} count={measuredThresholds.length}>
                {measuredThresholds.length ? (
                  <div className="space-y-2">
                    {measuredThresholds.map((row) => (
                      <div key={row.threshold_id || `${row.capability}:${row.mod_sig}`} className="homepage-muted-panel p-3 space-y-2">
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          <span className="font-mono font-bold">{row.capability}</span>
                          <span className="font-mono text-text-secondary">{row.mod_sig}</span>
                          {row.met ? <StatusPill ok>{d.thrMet}</StatusPill> : <span className="admin-status-pill is-warning">{d.thrNotMet}</span>}
                          {row.is_default ? <span className="admin-status-pill is-neutral">{d.thrDefault}</span> : null}
                          {row.applies_to_strand === false
                            ? <span className="admin-status-pill is-neutral">{d.thrForeign}</span> : null}
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                          <ProgressRow label={d.thrSamplesBar} value={row.measured_samples} target={row.min_samples} met={row.met}
                            noMinimumLabel={d.thrNoMinimum} />
                          <ProgressRow label={d.thrTokensBar} value={row.measured_tokens} target={row.min_token_est} met={row.met}
                            noMinimumLabel={d.thrNoMinimum} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-text-secondary">{d.thrEmpty}</p>}

                <div className="homepage-muted-panel p-4 space-y-2">
                  <div className="font-bold text-sm">{d.thrEditTitle}</div>
                  <p className="text-xs text-text-secondary">{d.thrEditSubtitle}</p>
                  <datalist id="datalake-capabilities">
                    {CAPABILITIES.map((capability) => <option key={capability} value={capability} />)}
                  </datalist>
                  {(thresholdRows || []).map((row, index) => (
                    <div key={index} className="flex flex-wrap gap-2 items-end text-xs">
                      <input className="form-input" style={{ width: '14rem' }} list="datalake-capabilities" value={row.capability} title={d.capability}
                        onChange={(event) => setThresholdRows(thresholdRows.map((r, i) => (i === index ? { ...r, capability: event.target.value } : r)))} />
                      <input className="form-input" style={{ width: '6rem' }} value={row.mod_sig} title={d.modSignature}
                        onChange={(event) => setThresholdRows(thresholdRows.map((r, i) => (i === index ? { ...r, mod_sig: event.target.value } : r)))} />
                      <input className="form-input" style={{ width: '8rem' }} type="number" min="0" value={row.min_samples} title={d.thrMinSamples}
                        onChange={(event) => setThresholdRows(thresholdRows.map((r, i) => (i === index ? { ...r, min_samples: event.target.value } : r)))} />
                      <input className="form-input" style={{ width: '9rem' }} type="number" min="0" value={row.min_token_est} title={d.thrMinTokens}
                        onChange={(event) => setThresholdRows(thresholdRows.map((r, i) => (i === index ? { ...r, min_token_est: event.target.value } : r)))} />
                      <label className="flex items-center gap-1">
                        <input type="checkbox" checked={row.enabled}
                          onChange={(event) => setThresholdRows(thresholdRows.map((r, i) => (i === index ? { ...r, enabled: event.target.checked } : r)))} />
                        <span>{d.thrEnabled}</span>
                      </label>
                      <button type="button" className="homepage-button-secondary"
                        onClick={() => setThresholdRows(thresholdRows.filter((_, i) => i !== index))}>{d.removeRow}</button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <button type="button" className="homepage-button-secondary"
                      onClick={() => setThresholdRows([...(thresholdRows || []), { capability: 'tool_calling', mod_sig: 't->t', min_samples: 0, min_token_est: 0, enabled: true }])}>
                      {d.thrAdd}
                    </button>
                    <button type="button" className="homepage-button-primary" disabled={busy || thresholdRows === null}
                      onClick={() => runAction(() => requestJson(`${API}/autopilot/thresholds`, {
                        method: 'POST',
                        body: JSON.stringify({
                          thresholds: (thresholdRows || []).map((row) => ({
                            capability: row.capability, mod_sig: row.mod_sig,
                            min_samples: Number(row.min_samples) || 0, min_token_est: Number(row.min_token_est) || 0,
                            enabled: Boolean(row.enabled),
                          })),
                        }),
                      }), d.thrSaved, async () => { setThresholdRows(null); await loadFeature('autopilot', `${API}/autopilot`); })}>
                      {d.thrSave}
                    </button>
                  </div>
                </div>
              </AdminSection>
              )}

              <AdminSection id="datalake-budgets" title={d.budgetsTitle} subtitle={d.budgetsSubtitle} collapsed>
                {budgets.length ? (
                  <table className="admin-table">
                    <thead><tr><th>{d.budgetScope}</th><th>{d.budgetDay}</th><th>{d.budgetSpent}</th><th>{d.budgetCap}</th></tr></thead>
                    <tbody>
                      {budgets.map((budget, index) => (
                        <tr key={index}>
                          <td className="font-mono text-xs">{budget.scope}</td>
                          <td className="text-xs">{budget.day}</td>
                          <td>{formatInteger(budget.spent)}</td>
                          <td>{formatInteger(budget.cap)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="text-sm text-text-secondary">{d.budgetsEmpty}</p>}
              </AdminSection>

              <AdminSection id="datalake-events" title={d.eventsTitle} subtitle={d.eventsSubtitle} count={eventRows.length}>
                {eventRows.length ? (
                  <div className="space-y-1">
                    {eventRows.map((event) => (
                      <div key={event.event_id} className="homepage-muted-panel p-2 text-xs flex flex-wrap items-center gap-2">
                        <span className="text-text-secondary">{formatBerlin(event.ts)}</span>
                        <span className={`admin-status-pill ${severityClass(event.severity)}`}>{event.severity || '-'}</span>
                        <span className="font-bold">{d[`ev_${event.kind}`] || String(event.kind || '').replace(/_/g, ' ')}</span>
                        {event.payload && Object.keys(event.payload).length ? (
                          <span className="text-text-secondary" style={{ overflowWrap: 'anywhere' }}>{readablePayload(event.payload, isDe)}</span>
                        ) : null}
                      </div>
                    ))}
                    {eventsHaveMore ? (
                      <button type="button" className="homepage-button-secondary" disabled={busy}
                        onClick={() => loadEvents(eventRows[eventRows.length - 1]?.event_id)}>
                        {d.loadMore}
                      </button>
                    ) : null}
                  </div>
                ) : <p className="text-sm text-text-secondary">{d.eventsEmpty}</p>}
              </AdminSection>
            </>
          ) : null}
        </>
      ) : null}

      {/* 02 — storage: the full download, connection + bootstrap, the space of the block storage and the folder tree
          with the size of every branch (D-HP-ADMIN-093). */}
      {storageVisited ? (
        <div hidden={activeSubPage !== 'storage'}>
          <BlockStorageDownload d={d} isDe={isDe} lang={currentLang} />
        </div>
      ) : null}
      {activeSubPage === 'storage' ? (
        <>
          <AdminSection
            id="datalake-connection"
            title={d.storageTitle}
            subtitle={d.storageSubtitle}
            state={connection.connected ? d.connectedYes : d.connectedNo}
            tone={connection.connected ? 'ok' : 'error'}
            actions={(
              <>
                <button type="button" className="homepage-button-secondary" onClick={() => setConnectOpen(true)}>{d.connect}</button>
                <button
                  type="button"
                  className="homepage-button-primary"
                  disabled={busy}
                  onClick={() => runAction(async () => {
                    const result = await requestJson(`${API}/bootstrap`, { method: 'POST', body: '{}' });
                    setBootstrapReport(result);
                    refreshAll();
                    return result;
                  }, d.bootstrapDone)}
                >
                  {d.bootstrapRun}
                </button>
              </>
            )}
          >
            <FactGrid items={[
              [d.backendLabel, connection.backend ? (connection.backend === 'localfs' ? d.backendLocal : d.backendBlockStorage) : connection.backend],
              [d.connectedLabel, connection.connected ? d.connectedYes : d.connectedNo],
              [d.accountLabel, connection.account],
              [d.oauthClientLabel, connection.client_configured ? d.clientConfigured : d.clientNotConfigured],
              ...(connection.expires_at ? [[d.signinValidUntil, formatBerlin(connection.expires_at, { seconds: false })]] : []),
            ]} />
            {!connection.connected && connection.reason ? (
              <p className="text-sm text-error">{pickBilingual(connection.reason, isDe)}</p>
            ) : null}
            {/* D-SAAS-254: an app in Google's testing mode loses its sign-in after seven days */}
            {connection.connected && connection.expiry_note ? (
              <p className={`text-sm ${connection.expiring_soon ? 'text-error' : 'text-text-secondary'}`}>
                {pickBilingual(connection.expiry_note, isDe)}
              </p>
            ) : null}
            {bootstrapReport ? (
              <div className="homepage-muted-panel p-3 space-y-1 text-sm">
                <div className="font-bold">{d.bootstrapReportTitle}</div>
                {pick(bootstrapReport, 'message', isDe) ? <div>{pick(bootstrapReport, 'message', isDe)}</div> : null}
                <div className="text-xs text-text-secondary">
                  {fill(d.bootstrapLine, {
                    folders: (bootstrapReport.report?.top_folders_created || []).join(', ') || d.none,
                    files: formatInteger(bootstrapReport.report?.cold_files_seen),
                    idempotent: bootstrapReport.report?.idempotent_rerun ? d.yes : d.no,
                  })}
                </div>
              </div>
            ) : <p className="text-sm text-text-secondary">{d.bootstrapNoRun}</p>}
          </AdminSection>

          <BlockStorageUsage d={d} usage={feature('bsUsage')} lang={currentLang} />

          <AdminSection id="datalake-tree" title={d.treeTitle} subtitle={d.treeSubtitle}>
            {feature('tree').status === 'ok' && tree ? (
              <>
                <div className="font-bold text-sm">{fill(d.treeActiveVersion, { version: tree.active?.version ?? '-' })}</div>
                <StorageTree
                  d={d}
                  folders={storageFolders}
                  sizes={feature('bsSizes').status === 'ok' ? feature('bsSizes').data : null}
                  treeStatsByFolder={treeStatsByFolder}
                  checkpointRuns={checkpointRuns}
                  busy={busy}
                  lang={currentLang}
                  onRetire={(path) => runAction(() => requestJson(`${API}/tree`, {
                    method: 'POST', body: JSON.stringify({ action: 'retire_folder', path }),
                  }), d.treeRetired, () => loadFeature('tree', `${API}/tree`))}
                />
                <form
                  className="flex flex-wrap gap-2 items-end text-sm"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (!treeForm.path.trim()) return;
                    runAction(() => requestJson(`${API}/tree`, {
                      method: 'POST',
                      body: JSON.stringify({ action: 'add_folder', path: treeForm.path.trim(), purpose: treeForm.purpose.trim() }),
                    }), d.treeFolderAdded, async () => {
                      setTreeForm({ path: '', purpose: '' });
                      await loadFeature('tree', `${API}/tree`);
                    });
                  }}
                >
                  <label className="block">
                    <span className="text-xs font-bold">{d.treePath}</span>
                    <input className="form-input w-full font-mono" value={treeForm.path}
                      onChange={(event) => setTreeForm({ ...treeForm, path: event.target.value })} />
                  </label>
                  <label className="block" style={{ minWidth: '18rem' }}>
                    <span className="text-xs font-bold">{d.treePurpose}</span>
                    <input className="form-input w-full" value={treeForm.purpose}
                      onChange={(event) => setTreeForm({ ...treeForm, purpose: event.target.value })} />
                  </label>
                  <button type="submit" className="homepage-button-primary" disabled={busy || !treeForm.path.trim()}>{d.treeAddFolder}</button>
                </form>
                {(tree.versions || []).length ? (
                  <div className="homepage-muted-panel p-3 space-y-1 text-xs">
                    <div className="font-bold">{d.treeVersionsTitle}</div>
                    {(tree.versions || []).map((version) => (
                      <div key={version.version} className="flex flex-wrap gap-3">
                        <span className="font-mono">v{version.version}</span>
                        <span>{version.author}</span>
                        <span className="text-text-secondary">{version.note}</span>
                        <span className="text-text-secondary">{formatBerlin(version.created_at)}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <FeatureNote featureKey="tree" />
                {feature('tree').status === 'missing' && (treeSpec.top_folders || []).length ? (
                  <div className="homepage-muted-panel p-4 space-y-2 text-sm">
                    <div className="font-bold">{fill(d.treePlanned, { version: treeSpec.version ?? '-' })}</div>
                    <ul className="space-y-1 font-mono text-xs">
                      {(treeSpec.top_folders || []).map((folder) => (
                        <li key={folder} className="flex justify-between gap-2">
                          <span>{folder}/</span>
                          <span className="text-text-secondary">
                            {treeStatsByFolder[folder]
                              ? `${formatInteger(treeStatsByFolder[folder].shards)} · ${formatBytes(treeStatsByFolder[folder].bytes)}`
                              : (treeSpec.partition_axes?.[folder] || '')}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </>
            )}
          </AdminSection>
        </>
      ) : null}

      {/* 03 — acquisition & preprocessing (D-SAAS-108/109): curated open-license sources
          with measured progress, German/English balance, pipeline counters, PII scrub. */}
      {activeSubPage === 'acquisition' ? (
        <>
          <AdminSection id="datalake-acquisition" title={d.acqTitle} subtitle={d.acqSubtitle}>
            <p className="text-xs text-text-secondary">{d.acqGdprNote}</p>
            {feature('acquisition').status !== 'ok' ? <FeatureNote featureKey="acquisition" /> : (
              (acquisition?.sources || []).length ? (
                <div className="admin-table-shell"><div className="admin-table-scroll">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>{d.acqSource}</th><th>{d.acqModality}</th><th>{d.acqLicense}</th>
                        <th>{d.acqProtection}</th><th>{d.acqProgress}</th><th>{d.statusCol}</th><th>{d.treeActions}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {acquisition.sources.map((source) => {
                        const targetGb = acqTargets[source.source_id] !== undefined
                          ? acqTargets[source.source_id]
                          : (Number(source.byte_target || 0) / GB).toFixed(1);
                        const stateLabel = acquisitionStateLabel(source, d);
                        return (
                          <tr key={source.source_id}>
                            <td className="text-xs">
                              <div className="font-bold">{source.name}</div>
                              {source.is_default ? <span className="admin-status-pill is-neutral">{d.thrDefault}</span> : null}
                              {!source.enabled && source.disabled_reason ? (
                                <div className="text-error" style={{ overflowWrap: 'anywhere' }}>{source.disabled_reason}</div>
                              ) : null}
                              {source.action_url ? (
                                <a className="homepage-button-secondary" href={source.action_url} target="_blank" rel="noreferrer">
                                  {d.licenseAcceptLink}
                                </a>
                              ) : null}
                            </td>
                            <td className="font-mono text-xs">{source.modality} · {source.lang}</td>
                            <td className="font-mono text-xs">{source.license}</td>
                            <td className="text-xs" style={{ maxWidth: '14rem' }}>{source.data_protection}</td>
                            <td style={{ minWidth: '11rem' }}>
                              <div className="space-y-1 text-xs">
                                <div className="flex justify-between gap-2">
                                  <span>{formatBytes(source.bytes_done)} / {formatBytes(source.byte_target)}</span>
                                  <span className="text-text-secondary">{fill(d.acqDocs, { count: formatInteger(source.docs_done) })}</span>
                                </div>
                                <div style={{ height: '0.5rem', background: 'var(--color-border, #e5e5e5)', borderRadius: '0.125rem', overflow: 'hidden' }}>
                                  <div style={{
                                    width: `${Number(source.byte_target) > 0 ? Math.min(100, Math.round((Number(source.bytes_done || 0) / Number(source.byte_target)) * 100)) : 0}%`,
                                    height: '0.5rem',
                                    background: 'var(--text-primary)',
                                  }} />
                                </div>
                              </div>
                            </td>
                            <td>
                              <StatusPill ok={Boolean(source.enabled)}>{source.enabled ? d.acqEnabledPill : d.acqDisabledPill}</StatusPill>
                              {source.state ? (
                                <div className={`text-xs text-text-secondary${stateLabel === source.state ? ' font-mono' : ''}`}>{stateLabel}</div>
                              ) : null}
                              {source.last_error_note ? (
                                <div className="text-xs text-error" title={source.last_error || ''}>{pickBilingual(source.last_error_note, isDe)}</div>
                              ) : source.last_error === 'RING_BUFFER_FULL' ? (
                                // backpressure, not a fault: the landed files wait for their upload to the block storage
                                <div className="text-xs text-text-secondary" title={source.last_error}>{d.acqWaitingBuffer}</div>
                              ) : source.last_error ? <div className="font-mono text-xs text-error" style={{ overflowWrap: 'anywhere' }}>{source.last_error}</div> : null}
                            </td>
                            <td>
                              <div className="flex flex-wrap items-end gap-1">
                                <button type="button" className="homepage-button-secondary" disabled={busy}
                                  onClick={() => runAction(() => requestJson(`${API}/acquisition`, {
                                    method: 'POST',
                                    body: JSON.stringify({ action: source.enabled ? 'disable' : 'enable', source_id: source.source_id }),
                                  }), source.enabled ? d.acqDisabledMsg : d.acqEnabledMsg, () => loadFeature('acquisition', `${API}/acquisition`))}>
                                  {source.enabled ? d.acqDisable : d.acqEnable}
                                </button>
                                <label className="block text-xs">
                                  <span className="font-bold">{d.acqTargetLabel}</span>
                                  <input className="form-input" style={{ width: '5.5rem' }} type="number" min="0" step="0.5" value={targetGb}
                                    onChange={(event) => setAcqTargets({ ...acqTargets, [source.source_id]: event.target.value })} />
                                </label>
                                <button type="button" className="homepage-button-secondary" disabled={busy || acqTargets[source.source_id] === undefined}
                                  onClick={() => runAction(() => requestJson(`${API}/acquisition`, {
                                    method: 'POST',
                                    body: JSON.stringify({
                                      action: 'set_target',
                                      source_id: source.source_id,
                                      byte_target: Math.max(0, Math.round((Number(acqTargets[source.source_id]) || 0) * GB)),
                                    }),
                                  }), d.acqTargetSaved, async () => {
                                    setAcqTargets((previous) => {
                                      const next = { ...previous };
                                      delete next[source.source_id];
                                      return next;
                                    });
                                    await loadFeature('acquisition', `${API}/acquisition`);
                                  })}>
                                  {d.acqSetTarget}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div></div>
              ) : <p className="text-sm text-text-secondary">{d.acqEmpty}</p>
            )}
          </AdminSection>

          <AdminSection id="datalake-balance" title={d.balanceTitle} subtitle={d.balanceSubtitle}>
            {feature('acquisition').status !== 'ok' ? <FeatureNote featureKey="acquisition" /> : (() => {
              const balance = acquisition?.balance || {};
              const modalities = [['text', d.modText], ['audio', d.modAudio], ['image', d.modImage], ['video', d.modVideo]]
                .filter(([key]) => balance[key]);
              if (!modalities.length) return <p className="text-sm text-text-secondary">{d.balanceEmpty}</p>;
              return (
                <div className="grid gap-3 md:grid-cols-2">
                  {modalities.map(([key, label]) => {
                    const pair = balance[key] || {};
                    const deBytes = Number(pair.de_bytes || 0);
                    const enBytes = Number(pair.en_bytes || 0);
                    const max = Math.max(deBytes, enBytes, 1);
                    return (
                      <div key={key} className="homepage-muted-panel p-3 space-y-1 text-xs">
                        <div className="font-bold">{label}</div>
                        {[['DE', deBytes], ['EN', enBytes]].map(([langLabel, bytes]) => (
                          <div key={langLabel} className="flex items-center gap-2">
                            <span className="font-mono" style={{ width: '2rem' }}>{langLabel}</span>
                            <div style={{ flex: 1, height: '0.6rem', background: 'var(--color-border, #e5e5e5)', borderRadius: '0.125rem', overflow: 'hidden' }}>
                              <div style={{ width: `${Math.round((bytes / max) * 100)}%`, height: '0.6rem', background: langLabel === 'DE' ? 'var(--text-primary)' : 'var(--success)' }} />
                            </div>
                            <span className="font-mono" style={{ minWidth: '5rem', textAlign: 'right' }}>{formatBytes(bytes)}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </AdminSection>

          <AdminSection id="datalake-preprocessing" title={d.prepTitle} subtitle={d.prepSubtitle}>
            {feature('preprocessing').status !== 'ok' ? <FeatureNote featureKey="preprocessing" /> : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    ['registered', d.prepRegistered], ['scrubbed', d.prepScrubbed], ['chunked', d.prepChunked],
                    ['embedded', d.prepEmbedded], ['extracted', d.prepExtracted], ['linked', d.prepLinked],
                    ['samples', d.prepSamples],
                  ].map(([key, label], index) => (
                    <React.Fragment key={key}>
                      {index > 0 ? <span className="text-text-secondary">→</span> : null}
                      <div className="homepage-muted-panel p-3 text-center" style={{ minWidth: '7rem' }}>
                        {/* The scrub stage reports its counts per kind; the stage count is the scrubbed assets. */}
                        <div className="font-black text-lg">{formatInteger(typeof preprocessing?.[key] === 'object' && preprocessing?.[key] !== null
                          ? preprocessing[key].assets_scrubbed : preprocessing?.[key])}</div>
                        <div className="text-xs text-text-secondary">{label}</div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                {Object.keys(preprocessing?.by_lang || {}).length ? (
                  <div className="homepage-muted-panel p-3 space-y-1 text-xs">
                    <div className="font-bold">{d.prepByLang}</div>
                    {Object.entries(preprocessing.by_lang).map(([lang, value]) => (
                      <div key={lang} className="flex flex-wrap gap-3">
                        <span className="font-mono font-bold">{lang}</span>
                        <span className="font-mono text-text-secondary">
                          {typeof value === 'object' && value !== null
                            ? Object.entries(value).map(([stage, count]) => `${stage}: ${formatInteger(count)}`).join(' · ')
                            : formatInteger(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className="grid gap-3 md:grid-cols-3 text-xs">
                  {[[d.prepAwaiting, preprocessing?.awaiting], [d.prepUnsupported, preprocessing?.unsupported], [d.prepFailures, preprocessing?.failures]].map(([label, rows]) => (
                    <div key={label} className="homepage-muted-panel p-3 space-y-1">
                      <div className="font-bold">{label}</div>
                      {(rows || []).length ? (rows || []).map((row) => (
                        <div key={row.code} className="flex justify-between gap-2">
                          <span>
                            {assetStateLabel(row.code)}
                            <span className="block font-mono text-text-secondary">{row.code}</span>
                          </span>
                          <span>{formatInteger(row.count)}</span>
                        </div>
                      )) : <div className="text-text-secondary">{d.none}</div>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </AdminSection>
        </>
      ) : null}

      {/* 04 — data: raw inbox, knowledge graph, coverage targets, TDVs, compatibility. */}
      {activeSubPage === 'data' ? (
        <>
          <AdminSection
            id="datalake-inbox"
            title={d.inboxTitle}
            subtitle={d.inboxSubtitle}
            state={inbox && unprocessedCount ? fill(d.unprocessedCount, { count: unprocessedCount }) : ''}
            tone={inbox && unprocessedCount ? 'warning' : ''}
            actions={feature('inbox').status === 'ok' ? (
              <>
                <label className={`homepage-button-secondary ${busy ? 'opacity-50' : ''}`} style={{ cursor: 'pointer' }}>
                  {d.uploadButton}
                  <input type="file" className="hidden" onChange={uploadAsset} disabled={busy} />
                </label>
                <button type="button" className="homepage-button-secondary" disabled={busy}
                  onClick={() => runAction(() => requestJson(`${API}/inbox/reconcile`, { method: 'POST', body: '{}' }), d.reconcileDone, () => loadFeature('inbox', `${API}/inbox`))}>
                  {d.reconcileNow}
                </button>
                <button type="button" className="homepage-button-primary" disabled={busy || !unprocessedCount}
                  onClick={() => runAction(() => requestJson(`${API}/inbox/process-now`, { method: 'POST', body: '{}' }), d.processStarted, () => loadFeature('inbox', `${API}/inbox`))}>
                  {d.processNow}
                </button>
              </>
            ) : null}
          >
            {feature('inbox').status !== 'ok' ? <FeatureNote featureKey="inbox" /> : (
              <>
                <p className="text-xs text-text-secondary">{d.uploadHint}</p>
                {(inbox?.assets || []).length ? (
                  <div className="admin-table-shell"><div className="admin-table-scroll">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>{d.assetWhat}</th><th>{d.assetName}</th><th>{d.assetSize}</th>
                          <th>{d.assetState}</th><th>{d.assetLang}</th><th>{d.assetRegistered}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inbox.assets.map((asset) => (
                          <tr key={asset.asset_id}>
                            <td className="text-xs">{describeAsset(asset)}</td>
                            <td className="text-xs" style={{ overflowWrap: 'anywhere' }}>
                              {asset.original_name}
                              <div className="font-mono text-text-secondary" style={{ fontSize: '0.65rem' }}>{asset.media_type}</div>
                            </td>
                            <td>{formatBytes(asset.size_bytes)}</td>
                            <td>
                              <StatusPill ok={!['failed', 'unsupported'].includes(asset.state)}>{assetStateLabel(asset.state)}</StatusPill>
                              {asset.failure_code ? <div className="font-mono text-xs text-error">{asset.failure_code}</div> : null}
                            </td>
                            <td className="font-mono text-xs">{asset.lang || '-'}</td>
                            <td className="text-xs">{formatBerlin(asset.registered_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div></div>
                ) : <p className="text-sm text-text-secondary">{d.inboxEmpty}</p>}
              </>
            )}
          </AdminSection>

          <AdminSection id="datalake-graph" title={d.graphTitle} subtitle={d.graphSubtitle}>
            {feature('graph').status !== 'ok' ? <FeatureNote featureKey="graph" /> : (
              <>
                <FactGrid items={[
                  [d.graphEmbedded, formatInteger(graph?.embedded_nodes)],
                  [d.graphModel, graph?.embedding_model],
                  [d.graphClusters, formatInteger(graph?.clusters)],
                  [d.graphAssetsLinked, formatInteger(graph?.assets_linked)],
                  [d.graphLastLink, formatBerlin(graph?.last_link_at)],
                ]} />
                {Object.keys(graph?.nodes_by_kind || {}).length || Object.keys(graph?.edges_by_kind || {}).length ? (
                  <div className="grid gap-4 md:grid-cols-2 text-sm">
                    {[[d.graphNodesTitle, graph?.nodes_by_kind], [d.graphEdgesTitle, graph?.edges_by_kind]].map(([title, byKind]) => (
                      <div key={title} className="homepage-muted-panel p-3 space-y-1">
                        <div className="font-bold text-xs">{title}</div>
                        <table className="admin-table">
                          <thead><tr><th>{d.kindCol}</th><th>{d.countCol}</th></tr></thead>
                          <tbody>
                            {Object.entries(byKind || {}).map(([kind, count]) => (
                              // A malformed kind never stretches the table: long values are cut, the full value stays in the tooltip.
                              <tr key={kind}><td className="font-mono text-xs" title={kind} style={{ overflowWrap: 'anywhere' }}>
                                {kind.length > 48 ? `${kind.slice(0, 48)}…` : kind}</td><td>{formatInteger(count)}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-text-secondary">{d.graphEmpty}</p>}
                <div className="homepage-muted-panel p-3 space-y-2 text-sm">
                  <div className="font-bold text-xs">{d.neighborsTitle}</div>
                  <form
                    className="flex flex-wrap gap-2 items-end"
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (!neighborNodeId.trim()) return;
                      setNeighborResult(null);
                      requestJson(`${API}/graph/neighbors?node_id=${encodeURIComponent(neighborNodeId.trim())}&k=10`)
                        .then(setNeighborResult)
                        .catch((error) => setNeighborResult({ error: errorText(error, isDe) }));
                    }}
                  >
                    <label className="block">
                      <span className="text-xs font-bold">{d.nodeIdLabel}</span>
                      <input className="form-input w-full font-mono text-xs" value={neighborNodeId}
                        onChange={(event) => setNeighborNodeId(event.target.value)} />
                    </label>
                    <button type="submit" className="homepage-button-secondary" disabled={!neighborNodeId.trim()}>{d.neighborsRun}</button>
                  </form>
                  {neighborResult?.error ? <p className="text-xs text-error">{neighborResult.error}</p> : null}
                  {neighborResult && !neighborResult.error ? (
                    (neighborResult.neighbors || []).length ? (
                      <table className="admin-table">
                        <thead><tr><th>{d.nodeCol}</th><th>{d.edgeKindCol}</th><th>{d.weightCol}</th></tr></thead>
                        <tbody>
                          {neighborResult.neighbors.map((neighbor, index) => (
                            <tr key={index}>
                              <td className="font-mono text-xs" style={{ overflowWrap: 'anywhere' }}>
                                {typeof neighbor.node === 'object' ? JSON.stringify(neighbor.node) : String(neighbor.node)}
                              </td>
                              <td className="font-mono text-xs">{neighbor.edge_kind}</td>
                              <td>{neighbor.weight}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : <p className="text-xs text-text-secondary">{d.neighborsEmpty}</p>
                  ) : null}
                </div>
              </>
            )}
          </AdminSection>

          {planGoverns ? (
          <AdminSection id="datalake-gap-matrix" title={d.mixPlanTitle} subtitle={d.mixPlanNote}>
            <p className="text-sm">
              {Object.entries(planMix).map(([area, share]) => fill(d.mixPlanShare, {
                area: d[`tpPlanCat_${area}`] || area, pct: formatNumber(Number(share) * 100, isDe) })).join(' · ') || '—'}
            </p>
          </AdminSection>
          ) : (
          <AdminSection
            id="datalake-gap-matrix"
            title={d.gapTitle}
            subtitle={d.gapSubtitle}
            state={gapMatrix.targets_defined ? '' : pick(gapMatrix, 'message', isDe)}
            tone={gapMatrix.targets_defined ? 'ok' : 'warning'}
          >
            {gapMatrix.targets_defined ? (
              <div className="admin-table-shell"><div className="admin-table-scroll">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>{d.capability}</th><th>{d.modSignature}</th><th>{d.tier}</th>
                      <th>{d.gapHave}</th><th>{d.gapTarget}</th><th>{d.gapWeight}</th><th>{d.gapPriority}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(gapMatrix.rows || []).map((row, index) => (
                      <tr key={index}>
                        <td className="font-mono text-xs">{row.capability}</td>
                        <td className="font-mono text-xs">{row.mod_sig}</td>
                        <td className="font-mono text-xs">{row.tier_level}</td>
                        <td>{formatInteger(row.have)}</td>
                        <td>{formatInteger(row.target_count)}</td>
                        <td>{row.weight}</td>
                        <td>{formatInteger(row.priority)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div></div>
            ) : (
              <p className="text-sm text-text-secondary">{pick(gapMatrix, 'message', isDe)}</p>
            )}

            <div className="homepage-muted-panel p-4 space-y-2">
              <div className="font-bold text-sm">{d.targetsTitle}</div>
              <p className="text-xs text-text-secondary">{d.targetsSubtitle}</p>
              {targetRows.map((row, index) => (
                <div key={index} className="flex flex-wrap gap-2 items-end text-xs">
                  <select className="form-input" value={row.capability}
                    onChange={(event) => setTargetRows(targetRows.map((r, i) => (i === index ? { ...r, capability: event.target.value } : r)))}>
                    {CAPABILITIES.map((capability) => <option key={capability} value={capability}>{capability}</option>)}
                  </select>
                  <input className="form-input" style={{ width: '6rem' }} value={row.mod_sig} title={d.modSignature}
                    onChange={(event) => setTargetRows(targetRows.map((r, i) => (i === index ? { ...r, mod_sig: event.target.value } : r)))} />
                  <input className="form-input" style={{ width: '7rem' }} value={row.tier_level} title={d.tier}
                    onChange={(event) => setTargetRows(targetRows.map((r, i) => (i === index ? { ...r, tier_level: event.target.value } : r)))} />
                  <input className="form-input" style={{ width: '7rem' }} type="number" min="0" value={row.target_count} title={d.gapTarget}
                    onChange={(event) => setTargetRows(targetRows.map((r, i) => (i === index ? { ...r, target_count: event.target.value } : r)))} />
                  <input className="form-input" style={{ width: '5rem' }} type="number" step="0.1" min="0" value={row.weight} title={d.gapWeight}
                    onChange={(event) => setTargetRows(targetRows.map((r, i) => (i === index ? { ...r, weight: event.target.value } : r)))} />
                  <button type="button" className="homepage-button-secondary"
                    onClick={() => setTargetRows(targetRows.filter((_, i) => i !== index))}>{d.removeRow}</button>
                </div>
              ))}
              <div className="flex gap-2">
                <button type="button" className="homepage-button-secondary"
                  onClick={() => setTargetRows([...targetRows, { capability: 'tool_calling', mod_sig: 't->t', tier_level: 'unmeasured', target_count: 0, weight: 1.0 }])}>
                  {d.addTargetRow}
                </button>
                <button type="button" className="homepage-button-primary" disabled={busy || !targetRows.length}
                  onClick={() => runAction(() => requestJson(`${API}/targets`, {
                    method: 'POST',
                    body: JSON.stringify({
                      set_by: 'admin-dashboard',
                      targets: targetRows.map((row) => ({
                        capability: row.capability, mod_sig: row.mod_sig, tier_level: row.tier_level,
                        target_count: Number(row.target_count) || 0, weight: Number(row.weight) || 1,
                      })),
                    }),
                  }), d.targetsSaved)}>
                  {d.saveTargets}
                </button>
              </div>
            </div>
          </AdminSection>
          )}

          <AdminSection
            id="datalake-tdvs"
            title={d.tdvTitle}
            subtitle={d.tdvSubtitle}
            count={tdvs.length}
            actions={planGoverns ? null : (
              <button type="button" className="homepage-button-primary" disabled={busy}
                onClick={() => runAction(() => requestJson(`${API}/tdv/create`, { method: 'POST', body: '{}' }), d.tdvCreated)}>
                {d.createTdv}
              </button>
            )}
          >
            {tdvs.length ? (() => {
              // Never-sealed older drafts are history, not information: the sealed versions
              // and a draft being built now stay in view, the rest folds away.
              const newestSealed = tdvs.filter((tdv) => tdv.status === 'sealed').map((tdv) => String(tdv.tdv_id)).sort().pop() || '';
              const current = tdvs.filter((tdv) => tdv.status === 'sealed' || String(tdv.tdv_id) > newestSealed);
              const history = tdvs.filter((tdv) => !current.includes(tdv));
              const table = (rows) => (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{d.tdvId}</th><th>{d.tdvName}</th><th>{d.statusCol}</th>
                    <th>{d.tdvMembers}</th><th>{d.tdvCreatedAt}</th><th>{d.tdvSealedAt}</th><th>{d.treeActions}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((tdv) => (
                    <tr key={tdv.tdv_id} className={selectedTdv === tdv.tdv_id ? 'font-bold' : ''}>
                      <td>
                        <button type="button" className="underline" onClick={() => loadTdvDetail(tdv.tdv_id)}>{tdv.tdv_id}</button>
                      </td>
                      <td>{tdv.name}</td>
                      <td><StatusPill ok={tdv.status === 'sealed'}>{tdv.status === 'sealed' ? d.tdvStatusSealed : d.tdvStatusDraft}</StatusPill></td>
                      <td>{formatInteger(tdv.members)}</td>
                      <td className="text-xs">{formatBerlin(tdv.created_at)}</td>
                      <td className="text-xs">{formatBerlin(tdv.sealed_at)}</td>
                      <td>
                        {tdv.status !== 'sealed' && !planGoverns ? (
                          <button type="button" className="homepage-button-secondary" disabled={busy}
                            onClick={() => runAction(() => requestJson(`${API}/tdv/${tdv.tdv_id}/seal`, { method: 'POST', body: '{}' }), d.tdvSealed)}>
                            {d.sealTdv}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              );
              return (
                <>
                  {table(current)}
                  {history.length ? (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-text-secondary">{fill(d.tdvDraftsHistory, { count: history.length })}</summary>
                      {table(history)}
                    </details>
                  ) : null}
                </>
              );
            })() : <p className="text-sm text-text-secondary">{planGoverns ? d.tdvPlanEmpty : d.tdvEmpty}</p>}
          </AdminSection>

          <AdminSection
            id="datalake-compat"
            title={d.compatTitle}
            subtitle={d.compatSubtitle}
            state={compatReport ? (compatReport.all_green ? d.compatAllGreen : d.compatNotGreen) : ''}
            tone={compatReport ? (compatReport.all_green ? 'ok' : 'error') : ''}
            actions={selectedTdv ? (
              <button type="button" className="homepage-button-secondary" disabled={busy}
                onClick={() => runAction(async () => {
                  const report = await requestJson(`${API}/compat/check`, {
                    method: 'POST', body: JSON.stringify({ tdv_id: selectedTdv }),
                  });
                  setTdvDetail((previous) => ({ ...(previous || {}), compat_report: report }));
                  return report;
                }, d.compatChecked, loadCompatReports)}>
                {d.compatRun}
              </button>
            ) : null}
          >
            {!selectedTdv ? <p className="text-sm text-text-secondary">{planGoverns && !tdvs.length ? d.compatPlanNone : d.compatSelectTdv}</p> : null}
            {selectedTdv && tdvDetail?.error ? <p className="text-sm text-error">{tdvDetail.error}</p> : null}
            {selectedTdv && tdvDetail && !tdvDetail.error && !compatReport ? (
              <p className="text-sm text-text-secondary">{d.compatNoReport} {d.compatDraftNote}</p>
            ) : null}
            {compatReport ? (
              <div className="admin-table-shell"><div className="admin-table-scroll">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>{d.compatRule}</th><th>{d.compatTitleCol}</th><th>{d.statusCol}</th>
                      <th>{d.compatKind}</th><th>{d.compatEvidence}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(compatReport.results || []).map((result) => (
                      <tr key={result.rule}>
                        <td className="font-mono text-xs">{result.rule}</td>
                        <td className="text-xs">{result.title}</td>
                        <td>
                          <span className={`admin-status-pill ${result.status === 'GREEN' ? 'is-ok' : result.status === 'WARN' ? 'is-warning' : 'is-missing'}`}>
                            {result.status}
                          </span>
                        </td>
                        <td className="text-xs">{result.kind === 'warn' ? d.kindWarn : d.kindHard}</td>
                        <td className="font-mono text-xs" style={{ maxWidth: '26rem', overflowWrap: 'anywhere' }}>
                          {JSON.stringify(result.evidence)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div></div>
            ) : null}
            {reports.length ? (
              <div className="homepage-muted-panel p-3 space-y-1 text-xs">
                <div className="font-bold">{d.reportsTitle}</div>
                {reports.map((report) => (
                  <div key={report.report_id} className="flex flex-wrap gap-3">
                    <span className="font-mono">{report.tdv_id}</span>
                    <span>{report.arch_version}</span>
                    <span>{formatBerlin(report.created_at)}</span>
                    <StatusPill ok={Boolean(report.all_green)}>{report.all_green ? d.compatAllGreen : d.compatNotGreen}</StatusPill>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-text-secondary">{d.reportsEmpty}</p>}
          </AdminSection>
        </>
      ) : null}

      {/* 05 — training: the strand selector (D-HP-ADMIN-072), the readiness gates in plain
          language, gated start, runs with live metrics, benchmarks, exports, HF releases. */}
      {activeSubPage === 'training' ? (
        <>
          <TrainingRunCard d={d} live={feature('trainingLive')} isDe={isDe} />
          <TrainingStrandSelector d={d} isDe={isDe} />
          <AdminSection
            id="datalake-training-checklist"
            title={d.checklistTitle}
            subtitle={d.checklistSubtitle}
            state={checklist ? (checklist.ready ? d.checklistReady : fill(d.checklistUnmet, { count: unmetItems.length })) : ''}
            tone={checklist?.ready ? 'ok' : 'error'}
            actions={(
              <button
                type="button"
                className="homepage-button-primary"
                disabled={busy || !checklist?.ready}
                title={checklist?.ready ? '' : fill(d.startBlocked, { items: unmetItems.map((item) => (isDe ? item.title : item.title_en)).join('; ') })}
                onClick={() => runAction(() => requestJson(`${API}/train`, {
                  method: 'POST',
                  body: JSON.stringify({ action: 'start', tdv_id: checklist?.selected_tdv || undefined }),
                }), d.trainingStarted)}
              >
                {d.startTraining}
              </button>
            )}
          >
            <FactGrid items={[
              [d.phaseLabel, phase === 'B' ? d.phaseB : d.phaseA],
              [d.selectedTdvLabel, checklist?.selected_tdv],
              [d.computedAt, formatBerlin(checklist?.computed_at)],
            ]} />
            {readinessFeature.status === 'ok' && readinessGateStages.length ? (
              // The training gates in plain language (D-HP-ADMIN-067): the same readiness
              // stages the hero shows, filtered to the ALL-GREEN gates — measured sentence
              // and next step instead of raw ids and detail JSON (ids stay in tooltips).
              <div className="space-y-2">
                {readinessGateStages.map(renderReadinessStage)}
              </div>
            ) : (
              <>
                {!checklist?.ready && unmetItems.length ? (
                  <p className="text-sm text-error">{fill(d.startBlocked, { items: unmetItems.map((item) => (isDe ? item.title : item.title_en)).join('; ') })}</p>
                ) : null}
                <div className="space-y-2">
                  {(checklist?.items || []).map((item) => (
                    <div key={item.id} className="homepage-muted-panel p-3 text-sm space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusPill ok={item.satisfied}>{item.satisfied ? d.itemSatisfied : d.itemUnsatisfied}</StatusPill>
                        <span className="font-bold">{isDe ? item.title : item.title_en}</span>
                      </div>
                      {item.detail ? (
                        <div className="font-mono text-xs text-text-secondary" style={{ overflowWrap: 'anywhere' }}>
                          {JSON.stringify(item.detail)}
                        </div>
                      ) : null}
                    </div>
                  ))}
                  {!checklist ? <p className="text-sm text-text-secondary">{d.checklistUnavailable}</p> : null}
                </div>
              </>
            )}
          </AdminSection>

          <AdminSection id="datalake-runs" title={d.runsTitle} subtitle={d.runsSubtitle} count={runs.filter((run) => !['aborted', 'failed'].includes(String(run.state))).length}>
            {runs.length ? (() => {
              // Aborted and failed runs are history; the active and completed runs stay in view.
              const history = runs.filter((run) => ['aborted', 'failed'].includes(String(run.state)));
              const current = runs.filter((run) => !history.includes(run));
              const renderRow = (run) => {
                      const heartbeat = run.metrics || run.last_metrics || run.heartbeat || {};
                      const step = heartbeat.step ?? run.step;
                      const loss = heartbeat.loss ?? run.loss;
                      return (
                        <tr key={run.run_id}>
                          <td className="font-mono text-xs">{String(run.run_id).slice(0, 13)}</td>
                          <td className="font-mono text-xs">{run.tdv_id}</td>
                          <td className="font-mono text-xs">{run.arch_version}</td>
                          <td className="font-mono text-xs">{run.preset || '-'}</td>
                          <td>{formatInteger(run.seq_len)}</td>
                          <td><StatusPill ok={run.state === 'completed'}>{run.state}</StatusPill></td>
                          <td>{formatInteger(step)}</td>
                          <td className="font-mono text-xs">{formatLoss(loss)}</td>
                          <td className="text-xs">{formatBerlin(run.started_at)}</td>
                          <td className="text-xs">{formatBerlin(run.finished_at)}</td>
                          <td>
                            <div className="flex flex-wrap gap-1">
                              {['running', 'starting', 'resuming'].includes(String(run.state)) ? (
                                <button type="button" className="homepage-button-secondary is-destructive" disabled={busy}
                                  onClick={() => { if (window.confirm(d.confirmAbort)) runAction(() => requestJson(`${API}/train`, { method: 'POST', body: JSON.stringify({ action: 'abort', run_id: run.run_id }) }), d.runAborted); }}>
                                  {d.abortRun}
                                </button>
                              ) : null}
                              {run.bench_report_id ? (
                                <button type="button" className="homepage-button-secondary" onClick={() => openBenchReport(run.run_id)}>
                                  {d.benchLink}
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
              };
              const table = (rows) => (
              <div className="admin-table-shell"><div className="admin-table-scroll">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>{d.runId}</th><th>{d.tdvId}</th><th>{d.archVersion}</th><th>{d.presetCol}</th>
                      <th>{d.seqLenCol}</th><th>{d.statusCol}</th><th>{d.stepCol}</th><th>{d.lossCol}</th>
                      <th>{d.runStarted}</th><th>{d.runFinished}</th><th>{d.treeActions}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(renderRow)}
                  </tbody>
                </table>
              </div></div>
              );
              return (
                <>
                  {current.length ? table(current) : <p className="text-sm text-text-secondary">{d.runsEmpty}</p>}
                  {history.length ? (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-text-secondary">{fill(d.runsHistory, { count: history.length })}</summary>
                      {table(history)}
                    </details>
                  ) : null}
                </>
              );
            })() : <p className="text-sm text-text-secondary">{d.runsEmpty}</p>}
          </AdminSection>

          <AdminSection id="datalake-bench" title={d.benchTitle} subtitle={d.benchSubtitle} count={benchList.length}>
            {feature('bench').status !== 'ok' ? <FeatureNote featureKey="bench" /> : (
              benchList.length ? (
                <table className="admin-table">
                  <thead>
                    <tr><th>{d.runId}</th><th>{d.suiteVersion}</th><th>{d.tdvCreatedAt}</th><th>{d.treeActions}</th></tr>
                  </thead>
                  <tbody>
                    {benchList.map((report) => (
                      <tr key={report.report_id}>
                        <td className="font-mono text-xs">{String(report.run_id || '').slice(0, 13)}</td>
                        <td className="font-mono text-xs">{report.suite_version}</td>
                        <td className="text-xs">{formatBerlin(report.created_at)}</td>
                        <td>
                          <button type="button" className="homepage-button-secondary" onClick={() => openBenchReport(report.run_id)}>
                            {d.benchOpen}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-sm text-text-secondary">{d.benchEmpty}</p>
            )}
          </AdminSection>

          <AdminSection id="datalake-exports" title={d.exportsTitle} subtitle={d.exportsSubtitle} count={exportsList.length}>
            {exportsList.length ? (
              <table className="admin-table">
                <thead>
                  <tr><th>{d.exportId}</th><th>{d.runId}</th><th>{d.exportSize}</th><th>{d.exportSha}</th><th>{d.tdvCreatedAt}</th><th>{d.download}</th></tr>
                </thead>
                <tbody>
                  {exportsList.map((exportRow) => (
                    <tr key={exportRow.export_id}>
                      <td className="font-mono text-xs">{String(exportRow.export_id).slice(0, 13)}</td>
                      <td className="font-mono text-xs">{String(exportRow.run_id || '').slice(0, 13)}</td>
                      <td>{formatBytes(exportRow.size_bytes)}</td>
                      <td className="font-mono text-xs">{String(exportRow.sha256 || '').slice(0, 16)}</td>
                      <td className="text-xs">{formatBerlin(exportRow.created_at)}</td>
                      <td>
                        <a className="homepage-button-secondary" href={`${API}/download/${encodeURIComponent(exportRow.export_id)}`}>
                          {d.download}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="text-sm text-text-secondary">{d.exportsEmpty}</p>}
          </AdminSection>

          <AdminSection id="datalake-hf" title={d.hfTitle} subtitle={d.hfSubtitle} count={hfUploads.length}>
            {feature('hf').status !== 'ok' ? <FeatureNote featureKey="hf" /> : (
              hfUploads.length ? (
                <div className="admin-table-shell"><div className="admin-table-scroll">
                  <table className="admin-table">
                    <thead>
                      <tr><th>{d.hfRepo}</th><th>{d.exportId}</th><th>{d.hfState}</th><th>{d.hfVisibility}</th><th>{d.hfVerifiedAt}</th></tr>
                    </thead>
                    <tbody>
                      {hfUploads.map((upload) => (
                        <tr key={upload.upload_id}>
                          <td className="font-mono text-xs" style={{ overflowWrap: 'anywhere' }}>{upload.repo_id}</td>
                          <td className="font-mono text-xs">{String(upload.export_id || '').slice(0, 13)}</td>
                          <td>
                            <StatusPill ok={!['failed', 'error', 'not_private'].includes(String(upload.state))}>{upload.state}</StatusPill>
                            {upload.error ? <div className="text-xs text-error" style={{ overflowWrap: 'anywhere' }}>{upload.error}</div> : null}
                          </td>
                          <td>
                            {upload.private_verified
                              ? <span className="admin-status-pill is-ok">{d.hfPrivateVerified}</span>
                              : <span className="admin-status-pill is-warning">{d.hfUnverified}</span>}
                          </td>
                          <td className="text-xs">{formatBerlin(upload.verified_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div></div>
              ) : <p className="text-sm text-text-secondary">{d.hfEmpty}</p>
            )}
          </AdminSection>
        </>
      ) : null}

      {/* Benchmark report detail (rendered markdown from the lake). */}
      {benchOpenId ? (
        <AdminDialog
          title={fill(d.benchReportFor, { run: String(benchOpenId).slice(0, 13) })}
          subtitle={benchDetail?.suite_version ? `${d.suiteVersion}: ${benchDetail.suite_version}` : ''}
          onRequestClose={() => { setBenchOpenId(''); setBenchDetail(null); }}
          labels={{ close: d.close }}
        >
          <div className="p-4 space-y-3 text-sm">
            {!benchDetail ? <p className="text-text-secondary">{d.loading}</p> : null}
            {benchDetail?.error ? <p className="text-error">{benchDetail.error}</p> : null}
            {benchDetail && !benchDetail.error ? (
              benchDetail.markdown
                ? renderMarkdown(benchDetail.markdown)
                : (
                  <>
                    <p className="text-text-secondary">{d.benchNoMarkdown}</p>
                    <div className="font-mono text-xs" style={{ overflowWrap: 'anywhere' }}>{JSON.stringify(benchDetail.results)}</div>
                  </>
                )
            ) : null}
            <div className="flex justify-end">
              <button type="button" className="homepage-button-secondary" onClick={() => { setBenchOpenId(''); setBenchDetail(null); }}>{d.close}</button>
            </div>
          </div>
        </AdminDialog>
      ) : null}

      {/* Google Drive connect dialog: sign-in window first, code paste for the public
          identity, device code as the last resort. */}
      {connectOpen ? (
        <AdminDialog title={d.connectTitle} subtitle={d.connectSubtitle} onRequestClose={closeConnect} labels={{ close: d.close }}>
          <div className="p-4 space-y-3 text-sm">
            <FactGrid items={[
              [d.backendLabel, connection.backend ? (connection.backend === 'localfs' ? d.backendLocal : d.backendBlockStorage) : connection.backend],
              [d.connectedLabel, connection.connected ? d.connectedYes : d.connectedNo],
              [d.accountLabel, connection.account],
              [d.oauthClientLabel, connection.own_client ? d.clientConfigured
                : connection.identity === 'public-rclone' ? d.publicIdentity : d.clientNotConfigured],
            ]} />
            {flowStatus === 'idle' ? (
              <div className="space-y-2">
                {connection.own_client ? (
                  <button type="button" className="homepage-button-primary" onClick={startWebFlow}>{d.webConnectStart}</button>
                ) : (
                  <>
                    <button type="button" className="homepage-button-primary" onClick={startManualFlow}>{d.webConnectStart}</button>
                    <div className="text-text-secondary text-xs">{d.publicIdentityNote}</div>
                  </>
                )}
                <div>
                  <button type="button" className="homepage-button-secondary" onClick={startDeviceFlow}>{d.deviceInstead}</button>
                </div>
              </div>
            ) : null}
            {flowStatus === 'webwait' || flowStatus === 'starting' ? (
              <div className="homepage-muted-panel p-4 space-y-2">
                <div>{d.webConnectWaiting}</div>
                <div className="text-text-secondary text-xs">{d.webConnectHint}</div>
              </div>
            ) : null}
            {flowStatus === 'manual' ? (
              <div className="homepage-muted-panel p-4 space-y-2">
                <div className="text-text-secondary text-xs">{d.manualHint}</div>
                <label className="block text-xs font-bold" htmlFor="gdrive-code">{d.manualCodeLabel}</label>
                <input id="gdrive-code" className="form-input w-full font-mono" value={codeValue}
                  onChange={(event) => setCodeValue(event.target.value)} placeholder="4/0A..." />
                <button type="button" className="homepage-button-primary" disabled={!codeValue.trim()}
                  onClick={submitManualCode}>{d.manualSubmit}</button>
              </div>
            ) : null}
            {flow && flowStatus === 'waiting' ? (
              <div className="homepage-muted-panel p-4 space-y-2">
                <div>{d.deviceFlowCode}: <strong className="font-mono text-lg">{flow.user_code}</strong></div>
                {flow.verification_url ? (
                  <a className="homepage-button-secondary" href={flow.verification_url} target="_blank" rel="noreferrer">
                    {d.deviceFlowOpenUrl}
                  </a>
                ) : null}
                <div className="text-text-secondary text-xs">{d.deviceFlowWaiting}</div>
              </div>
            ) : null}
            {flowStatus === 'authorized' ? <div className="text-sm"><StatusPill ok>{d.deviceFlowAuthorized}</StatusPill></div> : null}
            {flowStatus === 'expired' ? <div className="text-sm text-error">{d.deviceFlowExpired}</div> : null}
            {flowStatus === 'failed' ? <div className="text-sm text-error">{flowError}</div> : null}
            <div className="flex justify-end">
              <button type="button" className="homepage-button-secondary" onClick={closeConnect}>{d.close}</button>
            </div>
          </div>
        </AdminDialog>
      ) : null}
    </div>
  );
};

export default TrainingDataFactoryPage;
